import { ref, version, onBeforeMount, defineComponent, h } from 'vue';
import { registerAccordion, registerAccordionItem, registerActionGroup, registerAlert, registerAudioPlayer, registerAvatar, registerBadge, registerBanner, registerBreadcrumb, registerBreadcrumbItem, registerButton, registerCalendar, registerCalendarEvent, registerCard, registerCheckbox, registerCombobox, registerDataGrid, registerDatePicker, registerDateRangePicker, registerDateTimePicker, registerDialPad, registerDialog, registerDivider, registerEmptyState, registerFab, registerFilePicker, registerHeader, registerIcon, registerLayout, registerMenu, registerMenuItem, registerNav, registerNavDisclosure, registerNavItem, registerNote, registerNumberField, registerOption, registerPagination, registerProgress, registerProgressRing, registerRadio, registerRadioGroup, registerRangeSlider, registerRichTextEditor, registerSearchableSelect, registerSelect, registerSelectableBox, registerSideDrawer, registerSlider, registerSplitButton, registerSwitch, registerTab, registerTabPanel, registerTabs, registerTag, registerTagGroup, registerTextArea, registerTextField, registerTimePicker, registerToggletip, registerTooltip, registerTreeItem, registerTreeView, registerVideoPlayer } from '@vonage/vivid';

const CustomComponentPrefix = ref("vvd3");
function setCustomComponentPrefix(prefix) {
  CustomComponentPrefix.value = prefix;
}

const isVue2 = version.startsWith("2");

function handleVue3Props(props) {
  if (
    /* v8 ignore next 8 */
    // @ts-ignore
    import.meta.env?.SSR !== false && // @ts-ignore
    (globalThis.process && process.server) !== false && // @ts-ignore
    (globalThis.process && process.server || // @ts-ignore
    import.meta.env?.SSR)
  ) {
    const newProps = {};
    for (const key in props) {
      if (key[0] === "^") {
        newProps[key.slice(1)] = props[key];
      }
    }
    return newProps;
  } else {
    return props;
  }
}

function registerComponent(type, registerFunction) {
  const componentName = ref(`${CustomComponentPrefix.value}-${type}`);
  onBeforeMount(() => {
    registerFunction(CustomComponentPrefix.value);
  });
  return componentName;
}

const VAccordion = defineComponent({
  name: "VAccordion",
  props: {
    /**
     * Controls the expand mode of the Accordion, either allowing
     * single or multiple item expansion.
     */
    expandMode: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires a custom 'change' event when the active item changes
     * @type {CustomEvent<string | null>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    closeAll() {
      return this.element?.closeAll();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("accordion", registerAccordion);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.expandMode !== void 0 ? { "expand-mode": this.expandMode } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.expandMode !== void 0 ? { "^expand-mode": this.expandMode } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const isTextNode = (node) => !node.tag && node.text;
function handleNamedSlotV2(slot, children) {
  if (!children) {
    return null;
  }
  for (const [index, child] of children.entries()) {
    if (isTextNode(child)) {
      children[index] = h("span", { attrs: { slot } }, child.text);
    } else {
      if (!child.data) child.data = {};
      child.data.attrs = { ...child.data?.attrs, slot };
    }
  }
  return children;
}
const fragmentSymbol = Symbol.for("v-fgt");
const textSymbol = Symbol.for("v-txt");
const isVNodeV3 = (node) => typeof node === "object" && node !== null && !(node instanceof Array);
function handleSlottedChildrenV3(slot, children) {
  if (Array.isArray(children)) {
    for (const [index, child] of children.entries()) {
      if (!isVNodeV3(child)) {
        continue;
      }
      if (child.type === fragmentSymbol) {
        handleSlottedChildrenV3(slot, child.children);
      } else if (child.type === textSymbol) {
        children[index] = h("span", { slot }, child.children);
      } else {
        if (!child.props) child.props = {};
        child.props = { ...child.props, slot };
      }
    }
  }
  return children;
}
function handleNamedSlotV3(slot, children) {
  if (!children) {
    return null;
  }
  return handleSlottedChildrenV3(slot, children());
}

const VAccordionItem = defineComponent({
  name: "VAccordionItem",
  props: {
    /**
     * The appearance the button should have.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * Expands or collapses the item.
     */
    expanded: { type: Boolean, default: void 0 },
    heading: { type: String, default: void 0 },
    /**
     * Configures the https://www.w3.org/TR/wai-aria-1.1/#aria-level | level of the
     * heading element.
     */
    headingLevel: {
      type: Number,
      default: void 0
    },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * Indicates the icon affix alignment.
     */
    iconTrailing: { type: Boolean, default: void 0 },
    /**
     * The item ID
     */
    id: { type: String, default: void 0 },
    meta: { type: String, default: void 0 },
    /**
     * Indicates whether the accordion-item has indicator
     */
    noIndicator: { type: Boolean, default: void 0 },
    /**
     * The size the accordion-item should have.
     */
    size: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires a custom 'change' event when the button is invoked
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent(
      "accordion-item",
      registerAccordionItem
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.expanded !== void 0 ? { expanded: this.expanded } : {},
            ...this.heading !== void 0 ? { heading: this.heading } : {},
            ...this.headingLevel !== void 0 ? { "heading-level": this.headingLevel } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.iconTrailing !== void 0 ? { "icon-trailing": this.iconTrailing } : {},
            ...this.id !== void 0 ? { id: this.id } : {},
            ...this.meta !== void 0 ? { meta: this.meta } : {},
            ...this.noIndicator !== void 0 ? { "no-indicator": this.noIndicator } : {},
            ...this.size !== void 0 ? { size: this.size } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot heading Used to add content to the heading.
          handleNamedSlotV2("heading", this.$slots["heading"]),
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"]),
          // @slot meta Used to add additional content to the heading.
          handleNamedSlotV2("meta", this.$slots["meta"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.expanded !== void 0 && this.expanded !== false ? { "^expanded": this.expanded } : {},
          ...this.heading !== void 0 ? { "^heading": this.heading } : {},
          ...this.headingLevel !== void 0 ? { "^heading-level": this.headingLevel } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.iconTrailing !== void 0 && this.iconTrailing !== false ? { "^icon-trailing": this.iconTrailing } : {},
          ...this.id !== void 0 ? { "^id": this.id } : {},
          ...this.meta !== void 0 ? { "^meta": this.meta } : {},
          ...this.noIndicator !== void 0 && this.noIndicator !== false ? { "^no-indicator": this.noIndicator } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot heading Used to add content to the heading.
        handleNamedSlotV3("heading", this.$slots["heading"]),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"]),
        // @slot meta Used to add additional content to the heading.
        handleNamedSlotV3("meta", this.$slots["meta"])
      ]
    );
  }
});

const VActionGroup = defineComponent({
  name: "VActionGroup",
  props: {
    /**
     * The appearance the ActionGroup should have.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * The shape the ActionGroup should have.
     */
    shape: { type: String, default: void 0 },
    /**
     * Indicates whether action group should have padding.
     */
    tight: { type: Boolean, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent(
      "action-group",
      registerActionGroup
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.tight !== void 0 ? { tight: this.tight } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.tight !== void 0 && this.tight !== false ? { "^tight": this.tight } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VAlert = defineComponent({
  name: "VAlert",
  props: {
    /**
     * Sets an appropriate icon / icon color for the connotation.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Allows setting a custom aria-label for the dismiss button.
     */
    dismissButtonAriaLabel: {
      type: String,
      default: void 0
    },
    /**
     * Adds a headline to the Alert.
     */
    headline: { type: String, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * Open state of the Alert.
     */
    open: { type: Boolean, default: void 0 },
    /**
     * The placement of the Alert on the screen.
     */
    placement: {
      type: String,
      default: void 0
    },
    /**
     * Adds a close button to the Alert.
     */
    removable: { type: Boolean, default: void 0 },
    /**
     * Controls the `position` of the Alert.
     */
    strategy: {
      type: String,
      default: void 0
    },
    /**
     * The main text of the Alert.
     */
    text: { type: String, default: void 0 },
    /**
     * Timeout after which the Alert will close.
     */
    timeoutms: { type: Number, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fired when the Alert is closed
     * @type {CustomEvent<undefined>}
     */
    "close",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fired when the Alert is opened
     * @type {CustomEvent<undefined>}
     */
    "open"
  ],
  methods: {
    openChanged(oldValue, newValue) {
      return this.element?.openChanged(oldValue, newValue);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("alert", registerAlert);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.dismissButtonAriaLabel !== void 0 ? { "dismiss-button-aria-label": this.dismissButtonAriaLabel } : {},
            ...this.headline !== void 0 ? { headline: this.headline } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.open !== void 0 ? { open: this.open } : {},
            ...this.placement !== void 0 ? { placement: this.placement } : {},
            ...this.removable !== void 0 ? { removable: this.removable } : {},
            ...this.strategy !== void 0 ? { strategy: this.strategy } : {},
            ...this.text !== void 0 ? { text: this.text } : {},
            ...this.timeoutms !== void 0 ? { timeoutms: this.timeoutms } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            close: (event) => {
              this.$emit("close", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            open: (event) => {
              this.$emit("open", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot action-items Add action items to the Alert using this slot.
          handleNamedSlotV2("action-items", this.$slots["action-items"]),
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"]),
          // @slot main The main content of the Alert.
          handleNamedSlotV2("main", this.$slots["main"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.dismissButtonAriaLabel !== void 0 ? { "^dismiss-button-aria-label": this.dismissButtonAriaLabel } : {},
          ...this.headline !== void 0 ? { "^headline": this.headline } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.open !== void 0 && this.open !== false ? { "^open": this.open } : {},
          ...this.placement !== void 0 ? { "^placement": this.placement } : {},
          ...this.removable !== void 0 && this.removable !== false ? { "^removable": this.removable } : {},
          ...this.strategy !== void 0 ? { "^strategy": this.strategy } : {},
          ...this.text !== void 0 ? { "^text": this.text } : {},
          ...this.timeoutms !== void 0 ? { "^timeoutms": this.timeoutms } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onClose: (event) => {
          this.$emit("close", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onOpen: (event) => {
          this.$emit("open", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot action-items Add action items to the Alert using this slot.
        handleNamedSlotV3("action-items", this.$slots["action-items"]),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"]),
        // @slot main The main content of the Alert.
        handleNamedSlotV3("main", this.$slots["main"])
      ]
    );
  }
});

const VAudioPlayer = defineComponent({
  name: "VAudioPlayer",
  props: {
    /**
     * The connotation the audio-player should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Indicates whether audio player is disabled.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Enables fallback logic to fetch and decode audio buffer for duration when metadata is missing.
     */
    durationFallback: {
      type: Boolean,
      default: void 0
    },
    notime: { type: Boolean, default: void 0 },
    pauseButtonAriaLabel: {
      type: String,
      default: void 0
    },
    playButtonAriaLabel: {
      type: String,
      default: void 0
    },
    /**
     * Sets the available playback rates. When an empty string, no choices will be available
     */
    playbackRates: { type: String, default: void 0 },
    skipBackwardAriaLabel: {
      type: String,
      default: void 0
    },
    /**
     * Allows the audio to skip back or forward
     */
    skipBy: {
      type: String,
      default: void 0
    },
    skipForwardAriaLabel: {
      type: String,
      default: void 0
    },
    sliderAriaLabel: { type: String, default: void 0 },
    /**
     * Indicates the audio-player's src.
     */
    src: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    pause() {
      return this.element?.pause();
    },
    play() {
      return this.element?.play();
    },
    srcChanged() {
      return this.element?.srcChanged();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent(
      "audio-player",
      registerAudioPlayer
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.durationFallback !== void 0 ? { "duration-fallback": this.durationFallback } : {},
            ...this.notime !== void 0 ? { notime: this.notime } : {},
            ...this.pauseButtonAriaLabel !== void 0 ? { "pause-button-aria-label": this.pauseButtonAriaLabel } : {},
            ...this.playButtonAriaLabel !== void 0 ? { "play-button-aria-label": this.playButtonAriaLabel } : {},
            ...this.playbackRates !== void 0 ? { "playback-rates": this.playbackRates } : {},
            ...this.skipBackwardAriaLabel !== void 0 ? { "skip-backward-aria-label": this.skipBackwardAriaLabel } : {},
            ...this.skipBy !== void 0 ? { "skip-by": this.skipBy } : {},
            ...this.skipForwardAriaLabel !== void 0 ? { "skip-forward-aria-label": this.skipForwardAriaLabel } : {},
            ...this.sliderAriaLabel !== void 0 ? { "slider-aria-label": this.sliderAriaLabel } : {},
            ...this.src !== void 0 ? { src: this.src } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.durationFallback !== void 0 && this.durationFallback !== false ? { "^duration-fallback": this.durationFallback } : {},
          ...this.notime !== void 0 && this.notime !== false ? { "^notime": this.notime } : {},
          ...this.pauseButtonAriaLabel !== void 0 ? { "^pause-button-aria-label": this.pauseButtonAriaLabel } : {},
          ...this.playButtonAriaLabel !== void 0 ? { "^play-button-aria-label": this.playButtonAriaLabel } : {},
          ...this.playbackRates !== void 0 ? { "^playback-rates": this.playbackRates } : {},
          ...this.skipBackwardAriaLabel !== void 0 ? { "^skip-backward-aria-label": this.skipBackwardAriaLabel } : {},
          ...this.skipBy !== void 0 ? { "^skip-by": this.skipBy } : {},
          ...this.skipForwardAriaLabel !== void 0 ? { "^skip-forward-aria-label": this.skipForwardAriaLabel } : {},
          ...this.sliderAriaLabel !== void 0 ? { "^slider-aria-label": this.sliderAriaLabel } : {},
          ...this.src !== void 0 ? { "^src": this.src } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VAvatar = defineComponent({
  name: "VAvatar",
  props: {
    /**
     * The appearance the avatar should have.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * Indicates whether avatar should be a clickable <button> element.
     */
    clickable: { type: Boolean, default: void 0 },
    /**
     * The connotation the avatar should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Prompts the user to save the linked URL. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    download: { type: String, default: void 0 },
    /**
     * The URL the hyperlink references. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    href: { type: String, default: void 0 },
    /**
     * Hints at the language of the referenced resource. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    hreflang: { type: String, default: void 0 },
    /**
     * undefined
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * avatar name
     */
    initials: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    ping: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    referrerpolicy: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    rel: { type: String, default: void 0 },
    /**
     * The shape the avatar should have.
     */
    shape: { type: String, default: void 0 },
    /**
     * The size the avatar should have.
     */
    size: {
      type: String,
      default: void 0
    },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    target: {
      type: String,
      default: void 0
    },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    type: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("avatar", registerAvatar);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.clickable !== void 0 ? { clickable: this.clickable } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.download !== void 0 ? { download: this.download } : {},
            ...this.href !== void 0 ? { href: this.href } : {},
            ...this.hreflang !== void 0 ? { hreflang: this.hreflang } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.initials !== void 0 ? { initials: this.initials } : {},
            ...this.ping !== void 0 ? { ping: this.ping } : {},
            ...this.referrerpolicy !== void 0 ? { referrerpolicy: this.referrerpolicy } : {},
            ...this.rel !== void 0 ? { rel: this.rel } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.size !== void 0 ? { size: this.size } : {},
            ...this.target !== void 0 ? { target: this.target } : {},
            ...this.type !== void 0 ? { type: this.type } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot graphic Assign nodes to the graphic slot to set a graphic media of any kind (e.g. image, illustration etc).
          handleNamedSlotV2("graphic", this.$slots["graphic"]),
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.clickable !== void 0 && this.clickable !== false ? { "^clickable": this.clickable } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.download !== void 0 ? { "^download": this.download } : {},
          ...this.href !== void 0 ? { "^href": this.href } : {},
          ...this.hreflang !== void 0 ? { "^hreflang": this.hreflang } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.initials !== void 0 ? { "^initials": this.initials } : {},
          ...this.ping !== void 0 ? { "^ping": this.ping } : {},
          ...this.referrerpolicy !== void 0 ? { "^referrerpolicy": this.referrerpolicy } : {},
          ...this.rel !== void 0 ? { "^rel": this.rel } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {},
          ...this.target !== void 0 ? { "^target": this.target } : {},
          ...this.type !== void 0 ? { "^type": this.type } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot graphic Assign nodes to the graphic slot to set a graphic media of any kind (e.g. image, illustration etc).
        handleNamedSlotV3("graphic", this.$slots["graphic"]),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"])
      ]
    );
  }
});

const VBadge = defineComponent({
  name: "VBadge",
  props: {
    /**
     * The appearance the badge should have.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * The connotation the badge should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * Indicates the icon affix alignment.
     */
    iconTrailing: { type: Boolean, default: void 0 },
    /**
     * The shape the badge should have.
     */
    shape: { type: String, default: void 0 },
    /**
     * The size the badge should have.
     */
    size: {
      type: String,
      default: void 0
    },
    /**
     * Indicates the badge's text.
     */
    text: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("badge", registerBadge);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.iconTrailing !== void 0 ? { "icon-trailing": this.iconTrailing } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.size !== void 0 ? { size: this.size } : {},
            ...this.text !== void 0 ? { text: this.text } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.iconTrailing !== void 0 && this.iconTrailing !== false ? { "^icon-trailing": this.iconTrailing } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {},
          ...this.text !== void 0 ? { "^text": this.text } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"])
      ]
    );
  }
});

const VBanner = defineComponent({
  name: "VBanner",
  props: {
    actionHref: { type: String, default: void 0 },
    actionText: { type: String, default: void 0 },
    connotation: {
      type: String,
      default: void 0
    },
    dismissAriaLabel: { type: String, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    removable: { type: Boolean, default: void 0 },
    text: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    remove() {
      return this.element?.remove();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("banner", registerBanner);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.actionHref !== void 0 ? { "action-href": this.actionHref } : {},
            ...this.actionText !== void 0 ? { "action-text": this.actionText } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.dismissAriaLabel !== void 0 ? { "dismiss-aria-label": this.dismissAriaLabel } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.removable !== void 0 ? { removable: this.removable } : {},
            ...this.text !== void 0 ? { text: this.text } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot action-items Add action items to banner using this slot.
          handleNamedSlotV2("action-items", this.$slots["action-items"]),
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.actionHref !== void 0 ? { "^action-href": this.actionHref } : {},
          ...this.actionText !== void 0 ? { "^action-text": this.actionText } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.dismissAriaLabel !== void 0 ? { "^dismiss-aria-label": this.dismissAriaLabel } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.removable !== void 0 && this.removable !== false ? { "^removable": this.removable } : {},
          ...this.text !== void 0 ? { "^text": this.text } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot action-items Add action items to banner using this slot.
        handleNamedSlotV3("action-items", this.$slots["action-items"]),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"])
      ]
    );
  }
});

const VBreadcrumb = defineComponent({
  name: "VBreadcrumb",
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("breadcrumb", registerBreadcrumb);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {},
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({}),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VBreadcrumbItem = defineComponent({
  name: "VBreadcrumbItem",
  props: {
    /**
     * Prompts the user to save the linked URL. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    download: { type: String, default: void 0 },
    /**
     * The URL the hyperlink references. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    href: { type: String, default: void 0 },
    /**
     * Hints at the language of the referenced resource. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    hreflang: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    ping: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    referrerpolicy: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    rel: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    target: {
      type: String,
      default: void 0
    },
    text: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    type: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent(
      "breadcrumb-item",
      registerBreadcrumbItem
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.download !== void 0 ? { download: this.download } : {},
            ...this.href !== void 0 ? { href: this.href } : {},
            ...this.hreflang !== void 0 ? { hreflang: this.hreflang } : {},
            ...this.ping !== void 0 ? { ping: this.ping } : {},
            ...this.referrerpolicy !== void 0 ? { referrerpolicy: this.referrerpolicy } : {},
            ...this.rel !== void 0 ? { rel: this.rel } : {},
            ...this.target !== void 0 ? { target: this.target } : {},
            ...this.text !== void 0 ? { text: this.text } : {},
            ...this.type !== void 0 ? { type: this.type } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.download !== void 0 ? { "^download": this.download } : {},
          ...this.href !== void 0 ? { "^href": this.href } : {},
          ...this.hreflang !== void 0 ? { "^hreflang": this.hreflang } : {},
          ...this.ping !== void 0 ? { "^ping": this.ping } : {},
          ...this.referrerpolicy !== void 0 ? { "^referrerpolicy": this.referrerpolicy } : {},
          ...this.rel !== void 0 ? { "^rel": this.rel } : {},
          ...this.target !== void 0 ? { "^target": this.target } : {},
          ...this.text !== void 0 ? { "^text": this.text } : {},
          ...this.type !== void 0 ? { "^type": this.type } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VButton = defineComponent({
  name: "VButton",
  props: {
    /**
     * Displays the button in active state.
     */
    active: { type: Boolean, default: void 0 },
    /**
     * The appearance the button should have.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * Determines if the element should receive document focus on page load.
     */
    autofocus: { type: Boolean, default: void 0 },
    /**
     * The connotation the button should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Prompts the user to save the linked URL. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    download: { type: String, default: void 0 },
    /**
     * Display a chevron to indicate that the button opens a dropdown.
     */
    dropdownIndicator: {
      type: Boolean,
      default: void 0
    },
    /**
     * The id of a form to associate the element to.
     */
    form: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element for more details.
     */
    formaction: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element for more details.
     */
    formenctype: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element for more details.
     */
    formmethod: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element for more details.
     */
    formnovalidate: { type: Boolean, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element for more details.
     */
    formtarget: {
      type: String,
      default: void 0
    },
    /**
     * The URL the hyperlink references. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    href: { type: String, default: void 0 },
    /**
     * Hints at the language of the referenced resource. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    hreflang: { type: String, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * Indicates the icon affix alignment.
     */
    iconTrailing: { type: Boolean, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * Indicates the button's label.
     */
    label: { type: String, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * Displays the button in pending state.
     */
    pending: { type: Boolean, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    ping: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    referrerpolicy: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    rel: { type: String, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The shape the button should have.
     */
    shape: { type: String, default: void 0 },
    /**
     * The size the button should have.
     */
    size: {
      type: String,
      default: void 0
    },
    /**
     * Indicates the icon is stacked.
     */
    stacked: { type: Boolean, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    target: {
      type: String,
      default: void 0
    },
    /**
     * Specifies extra information about the button. This information is typically used by browsers to display a tooltip.
     */
    title: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    type: {
      type: String,
      default: void 0
    },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    },
    /**
     * {@inheritDoc (FormAssociated:interface).validate}
     */
    validate() {
      return this.element?.validate();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("button", registerButton);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.active !== void 0 ? { active: this.active } : {},
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.autofocus !== void 0 ? { autofocus: this.autofocus } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.download !== void 0 ? { download: this.download } : {},
            ...this.dropdownIndicator !== void 0 ? { "dropdown-indicator": this.dropdownIndicator } : {},
            ...this.form !== void 0 ? { form: this.form } : {},
            ...this.formaction !== void 0 ? { formaction: this.formaction } : {},
            ...this.formenctype !== void 0 ? { formenctype: this.formenctype } : {},
            ...this.formmethod !== void 0 ? { formmethod: this.formmethod } : {},
            ...this.formnovalidate !== void 0 ? { formnovalidate: this.formnovalidate } : {},
            ...this.formtarget !== void 0 ? { formtarget: this.formtarget } : {},
            ...this.href !== void 0 ? { href: this.href } : {},
            ...this.hreflang !== void 0 ? { hreflang: this.hreflang } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.iconTrailing !== void 0 ? { "icon-trailing": this.iconTrailing } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.pending !== void 0 ? { pending: this.pending } : {},
            ...this.ping !== void 0 ? { ping: this.ping } : {},
            ...this.referrerpolicy !== void 0 ? { referrerpolicy: this.referrerpolicy } : {},
            ...this.rel !== void 0 ? { rel: this.rel } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.size !== void 0 ? { size: this.size } : {},
            ...this.stacked !== void 0 ? { stacked: this.stacked } : {},
            ...this.target !== void 0 ? { target: this.target } : {},
            ...this.title !== void 0 ? { title: this.title } : {},
            ...this.type !== void 0 ? { type: this.type } : {},
            ...this.value !== void 0 ? { "current-value": this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.active !== void 0 && this.active !== false ? { "^active": this.active } : {},
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.autofocus !== void 0 && this.autofocus !== false ? { "^autofocus": this.autofocus } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.download !== void 0 ? { "^download": this.download } : {},
          ...this.dropdownIndicator !== void 0 && this.dropdownIndicator !== false ? { "^dropdown-indicator": this.dropdownIndicator } : {},
          ...this.form !== void 0 ? { "^form": this.form } : {},
          ...this.formaction !== void 0 ? { "^formaction": this.formaction } : {},
          ...this.formenctype !== void 0 ? { "^formenctype": this.formenctype } : {},
          ...this.formmethod !== void 0 ? { "^formmethod": this.formmethod } : {},
          ...this.formnovalidate !== void 0 && this.formnovalidate !== false ? { "^formnovalidate": this.formnovalidate } : {},
          ...this.formtarget !== void 0 ? { "^formtarget": this.formtarget } : {},
          ...this.href !== void 0 ? { "^href": this.href } : {},
          ...this.hreflang !== void 0 ? { "^hreflang": this.hreflang } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.iconTrailing !== void 0 && this.iconTrailing !== false ? { "^icon-trailing": this.iconTrailing } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.pending !== void 0 && this.pending !== false ? { "^pending": this.pending } : {},
          ...this.ping !== void 0 ? { "^ping": this.ping } : {},
          ...this.referrerpolicy !== void 0 ? { "^referrerpolicy": this.referrerpolicy } : {},
          ...this.rel !== void 0 ? { "^rel": this.rel } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {},
          ...this.stacked !== void 0 && this.stacked !== false ? { "^stacked": this.stacked } : {},
          ...this.target !== void 0 ? { "^target": this.target } : {},
          ...this.title !== void 0 ? { "^title": this.title } : {},
          ...this.type !== void 0 ? { "^type": this.type } : {},
          ...this.value !== void 0 ? { "^current-value": this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"])
      ]
    );
  }
});

const VCalendar = defineComponent({
  name: "VCalendar",
  props: {
    /**
     * The date within a week of choice.
     * Accepts any parameter acceptable by the `Date()` constructor.
     */
    datetime: {
      type: [Date, String],
      default: void 0
    },
    /**
     * The convention of displayed time in which the day runs from midnight to midnight and is divided into 24 or 12 hours.
     * Unless provided, choice will be set according to local time preference (e.g. US = 12, IL = 24)
     */
    hour12: { type: Boolean, default: void 0 },
    /**
     * A locale string or array of locale strings that contain one or more language or locale tags.
     * If you include more than one locale string,
     * list them in descending order of priority so that the first entry is the preferred locale.
     * If you omit this parameter, the default locale of the JavaScript runtime is used.
     * This parameter must conform to BCP 47 standards; see the Intl.Collator object for details.
     * e.g. en-US | en-US, he-IL
     */
    locales: {
      type: [String, Array],
      default: void 0
    },
    /**
     * The day to show as the first within a work week.
     * Accepts either Sunday or Monday
     * Calendar will - programmatically - default to Monday if not specified
     *
     * (only applicable for views of more than a single day)
     */
    startDay: {
      type: String,
      default: void 0
    },
    /**
     * Sticky header and sticky column options
     */
    stickyMode: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    onKeydown(_arg0) {
      return this.element?.onKeydown(_arg0);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("calendar", registerCalendar);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.hour12 !== void 0 ? { hour12: this.hour12 } : {},
            ...this.startDay !== void 0 ? { "start-day": this.startDay } : {},
            ...this.stickyMode !== void 0 ? { "sticky-mode": this.stickyMode } : {}
          },
          class: "vvd-component",
          domProps: {
            ...this.datetime !== void 0 ? { datetime: this.datetime } : {},
            ...this.locales !== void 0 ? { locales: this.locales } : {}
          },
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot day-0 Assign elements to corresponding day column using this slot.
          handleNamedSlotV2("day-0", this.$slots["day-0"]),
          // @slot day-1 Assign elements to corresponding day column using this slot.
          handleNamedSlotV2("day-1", this.$slots["day-1"]),
          // @slot day-2 Assign elements to corresponding day column using this slot.
          handleNamedSlotV2("day-2", this.$slots["day-2"]),
          // @slot day-3 Assign elements to corresponding day column using this slot.
          handleNamedSlotV2("day-3", this.$slots["day-3"]),
          // @slot day-4 Assign elements to corresponding day column using this slot.
          handleNamedSlotV2("day-4", this.$slots["day-4"]),
          // @slot day-5 Assign elements to corresponding day column using this slot.
          handleNamedSlotV2("day-5", this.$slots["day-5"]),
          // @slot day-6 Assign elements to corresponding day column using this slot.
          handleNamedSlotV2("day-6", this.$slots["day-6"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.datetime !== void 0 ? { ".datetime": this.datetime } : {},
          ...this.hour12 !== void 0 && this.hour12 !== false ? { "^hour12": this.hour12 } : {},
          ...this.locales !== void 0 ? { ".locales": this.locales } : {},
          ...this.startDay !== void 0 ? { "^start-day": this.startDay } : {},
          ...this.stickyMode !== void 0 ? { "^sticky-mode": this.stickyMode } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot day-0 Assign elements to corresponding day column using this slot.
        handleNamedSlotV3("day-0", this.$slots["day-0"]),
        // @slot day-1 Assign elements to corresponding day column using this slot.
        handleNamedSlotV3("day-1", this.$slots["day-1"]),
        // @slot day-2 Assign elements to corresponding day column using this slot.
        handleNamedSlotV3("day-2", this.$slots["day-2"]),
        // @slot day-3 Assign elements to corresponding day column using this slot.
        handleNamedSlotV3("day-3", this.$slots["day-3"]),
        // @slot day-4 Assign elements to corresponding day column using this slot.
        handleNamedSlotV3("day-4", this.$slots["day-4"]),
        // @slot day-5 Assign elements to corresponding day column using this slot.
        handleNamedSlotV3("day-5", this.$slots["day-5"]),
        // @slot day-6 Assign elements to corresponding day column using this slot.
        handleNamedSlotV3("day-6", this.$slots["day-6"])
      ]
    );
  }
});

const VCalendarEvent = defineComponent({
  name: "VCalendarEvent",
  props: {
    /**
     * The appearance the calendar event should have.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * The connotation the calendar event should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * the description of the event
     */
    description: { type: String, default: void 0 },
    /**
     * event's time duration in hours
     */
    duration: { type: Number, default: void 0 },
    /**
     * the heading of the event
     */
    heading: { type: String, default: void 0 },
    /**
     * sets card display precendence and indentation
     */
    overlapCount: { type: Number, default: void 0 },
    /**
     * time of day event starts
     */
    start: { type: Number, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent(
      "calendar-event",
      registerCalendarEvent
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.description !== void 0 ? { description: this.description } : {},
            ...this.duration !== void 0 ? { duration: this.duration } : {},
            ...this.heading !== void 0 ? { heading: this.heading } : {},
            ...this.overlapCount !== void 0 ? { "overlap-count": this.overlapCount } : {},
            ...this.start !== void 0 ? { start: this.start } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.description !== void 0 ? { "^description": this.description } : {},
          ...this.duration !== void 0 ? { "^duration": this.duration } : {},
          ...this.heading !== void 0 ? { "^heading": this.heading } : {},
          ...this.overlapCount !== void 0 ? { "^overlap-count": this.overlapCount } : {},
          ...this.start !== void 0 ? { "^start": this.start } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VCard = defineComponent({
  name: "VCard",
  props: {
    /**
     * The appearance the card should have.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * Indicates whether card should be a clickable <button> element.
     */
    clickableCard: { type: Boolean, default: void 0 },
    /**
     * Prompts the user to save the linked URL. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    download: { type: String, default: void 0 },
    /**
     * card elevation dp
     */
    elevation: {
      type: Number,
      default: void 0
    },
    /**
     * the text of the card heading
     * accepts string
     */
    headline: { type: String, default: void 0 },
    /**
     * The URL the hyperlink references. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    href: { type: String, default: void 0 },
    /**
     * Hints at the language of the referenced resource. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    hreflang: { type: String, default: void 0 },
    /**
     * card header icon
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    ping: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    referrerpolicy: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    rel: { type: String, default: void 0 },
    /**
     * the text of the card sub-heading
     * accepts string
     */
    subtitle: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    target: {
      type: String,
      default: void 0
    },
    /**
     * the text of the card sub-heading
     * accepts string
     */
    text: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    type: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("card", registerCard);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.clickableCard !== void 0 ? { "clickable-card": this.clickableCard } : {},
            ...this.download !== void 0 ? { download: this.download } : {},
            ...this.elevation !== void 0 ? { elevation: this.elevation } : {},
            ...this.headline !== void 0 ? { headline: this.headline } : {},
            ...this.href !== void 0 ? { href: this.href } : {},
            ...this.hreflang !== void 0 ? { hreflang: this.hreflang } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.ping !== void 0 ? { ping: this.ping } : {},
            ...this.referrerpolicy !== void 0 ? { referrerpolicy: this.referrerpolicy } : {},
            ...this.rel !== void 0 ? { rel: this.rel } : {},
            ...this.subtitle !== void 0 ? { subtitle: this.subtitle } : {},
            ...this.target !== void 0 ? { target: this.target } : {},
            ...this.text !== void 0 ? { text: this.text } : {},
            ...this.type !== void 0 ? { type: this.type } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot footer The footer slot is for content in the card footer.
          handleNamedSlotV2("footer", this.$slots["footer"]),
          // @slot graphic The graphic slot overrides the icon property.
          handleNamedSlotV2("graphic", this.$slots["graphic"]),
          // @slot main Assign nodes to main slot to fully override a card's predefined flow and style with your own.
          handleNamedSlotV2("main", this.$slots["main"]),
          // @slot media The media slot is mainly for images or video content above the card header.
          handleNamedSlotV2("media", this.$slots["media"]),
          // @slot meta The meta slot is for action content in the card header.
          handleNamedSlotV2("meta", this.$slots["meta"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.clickableCard !== void 0 && this.clickableCard !== false ? { "^clickable-card": this.clickableCard } : {},
          ...this.download !== void 0 ? { "^download": this.download } : {},
          ...this.elevation !== void 0 ? { "^elevation": this.elevation } : {},
          ...this.headline !== void 0 ? { "^headline": this.headline } : {},
          ...this.href !== void 0 ? { "^href": this.href } : {},
          ...this.hreflang !== void 0 ? { "^hreflang": this.hreflang } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.ping !== void 0 ? { "^ping": this.ping } : {},
          ...this.referrerpolicy !== void 0 ? { "^referrerpolicy": this.referrerpolicy } : {},
          ...this.rel !== void 0 ? { "^rel": this.rel } : {},
          ...this.subtitle !== void 0 ? { "^subtitle": this.subtitle } : {},
          ...this.target !== void 0 ? { "^target": this.target } : {},
          ...this.text !== void 0 ? { "^text": this.text } : {},
          ...this.type !== void 0 ? { "^type": this.type } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot footer The footer slot is for content in the card footer.
        handleNamedSlotV3("footer", this.$slots["footer"]),
        // @slot graphic The graphic slot overrides the icon property.
        handleNamedSlotV3("graphic", this.$slots["graphic"]),
        // @slot main Assign nodes to main slot to fully override a card's predefined flow and style with your own.
        handleNamedSlotV3("main", this.$slots["main"]),
        // @slot media The media slot is mainly for images or video content above the card header.
        handleNamedSlotV3("media", this.$slots["media"]),
        // @slot meta The meta slot is for action content in the card header.
        handleNamedSlotV3("meta", this.$slots["meta"])
      ]
    );
  }
});

const VCheckbox = defineComponent({
  name: "VCheckbox",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * The current checkedness of the element.
     */
    checked: { type: Boolean, default: void 0 },
    /**
     * The current checkedness of the element.
     */
    modelValue: { type: Boolean, default: void 0 },
    /**
     * The connotation the checklist should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * The default checkedness of the element. This value sets the `checked` property
     * only when the `checked` property has not been explicitly set.
     */
    defaultChecked: { type: Boolean, default: void 0 },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * The indeterminate state of the control.
     */
    indeterminate: { type: Boolean, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * When true, the control will be immutable by user interaction. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly | readonly HTML attribute for more information.
     */
    readonly: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * Provides a custom success message. Any current error state will be overridden.
     */
    successText: { type: String, default: void 0 },
    tabindex: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Emitted when the checked state changes.
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Emitted when the checked state changes.
     * @type {CustomEvent<undefined>}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {boolean}
     */
    "update:modelValue"
  ],
  methods: {
    ariaCheckedChanged() {
      return this.element?.ariaCheckedChanged();
    },
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("checkbox", registerCheckbox);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...(this.modelValue ?? this.checked) !== void 0 ? { "current-checked": this.modelValue ?? this.checked } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.defaultChecked !== void 0 ? { checked: this.defaultChecked } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.indeterminate !== void 0 ? { indeterminate: this.indeterminate } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.readonly !== void 0 ? { readonly: this.readonly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.successText !== void 0 ? { "success-text": this.successText } : {},
            ...this.tabindex !== void 0 ? { tabindex: this.tabindex } : {},
            ...this.value !== void 0 ? { "current-value": this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("update:modelValue", event.currentTarget.checked);
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the checkbox. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...(this.modelValue ?? this.checked) !== void 0 && (this.modelValue ?? this.checked) !== false ? { "^current-checked": this.modelValue ?? this.checked } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.defaultChecked !== void 0 && this.defaultChecked !== false ? { "^checked": this.defaultChecked } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.indeterminate !== void 0 && this.indeterminate !== false ? { "^indeterminate": this.indeterminate } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.readonly !== void 0 && this.readonly !== false ? { "^readonly": this.readonly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.successText !== void 0 ? { "^success-text": this.successText } : {},
          ...this.tabindex !== void 0 ? { "^tabindex": this.tabindex } : {},
          ...this.value !== void 0 ? { "^current-value": this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("update:modelValue", event.currentTarget.checked);
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the checkbox. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"])
      ]
    );
  }
});

const VCombobox = defineComponent({
  name: "VCombobox",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * The appearance attribute.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * The autocomplete attribute.
     */
    autocomplete: {
      type: String,
      default: void 0
    },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * Whether the dropdown is using a fixed positioning strategy.
     */
    fixedDropdown: { type: Boolean, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * The open attribute.
     */
    open: { type: Boolean, default: void 0 },
    /**
     * Sets the placeholder value of the element, generally used to provide a hint to the user.
     */
    placeholder: { type: String, default: void 0 },
    /**
     * the placement of the combobox
     *
     * HTML Attribute: string
     */
    placement: {
      type: String,
      default: void 0
    },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The size the combobox should have.
     */
    scale: {
      type: String,
      default: void 0
    },
    /**
     * The shape attribute.
     */
    shape: { type: String, default: void 0 },
    /**
     * Provides a custom success message. Any current error state will be overridden.
     */
    successText: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    modelValue: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires a custom 'change' event when the value updates
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Filter available options by text value.
     */
    filterOptions() {
      return this.element?.filterOptions();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    },
    /**
     * Moves focus to the first selectable option.
     */
    selectFirstOption() {
      return this.element?.selectFirstOption();
    },
    /**
     * Sets an option as selected and gives it focus.
     */
    setSelectedOptions() {
      return this.element?.setSelectedOptions();
    },
    /**
     * {@inheritDoc (FormAssociated:interface).validate}
     */
    validate() {
      return this.element?.validate();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("combobox", registerCombobox);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.autocomplete !== void 0 ? { autocomplete: this.autocomplete } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.fixedDropdown !== void 0 ? { "fixed-dropdown": this.fixedDropdown } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.open !== void 0 ? { open: this.open } : {},
            ...this.placeholder !== void 0 ? { placeholder: this.placeholder } : {},
            ...this.placement !== void 0 ? { placement: this.placement } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.scale !== void 0 ? { scale: this.scale } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.successText !== void 0 ? { "success-text": this.successText } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { "current-value": this.modelValue ?? this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the combobox. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"]),
          // @slot icon The preferred way to add an icon to the combobox control.
          handleNamedSlotV2("icon", this.$slots["icon"]),
          // @slot meta Slot to add meta content to the combobox control.
          handleNamedSlotV2("meta", this.$slots["meta"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.autocomplete !== void 0 ? { "^autocomplete": this.autocomplete } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.fixedDropdown !== void 0 && this.fixedDropdown !== false ? { "^fixed-dropdown": this.fixedDropdown } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.open !== void 0 && this.open !== false ? { "^open": this.open } : {},
          ...this.placeholder !== void 0 ? { "^placeholder": this.placeholder } : {},
          ...this.placement !== void 0 ? { "^placement": this.placement } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.scale !== void 0 ? { "^scale": this.scale } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.successText !== void 0 ? { "^success-text": this.successText } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^current-value": this.modelValue ?? this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the combobox. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"]),
        // @slot icon The preferred way to add an icon to the combobox control.
        handleNamedSlotV3("icon", this.$slots["icon"]),
        // @slot meta Slot to add meta content to the combobox control.
        handleNamedSlotV3("meta", this.$slots["meta"])
      ]
    );
  }
});

const VDataGrid = defineComponent({
  name: "VDataGrid",
  props: {
    /**
     * Number of columns to be fixed when scrolling horizontally
     */
    fixedColumns: { type: Number, default: void 0 },
    /**
     * Whether the grid should automatically generate a header row and its type
     */
    generateHeader: {
      type: String,
      default: void 0
    },
    /**
     * String that gets applied to the css gridTemplateColumns attribute of child rows
     */
    gridTemplateColumns: {
      type: String,
      default: void 0
    },
    /**
     * When true the component will not add itself to the tab queue.
     * Default is false.
     */
    noTabbing: { type: Boolean, default: void 0 },
    /**
     * Array of objects representing the rows of the grid.
     */
    rowsData: { type: Array, default: void 0 },
    /**
     * Indicates the selection mode.
     */
    selectionMode: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Event that fires when a cell is clicked
     * @type {CustomEvent<{cell: HTMLElement, row: HTMLElement, isHeaderCell: boolean, columnDataKey: string}>}
     */
    "cell-click",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    selectionModeChanged(oldValue) {
      return this.element?.selectionModeChanged(oldValue);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("data-grid", registerDataGrid);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.fixedColumns !== void 0 ? { "fixed-columns": this.fixedColumns } : {},
            ...this.generateHeader !== void 0 ? { "generate-header": this.generateHeader } : {},
            ...this.gridTemplateColumns !== void 0 ? { "grid-template-columns": this.gridTemplateColumns } : {},
            ...this.noTabbing !== void 0 ? { "no-tabbing": this.noTabbing } : {},
            ...this.selectionMode !== void 0 ? { "selection-mode": this.selectionMode } : {}
          },
          class: "vvd-component",
          domProps: {
            ...this.rowsData !== void 0 ? { rowsData: this.rowsData } : {}
          },
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            "cell-click": (event) => {
              this.$emit("cell-click", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.fixedColumns !== void 0 ? { "^fixed-columns": this.fixedColumns } : {},
          ...this.generateHeader !== void 0 ? { "^generate-header": this.generateHeader } : {},
          ...this.gridTemplateColumns !== void 0 ? { "^grid-template-columns": this.gridTemplateColumns } : {},
          ...this.noTabbing !== void 0 && this.noTabbing !== false ? { "^no-tabbing": this.noTabbing } : {},
          ...this.rowsData !== void 0 ? { ".rowsData": this.rowsData } : {},
          ...this.selectionMode !== void 0 ? { "^selection-mode": this.selectionMode } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onCellClick: (event) => {
          this.$emit("cell-click", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VDataGridCell = defineComponent({
  name: "VDataGridCell",
  props: {
    /**
     * Indicates the selected status.
     */
    ariaSelected: { type: String, default: void 0 },
    /**
     * Indicates the sort status.
     */
    ariaSort: { type: String, default: void 0 },
    /**
     * The type of cell
     */
    cellType: {
      type: String,
      default: void 0
    },
    /**
     * Object representing the column definition.
     */
    columnDefinition: { type: Object, default: void 0 },
    /**
     * The column index of the cell.
     * This will be applied to the css grid-column-index value
     * applied to the cell
     */
    gridColumn: { type: String, default: void 0 },
    /**
     * Reflects selected state of the row
     */
    selected: { type: Boolean, default: void 0 },
    /**
     * Sets the sorting direction.
     */
    sortDirection: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Event that fires when a cell is clicked
     * @type {CustomEvent<{cell: HTMLElement, row: HTMLElement, isHeaderCell: boolean, columnDataKey: string}>}
     */
    "cell-click",
    /**
     * Fires a custom 'cell-focused' event when focus is on the cell or its contents
     * @type {CustomEvent<HTMLElement>}
     */
    "cell-focused",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Event that fires when a sortable column header is clicked
     * @type {CustomEvent<{columnDataKey: string, ariaSort: string | null, sortDirection: string | null}>}
     */
    "sort"
  ],
  methods: {
    handleFocusin(_) {
      return this.element?.handleFocusin(_);
    },
    handleFocusout(_) {
      return this.element?.handleFocusout(_);
    },
    handleKeydown(e) {
      return this.element?.handleKeydown(e);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("data-grid-cell", registerDataGrid);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.ariaSelected !== void 0 ? { "aria-selected": this.ariaSelected } : {},
            ...this.ariaSort !== void 0 ? { "aria-sort": this.ariaSort } : {},
            ...this.cellType !== void 0 ? { "cell-type": this.cellType } : {},
            ...this.gridColumn !== void 0 ? { "grid-column": this.gridColumn } : {},
            ...this.selected !== void 0 ? { selected: this.selected } : {},
            ...this.sortDirection !== void 0 ? { "sort-direction": this.sortDirection } : {}
          },
          class: "vvd-component",
          domProps: {
            ...this.columnDefinition !== void 0 ? { columnDefinition: this.columnDefinition } : {}
          },
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            "cell-click": (event) => {
              this.$emit("cell-click", event);
            },
            "cell-focused": (event) => {
              this.$emit("cell-focused", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            sort: (event) => {
              this.$emit("sort", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.ariaSelected !== void 0 ? { "^aria-selected": this.ariaSelected } : {},
          ...this.ariaSort !== void 0 ? { "^aria-sort": this.ariaSort } : {},
          ...this.cellType !== void 0 ? { "^cell-type": this.cellType } : {},
          ...this.columnDefinition !== void 0 ? { ".columnDefinition": this.columnDefinition } : {},
          ...this.gridColumn !== void 0 ? { "^grid-column": this.gridColumn } : {},
          ...this.selected !== void 0 && this.selected !== false ? { "^selected": this.selected } : {},
          ...this.sortDirection !== void 0 ? { "^sort-direction": this.sortDirection } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onCellClick: (event) => {
          this.$emit("cell-click", event);
        },
        onCellFocused: (event) => {
          this.$emit("cell-focused", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onSort: (event) => {
          this.$emit("sort", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VDataGridRow = defineComponent({
  name: "VDataGridRow",
  props: {
    /**
     * Indicates the selected status.
     */
    ariaSelected: { type: String, default: void 0 },
    /**
     * String that gets applied to the the css gridTemplateColumns attribute for the row
     * x
     */
    gridTemplateColumns: {
      type: String,
      default: void 0
    },
    /**
     * The type of row
     */
    rowType: {
      type: String,
      default: void 0
    },
    /**
     * Reflects selected state of the row
     */
    selected: { type: Boolean, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Event that fires when a cell is clicked
     * @type {CustomEvent<{cell: HTMLElement, row: HTMLElement, isHeaderCell: boolean, columnDataKey: string}>}
     */
    "cell-click",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires a custom 'row-focused' event when focus is on an element (usually a cell or its contents) in the row
     * @type {CustomEvent<HTMLElement>}
     */
    "row-focused"
  ],
  methods: {
    handleCellFocus(e) {
      return this.element?.handleCellFocus(e);
    },
    handleFocusout(_) {
      return this.element?.handleFocusout(_);
    },
    handleKeydown(e) {
      return this.element?.handleKeydown(e);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("data-grid-row", registerDataGrid);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.ariaSelected !== void 0 ? { "aria-selected": this.ariaSelected } : {},
            ...this.gridTemplateColumns !== void 0 ? { "grid-template-columns": this.gridTemplateColumns } : {},
            ...this.rowType !== void 0 ? { "row-type": this.rowType } : {},
            ...this.selected !== void 0 ? { selected: this.selected } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            "cell-click": (event) => {
              this.$emit("cell-click", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            "row-focused": (event) => {
              this.$emit("row-focused", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.ariaSelected !== void 0 ? { "^aria-selected": this.ariaSelected } : {},
          ...this.gridTemplateColumns !== void 0 ? { "^grid-template-columns": this.gridTemplateColumns } : {},
          ...this.rowType !== void 0 ? { "^row-type": this.rowType } : {},
          ...this.selected !== void 0 && this.selected !== false ? { "^selected": this.selected } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onCellClick: (event) => {
          this.$emit("cell-click", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onRowFocused: (event) => {
          this.$emit("row-focused", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VDatePicker = defineComponent({
  name: "VDatePicker",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * The latest accepted date.
     */
    max: { type: String, default: void 0 },
    /**
     * The earliest accepted date.
     */
    min: { type: String, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * Whether the date-picker is readonly.
     */
    readonly: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    modelValue: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Emitted when the date is changed by the user.
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Emitted when the date is changed by the user.
     * @type {CustomEvent<undefined>}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("date-picker", registerDatePicker);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.max !== void 0 ? { max: this.max } : {},
            ...this.min !== void 0 ? { min: this.min } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.readonly !== void 0 ? { readonly: this.readonly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { "current-value": this.modelValue ?? this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the date-picker. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.max !== void 0 ? { "^max": this.max } : {},
          ...this.min !== void 0 ? { "^min": this.min } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.readonly !== void 0 && this.readonly !== false ? { "^readonly": this.readonly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^current-value": this.modelValue ?? this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the date-picker. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"])
      ]
    );
  }
});

const VDateRangePicker = defineComponent({
  name: "VDateRangePicker",
  props: {
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * The current end value of the element. This property serves as a mechanism
     * to set the `end` property through both property assignment and the
     * .setAttribute() method. This is useful for setting the field's value
     * in UI libraries that bind data through the .setAttribute() API
     * and don't support IDL attribute binding.
     */
    end: { type: String, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * The initial end value. This value sets the `end` property
     * only when the `end` property has not been explicitly set.
     */
    initialEnd: { type: String, default: void 0 },
    /**
     * The initial start value. This value sets the `start` property
     * only when the `start` property has not been explicitly set.
     */
    initialStart: { type: String, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * The latest accepted date.
     */
    max: { type: String, default: void 0 },
    /**
     * The earliest accepted date.
     */
    min: { type: String, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * Whether the date-picker is readonly.
     */
    readonly: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The current start value of the element. This property serves as a mechanism
     * to set the `start` property through both property assignment and the
     * .setAttribute() method. This is useful for setting the field's value
     * in UI libraries that bind data through the .setAttribute() API
     * and don't support IDL attribute binding.
     */
    start: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Emitted when either the start or end value changes
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Emitted when either the start or end value changes
     * @type {CustomEvent<undefined>}
     */
    "input",
    /**
     * Event emitted when the end value changes
     * @type {CustomEvent<undefined>}
     */
    "input:end",
    /**
     * Event emitted when the start value changes
     * @type {CustomEvent<undefined>}
     */
    "input:start",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the end value changes
     * @type {string}
     */
    "update:end",
    /**
     * Fires when the start value changes
     * @type {string}
     */
    "update:start"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent(
      "date-range-picker",
      registerDateRangePicker
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.end !== void 0 ? { "current-end": this.end } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.initialEnd !== void 0 ? { end: this.initialEnd } : {},
            ...this.initialStart !== void 0 ? { start: this.initialStart } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.max !== void 0 ? { max: this.max } : {},
            ...this.min !== void 0 ? { min: this.min } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.readonly !== void 0 ? { readonly: this.readonly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.start !== void 0 ? { "current-start": this.start } : {},
            ...this.value !== void 0 ? { "current-value": this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            "input:end": (event) => {
              this.$emit("update:end", event.currentTarget.end);
              this.$emit("input:end", event);
            },
            "input:start": (event) => {
              this.$emit("update:start", event.currentTarget.start);
              this.$emit("input:start", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the date-range-picker. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.end !== void 0 ? { "^current-end": this.end } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.initialEnd !== void 0 ? { "^end": this.initialEnd } : {},
          ...this.initialStart !== void 0 ? { "^start": this.initialStart } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.max !== void 0 ? { "^max": this.max } : {},
          ...this.min !== void 0 ? { "^min": this.min } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.readonly !== void 0 && this.readonly !== false ? { "^readonly": this.readonly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.start !== void 0 ? { "^current-start": this.start } : {},
          ...this.value !== void 0 ? { "^current-value": this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        "onInput:end": (event) => {
          this.$emit("update:end", event.currentTarget.end);
          this.$emit("input:end", event);
        },
        "onInput:start": (event) => {
          this.$emit("update:start", event.currentTarget.start);
          this.$emit("input:start", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the date-range-picker. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"])
      ]
    );
  }
});

const VDateTimePicker = defineComponent({
  name: "VDateTimePicker",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * Forces a 12h or 24h clock to be used.
     */
    clock: { type: String, default: void 0 },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * The latest accepted date-time.
     */
    max: { type: String, default: void 0 },
    /**
     * The latest accepted time date of the date-time.
     */
    maxDate: { type: String, default: void 0 },
    /**
     * The latest accepted time part of the date-time.
     */
    maxTime: { type: String, default: void 0 },
    /**
     * The earliest accepted date-time.
     */
    min: { type: String, default: void 0 },
    /**
     * The earliest accepted date part of the date-time.
     */
    minDate: { type: String, default: void 0 },
    /**
     * The earliest accepted time part of the date-time.
     */
    minTime: { type: String, default: void 0 },
    /**
     * Distance between presented minute options.
     */
    minutesStep: { type: Number, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * Whether the date-picker is readonly.
     */
    readonly: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * Distance between presented seconds options. If null, seconds are not presented.
     */
    secondsStep: { type: Number, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    modelValue: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Emitted when the date is changed by the user.
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Emitted when the date is changed by the user.
     * @type {CustomEvent<undefined>}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent(
      "date-time-picker",
      registerDateTimePicker
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.clock !== void 0 ? { clock: this.clock } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.max !== void 0 ? { max: this.max } : {},
            ...this.maxDate !== void 0 ? { "max-date": this.maxDate } : {},
            ...this.maxTime !== void 0 ? { "max-time": this.maxTime } : {},
            ...this.min !== void 0 ? { min: this.min } : {},
            ...this.minDate !== void 0 ? { "min-date": this.minDate } : {},
            ...this.minTime !== void 0 ? { "min-time": this.minTime } : {},
            ...this.minutesStep !== void 0 ? { "minutes-step": this.minutesStep } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.readonly !== void 0 ? { readonly: this.readonly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.secondsStep !== void 0 ? { "seconds-step": this.secondsStep } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { "current-value": this.modelValue ?? this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the date-picker. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.clock !== void 0 ? { "^clock": this.clock } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.max !== void 0 ? { "^max": this.max } : {},
          ...this.maxDate !== void 0 ? { "^max-date": this.maxDate } : {},
          ...this.maxTime !== void 0 ? { "^max-time": this.maxTime } : {},
          ...this.min !== void 0 ? { "^min": this.min } : {},
          ...this.minDate !== void 0 ? { "^min-date": this.minDate } : {},
          ...this.minTime !== void 0 ? { "^min-time": this.minTime } : {},
          ...this.minutesStep !== void 0 ? { "^minutes-step": this.minutesStep } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.readonly !== void 0 && this.readonly !== false ? { "^readonly": this.readonly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.secondsStep !== void 0 ? { "^seconds-step": this.secondsStep } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^current-value": this.modelValue ?? this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the date-picker. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"])
      ]
    );
  }
});

const VDialPad = defineComponent({
  name: "VDialPad",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * Indicates that this element should get focus after the page finishes loading.
     */
    autofocus: { type: Boolean, default: void 0 },
    /**
     * Indicates the active state of the dial-pad.
     */
    callActive: { type: Boolean, default: void 0 },
    callButtonLabel: { type: String, default: void 0 },
    /**
     * Indicates the disabled state of the dial-pad.
     */
    disabled: { type: Boolean, default: void 0 },
    endCallButtonLabel: {
      type: String,
      default: void 0
    },
    /**
     * Indicates the helper-text's text.
     */
    helperText: { type: String, default: void 0 },
    /**
     * Indicates the no-call state of the dial-pad.
     */
    noCall: { type: Boolean, default: void 0 },
    /**
     * Indicates the no-input state of the dial-pad.
     */
    noInput: { type: Boolean, default: void 0 },
    /**
     * Indicates the dial pad's pattern.
     */
    pattern: { type: String, default: void 0 },
    /**
     * Indicates the pending state of the dial-pad.
     */
    pending: { type: Boolean, default: void 0 },
    /**
     * Indicates the placeholder's text.
     */
    placeholder: { type: String, default: void 0 },
    /**
     * Indicates the value's text.
     */
    value: { type: String, default: void 0 },
    /**
     * Indicates the value's text.
     */
    modelValue: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Emitted when the text field loses focus
     * @type {CustomEvent<undefined>}
     */
    "blur",
    /**
     * Emitted when the text field value changes
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Emitted when the call button is clicked
     * @type {CustomEvent<undefined>}
     */
    "dial",
    /**
     * Emitted when the end call button is clicked
     * @type {CustomEvent<undefined>}
     */
    "end-call",
    /**
     * Emitted when the text field receives focus
     * @type {CustomEvent<undefined>}
     */
    "focus",
    /**
     * Emitted when the text field value changes
     * @type {CustomEvent<undefined>}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Emitted when a digit button is clicked
     * @type {CustomEvent<HTMLElement>}
     */
    "keypad-click",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue"
  ],
  methods: {
    /**
     * Moves focus into the diapl-pad.
     */
    focus() {
      return this.element?.focus();
    },
    valueChanged(_oldValue, newValue) {
      return this.element?.valueChanged(_oldValue, newValue);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("dial-pad", registerDialPad);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.autofocus !== void 0 ? { autofocus: this.autofocus } : {},
            ...this.callActive !== void 0 ? { "call-active": this.callActive } : {},
            ...this.callButtonLabel !== void 0 ? { "call-button-label": this.callButtonLabel } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.endCallButtonLabel !== void 0 ? { "end-call-button-label": this.endCallButtonLabel } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.noCall !== void 0 ? { "no-call": this.noCall } : {},
            ...this.noInput !== void 0 ? { "no-input": this.noInput } : {},
            ...this.pattern !== void 0 ? { pattern: this.pattern } : {},
            ...this.pending !== void 0 ? { pending: this.pending } : {},
            ...this.placeholder !== void 0 ? { placeholder: this.placeholder } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { value: this.modelValue ?? this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            dial: (event) => {
              this.$emit("dial", event);
            },
            "end-call": (event) => {
              this.$emit("end-call", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            "keypad-click": (event) => {
              this.$emit("keypad-click", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.autofocus !== void 0 && this.autofocus !== false ? { "^autofocus": this.autofocus } : {},
          ...this.callActive !== void 0 && this.callActive !== false ? { "^call-active": this.callActive } : {},
          ...this.callButtonLabel !== void 0 ? { "^call-button-label": this.callButtonLabel } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.endCallButtonLabel !== void 0 ? { "^end-call-button-label": this.endCallButtonLabel } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.noCall !== void 0 && this.noCall !== false ? { "^no-call": this.noCall } : {},
          ...this.noInput !== void 0 && this.noInput !== false ? { "^no-input": this.noInput } : {},
          ...this.pattern !== void 0 ? { "^pattern": this.pattern } : {},
          ...this.pending !== void 0 && this.pending !== false ? { "^pending": this.pending } : {},
          ...this.placeholder !== void 0 ? { "^placeholder": this.placeholder } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^value": this.modelValue ?? this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onDial: (event) => {
          this.$emit("dial", event);
        },
        onEndCall: (event) => {
          this.$emit("end-call", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeypadClick: (event) => {
          this.$emit("keypad-click", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VDialog = defineComponent({
  name: "VDialog",
  props: {
    dismissButtonAriaLabel: {
      type: String,
      default: void 0
    },
    fullWidthBody: { type: Boolean, default: void 0 },
    headline: { type: String, default: void 0 },
    /**
     * undefined
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    iconPlacement: {
      type: String,
      default: void 0
    },
    /**
     * Controls whether the dialog is modal.
     */
    modal: { type: Boolean, default: void 0 },
    /**
     * Hides the dismiss button.
     */
    noDismissButton: { type: Boolean, default: void 0 },
    /**
     * Prevents the dialog from being dismissed when the escape key is pressed.
     */
    noDismissOnEsc: { type: Boolean, default: void 0 },
    /**
     * Prevents the dialog from being dismissed when clicking outside it.
     */
    noLightDismiss: { type: Boolean, default: void 0 },
    /**
     * Disables all means of dismissal for the dialog.
     */
    nonDismissible: { type: Boolean, default: void 0 },
    /**
     * Indicates dialog's state
     */
    open: { type: Boolean, default: void 0 },
    /**
     * Makes only the element's content area between the header and footer scrollable
     */
    scrollableBody: { type: Boolean, default: void 0 },
    subtitle: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * The `cancel` event fires when the user requests to close the dialog. You can prevent the dialog from closing by calling `.preventDefault()` on the event.
     * @type {CustomEvent<undefined>}
     */
    "cancel",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * The `close` event fires when the dialog closes (either via user interaction or via the API). It returns the return value inside the event's details property.
     * @type {CustomEvent<string>}
     */
    "close",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * The `open` event fires when the dialog opens.
     * @type {CustomEvent<undefined>}
     */
    "open",
    /**
     * Fires when the open value changes
     * @type {boolean}
     */
    "update:open"
  ],
  methods: {
    close() {
      return this.element?.close();
    },
    show() {
      return this.element?.show();
    },
    showModal() {
      return this.element?.showModal();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("dialog", registerDialog);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.dismissButtonAriaLabel !== void 0 ? { "dismiss-button-aria-label": this.dismissButtonAriaLabel } : {},
            ...this.fullWidthBody !== void 0 ? { "full-width-body": this.fullWidthBody } : {},
            ...this.headline !== void 0 ? { headline: this.headline } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.iconPlacement !== void 0 ? { "icon-placement": this.iconPlacement } : {},
            ...this.modal !== void 0 ? { modal: this.modal } : {},
            ...this.noDismissButton !== void 0 ? { "no-dismiss-button": this.noDismissButton } : {},
            ...this.noDismissOnEsc !== void 0 ? { "no-dismiss-on-esc": this.noDismissOnEsc } : {},
            ...this.noLightDismiss !== void 0 ? { "no-light-dismiss": this.noLightDismiss } : {},
            ...this.nonDismissible !== void 0 ? { "non-dismissible": this.nonDismissible } : {},
            ...this.open !== void 0 ? { open: this.open } : {},
            ...this.scrollableBody !== void 0 ? { "scrollable-body": this.scrollableBody } : {},
            ...this.subtitle !== void 0 ? { subtitle: this.subtitle } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            cancel: (event) => {
              this.$emit("cancel", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            close: (event) => {
              this.$emit("update:open", event.currentTarget.open);
              this.$emit("close", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            open: (event) => {
              this.$emit("update:open", event.currentTarget.open);
              this.$emit("open", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot action-items Use the action-items slot in order to add action buttons to the bottom of the dialog.
          handleNamedSlotV2("action-items", this.$slots["action-items"]),
          // @slot body Use the body slot in order to add custom HTML to the dialog.
          handleNamedSlotV2("body", this.$slots["body"]),
          // @slot footer Use the footer slot in order to add action buttons to the bottom of the dialog.
          handleNamedSlotV2("footer", this.$slots["footer"]),
          // @slot graphic Use the graphic slot in order to replace the icon.
          handleNamedSlotV2("graphic", this.$slots["graphic"]),
          // @slot main Assign nodes to the main slot to fully override a dialog’s predefined flow and style with your own.
          handleNamedSlotV2("main", this.$slots["main"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.dismissButtonAriaLabel !== void 0 ? { "^dismiss-button-aria-label": this.dismissButtonAriaLabel } : {},
          ...this.fullWidthBody !== void 0 && this.fullWidthBody !== false ? { "^full-width-body": this.fullWidthBody } : {},
          ...this.headline !== void 0 ? { "^headline": this.headline } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.iconPlacement !== void 0 ? { "^icon-placement": this.iconPlacement } : {},
          ...this.modal !== void 0 && this.modal !== false ? { "^modal": this.modal } : {},
          ...this.noDismissButton !== void 0 && this.noDismissButton !== false ? { "^no-dismiss-button": this.noDismissButton } : {},
          ...this.noDismissOnEsc !== void 0 && this.noDismissOnEsc !== false ? { "^no-dismiss-on-esc": this.noDismissOnEsc } : {},
          ...this.noLightDismiss !== void 0 && this.noLightDismiss !== false ? { "^no-light-dismiss": this.noLightDismiss } : {},
          ...this.nonDismissible !== void 0 && this.nonDismissible !== false ? { "^non-dismissible": this.nonDismissible } : {},
          ...this.open !== void 0 && this.open !== false ? { "^open": this.open } : {},
          ...this.scrollableBody !== void 0 && this.scrollableBody !== false ? { "^scrollable-body": this.scrollableBody } : {},
          ...this.subtitle !== void 0 ? { "^subtitle": this.subtitle } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onCancel: (event) => {
          this.$emit("cancel", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onClose: (event) => {
          this.$emit("update:open", event.currentTarget.open);
          this.$emit("close", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onOpen: (event) => {
          this.$emit("update:open", event.currentTarget.open);
          this.$emit("open", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot action-items Use the action-items slot in order to add action buttons to the bottom of the dialog.
        handleNamedSlotV3("action-items", this.$slots["action-items"]),
        // @slot body Use the body slot in order to add custom HTML to the dialog.
        handleNamedSlotV3("body", this.$slots["body"]),
        // @slot footer Use the footer slot in order to add action buttons to the bottom of the dialog.
        handleNamedSlotV3("footer", this.$slots["footer"]),
        // @slot graphic Use the graphic slot in order to replace the icon.
        handleNamedSlotV3("graphic", this.$slots["graphic"]),
        // @slot main Assign nodes to the main slot to fully override a dialog’s predefined flow and style with your own.
        handleNamedSlotV3("main", this.$slots["main"])
      ]
    );
  }
});

const VDivider = defineComponent({
  name: "VDivider",
  props: {
    /**
     * The appearance of the divider.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * The orientation of the divider.
     */
    orientation: {
      type: String,
      default: void 0
    },
    /**
     * The role of the element. This property will be removed
     */
    role: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("divider", registerDivider);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.orientation !== void 0 ? { orientation: this.orientation } : {},
            ...this.role !== void 0 ? { role: this.role } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.orientation !== void 0 ? { "^orientation": this.orientation } : {},
          ...this.role !== void 0 ? { "^role": this.role } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VEmptyState = defineComponent({
  name: "VEmptyState",
  props: {
    /**
     * The connotation the empty state should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * An optional headline for the empty state.
     */
    headline: { type: String, default: void 0 },
    /**
     * Icon for the empty state.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    iconDecoration: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("empty-state", registerEmptyState);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.headline !== void 0 ? { headline: this.headline } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.iconDecoration !== void 0 ? { "icon-decoration": this.iconDecoration } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot action-items Slot to add action items to the empty state
          handleNamedSlotV2("action-items", this.$slots["action-items"]),
          // @slot graphic The graphic slot allows overriding the icon with a custom illustration
          handleNamedSlotV2("graphic", this.$slots["graphic"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.headline !== void 0 ? { "^headline": this.headline } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.iconDecoration !== void 0 ? { "^icon-decoration": this.iconDecoration } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot action-items Slot to add action items to the empty state
        handleNamedSlotV3("action-items", this.$slots["action-items"]),
        // @slot graphic The graphic slot allows overriding the icon with a custom illustration
        handleNamedSlotV3("graphic", this.$slots["graphic"])
      ]
    );
  }
});

const VFab = defineComponent({
  name: "VFab",
  props: {
    /**
     * Determines if the element should receive document focus on page load.
     */
    autofocus: { type: Boolean, default: void 0 },
    /**
     * The connotation the fab should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * The id of a form to associate the element to.
     */
    form: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element for more details.
     */
    formaction: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element for more details.
     */
    formenctype: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element for more details.
     */
    formmethod: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element for more details.
     */
    formnovalidate: { type: Boolean, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button | <button> element for more details.
     */
    formtarget: {
      type: String,
      default: void 0
    },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * Indicates the icon affix alignment.
     */
    iconTrailing: { type: Boolean, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * Indicates the fab's label.
     */
    label: { type: String, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The size the FAB should have.
     */
    size: {
      type: String,
      default: void 0
    },
    /**
     * The button type.
     */
    type: {
      type: String,
      default: void 0
    },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    },
    /**
     * {@inheritDoc (FormAssociated:interface).validate}
     */
    validate() {
      return this.element?.validate();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("fab", registerFab);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.autofocus !== void 0 ? { autofocus: this.autofocus } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.form !== void 0 ? { form: this.form } : {},
            ...this.formaction !== void 0 ? { formaction: this.formaction } : {},
            ...this.formenctype !== void 0 ? { formenctype: this.formenctype } : {},
            ...this.formmethod !== void 0 ? { formmethod: this.formmethod } : {},
            ...this.formnovalidate !== void 0 ? { formnovalidate: this.formnovalidate } : {},
            ...this.formtarget !== void 0 ? { formtarget: this.formtarget } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.iconTrailing !== void 0 ? { "icon-trailing": this.iconTrailing } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.size !== void 0 ? { size: this.size } : {},
            ...this.type !== void 0 ? { type: this.type } : {},
            ...this.value !== void 0 ? { "current-value": this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.autofocus !== void 0 && this.autofocus !== false ? { "^autofocus": this.autofocus } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.form !== void 0 ? { "^form": this.form } : {},
          ...this.formaction !== void 0 ? { "^formaction": this.formaction } : {},
          ...this.formenctype !== void 0 ? { "^formenctype": this.formenctype } : {},
          ...this.formmethod !== void 0 ? { "^formmethod": this.formmethod } : {},
          ...this.formnovalidate !== void 0 && this.formnovalidate !== false ? { "^formnovalidate": this.formnovalidate } : {},
          ...this.formtarget !== void 0 ? { "^formtarget": this.formtarget } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.iconTrailing !== void 0 && this.iconTrailing !== false ? { "^icon-trailing": this.iconTrailing } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {},
          ...this.type !== void 0 ? { "^type": this.type } : {},
          ...this.value !== void 0 ? { "^current-value": this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"])
      ]
    );
  }
});

const VFilePicker = defineComponent({
  name: "VFilePicker",
  props: {
    /**
     * List of accepted files types
     */
    accept: { type: String, default: void 0 },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * Overrides the localized error message for file too big
     */
    fileTooBigError: { type: String, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * Overrides the localized error message for invalid file type
     */
    invalidFileTypeError: {
      type: String,
      default: void 0
    },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * The max file size that can be selected.
     */
    maxFileSize: { type: Number, default: void 0 },
    /**
     * The max files that can be selected.
     */
    maxFiles: { type: Number, default: void 0 },
    /**
     * Overrides the localized error message for max file exceed
     */
    maxFilesExceededError: {
      type: String,
      default: void 0
    },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * Single file state.
     */
    singleFile: { type: Boolean, default: void 0 },
    /**
     * The size the file-uploader should have.
     */
    size: {
      type: String,
      default: void 0
    },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Emitted when a file is added or removed.
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    acceptChanged(_oldValue, newValue) {
      return this.element?.acceptChanged(_oldValue, newValue);
    },
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    fileTooBigErrorChanged(_oldValue, newValue) {
      return this.element?.fileTooBigErrorChanged(_oldValue, newValue);
    },
    formResetCallback() {
      return this.element?.formResetCallback();
    },
    invalidFileTypeErrorChanged(_oldValue, newValue) {
      return this.element?.invalidFileTypeErrorChanged(
        _oldValue,
        newValue
      );
    },
    maxFileSizeChanged(_oldValue, newValue) {
      return this.element?.maxFileSizeChanged(_oldValue, newValue);
    },
    maxFilesChanged(_oldValue, newValue) {
      return this.element?.maxFilesChanged(_oldValue, newValue);
    },
    maxFilesExceededErrorChanged(_oldValue, newValue) {
      return this.element?.maxFilesExceededErrorChanged(
        _oldValue,
        newValue
      );
    },
    nameChanged(previous, next) {
      return this.element?.nameChanged(previous, next);
    },
    /**
     * Removes all files from the File Picker.
     */
    removeAllFiles() {
      return this.element?.removeAllFiles();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    },
    singleFileChanged() {
      return this.element?.singleFileChanged();
    },
    /**
     * {@inheritDoc (FormAssociated:interface).validate}
     */
    validate() {
      return this.element?.validate();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("file-picker", registerFilePicker);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.accept !== void 0 ? { accept: this.accept } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.fileTooBigError !== void 0 ? { "file-too-big-error": this.fileTooBigError } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.invalidFileTypeError !== void 0 ? { "invalid-file-type-error": this.invalidFileTypeError } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.maxFileSize !== void 0 ? { "max-file-size": this.maxFileSize } : {},
            ...this.maxFiles !== void 0 ? { "max-files": this.maxFiles } : {},
            ...this.maxFilesExceededError !== void 0 ? { "max-files-exceeded-error": this.maxFilesExceededError } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.singleFile !== void 0 ? { "single-file": this.singleFile } : {},
            ...this.size !== void 0 ? { size: this.size } : {},
            ...this.value !== void 0 ? { "current-value": this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the file-picker. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.accept !== void 0 ? { "^accept": this.accept } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.fileTooBigError !== void 0 ? { "^file-too-big-error": this.fileTooBigError } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.invalidFileTypeError !== void 0 ? { "^invalid-file-type-error": this.invalidFileTypeError } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.maxFileSize !== void 0 ? { "^max-file-size": this.maxFileSize } : {},
          ...this.maxFiles !== void 0 ? { "^max-files": this.maxFiles } : {},
          ...this.maxFilesExceededError !== void 0 ? { "^max-files-exceeded-error": this.maxFilesExceededError } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.singleFile !== void 0 && this.singleFile !== false ? { "^single-file": this.singleFile } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {},
          ...this.value !== void 0 ? { "^current-value": this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the file-picker. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"])
      ]
    );
  }
});

const VHeader = defineComponent({
  name: "VHeader",
  props: {
    /**
     * applies scheme alternate to header region
     */
    alternate: { type: Boolean, default: void 0 },
    /**
     * header elevation shadow
     */
    elevationShadow: { type: Boolean, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("header", registerHeader);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.alternate !== void 0 ? { alternate: this.alternate } : {},
            ...this.elevationShadow !== void 0 ? { "elevation-shadow": this.elevationShadow } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot action-items Nodes assigned to action-items slot will be set at the end of the header.
          handleNamedSlotV2("action-items", this.$slots["action-items"]),
          // @slot app-content Content vertically aligned with header.
          handleNamedSlotV2("app-content", this.$slots["app-content"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.alternate !== void 0 && this.alternate !== false ? { "^alternate": this.alternate } : {},
          ...this.elevationShadow !== void 0 && this.elevationShadow !== false ? { "^elevation-shadow": this.elevationShadow } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot action-items Nodes assigned to action-items slot will be set at the end of the header.
        handleNamedSlotV3("action-items", this.$slots["action-items"]),
        // @slot app-content Content vertically aligned with header.
        handleNamedSlotV3("app-content", this.$slots["app-content"])
      ]
    );
  }
});

const VIcon = defineComponent({
  name: "VIcon",
  props: {
    /**
     * The connotation the icon should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Provides a (screen reader only) descriptive label for the icon.
     */
    label: { type: String, default: void 0 },
    /**
     * Indicates which icon to resolve.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    name: { type: String, default: void 0 },
    size: {
      type: Number,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    nameChanged() {
      return this.element?.nameChanged();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("icon", registerIcon);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.size !== void 0 ? { size: this.size } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VLayout = defineComponent({
  name: "VLayout",
  props: {
    /**
     * sets the initial preferred auto-sizing from predefined available options
     */
    autoSizing: {
      type: String,
      default: void 0
    },
    /**
     * sets the initial preferred measure of a column from predefined available options
     */
    columnBasis: {
      type: String,
      default: void 0
    },
    /**
     * sets the initial preferred spacing of a column from predefined available options
     */
    columnSpacing: {
      type: String,
      default: void 0
    },
    /**
     * sets the initial preferred margin from predefined available options
     */
    gutters: {
      type: String,
      default: void 0
    },
    /**
     * sets the initial preferred spacing of a row from predefined available options
     */
    rowSpacing: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("layout", registerLayout);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.autoSizing !== void 0 ? { "auto-sizing": this.autoSizing } : {},
            ...this.columnBasis !== void 0 ? { "column-basis": this.columnBasis } : {},
            ...this.columnSpacing !== void 0 ? { "column-spacing": this.columnSpacing } : {},
            ...this.gutters !== void 0 ? { gutters: this.gutters } : {},
            ...this.rowSpacing !== void 0 ? { "row-spacing": this.rowSpacing } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.autoSizing !== void 0 ? { "^auto-sizing": this.autoSizing } : {},
          ...this.columnBasis !== void 0 ? { "^column-basis": this.columnBasis } : {},
          ...this.columnSpacing !== void 0 ? { "^column-spacing": this.columnSpacing } : {},
          ...this.gutters !== void 0 ? { "^gutters": this.gutters } : {},
          ...this.rowSpacing !== void 0 ? { "^row-spacing": this.rowSpacing } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VMenu = defineComponent({
  name: "VMenu",
  props: {
    /**
     * ID or direct reference to the component's anchor element.
     */
    anchor: {
      type: [String, HTMLElement],
      default: void 0
    },
    /**
     * indicates whether the menu will automatically close when focus moves away from it.
     */
    autoDismiss: { type: Boolean, default: void 0 },
    /**
     * indicates whether the menu is open
     */
    open: { type: Boolean, default: void 0 },
    /**
     * placement of the menu
     */
    placement: {
      type: String,
      default: void 0
    },
    /**
     * The strategy-absolute attribute.
     */
    positionStrategy: {
      type: String,
      default: void 0
    },
    /**
     * Controls how the menu opens and closes itself.
     */
    trigger: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fired when the menu is closed
     * @type {CustomEvent<undefined>}
     */
    "close",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fired when the menu is opened
     * @type {CustomEvent<undefined>}
     */
    "open"
  ],
  methods: {
    /**
     * Collapses any expanded Menu Items.
     */
    collapseExpandedItem() {
      return this.element?.collapseExpandedItem();
    },
    /**
     * Moves focus into the menu. If there is a child with the `autofocus` attribute, it will be focused.
     * Otherwise, the first focusable child will be focused.
     */
    focus() {
      return this.element?.focus();
    },
    openChanged(_, newValue) {
      return this.element?.openChanged(_, newValue);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("menu", registerMenu);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.autoDismiss !== void 0 ? { "auto-dismiss": this.autoDismiss } : {},
            ...this.open !== void 0 ? { open: this.open } : {},
            ...this.placement !== void 0 ? { placement: this.placement } : {},
            ...this.positionStrategy !== void 0 ? { "position-strategy": this.positionStrategy } : {},
            ...this.trigger !== void 0 ? { trigger: this.trigger } : {}
          },
          class: "vvd-component",
          domProps: {
            ...this.anchor !== void 0 ? { anchor: this.anchor } : {}
          },
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            close: (event) => {
              this.$emit("close", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            open: (event) => {
              this.$emit("open", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot action-items Used to add action items to the bottom of the menu.
          handleNamedSlotV2("action-items", this.$slots["action-items"]),
          // @slot anchor Used to set the anchor element for the menu.
          handleNamedSlotV2("anchor", this.$slots["anchor"]),
          // @slot header Used to add additional content to the top of the menu.
          handleNamedSlotV2("header", this.$slots["header"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.anchor !== void 0 ? { ".anchor": this.anchor } : {},
          ...this.autoDismiss !== void 0 && this.autoDismiss !== false ? { "^auto-dismiss": this.autoDismiss } : {},
          ...this.open !== void 0 && this.open !== false ? { "^open": this.open } : {},
          ...this.placement !== void 0 ? { "^placement": this.placement } : {},
          ...this.positionStrategy !== void 0 ? { "^position-strategy": this.positionStrategy } : {},
          ...this.trigger !== void 0 ? { "^trigger": this.trigger } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onClose: (event) => {
          this.$emit("close", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onOpen: (event) => {
          this.$emit("open", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot action-items Used to add action items to the bottom of the menu.
        handleNamedSlotV3("action-items", this.$slots["action-items"]),
        // @slot anchor Used to set the anchor element for the menu.
        handleNamedSlotV3("anchor", this.$slots["anchor"]),
        // @slot header Used to add additional content to the top of the menu.
        handleNamedSlotV3("header", this.$slots["header"])
      ]
    );
  }
});

const VMenuItem = defineComponent({
  name: "VMenuItem",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * Controls the appearance of the check indicator.
     */
    checkAppearance: {
      type: String,
      default: void 0
    },
    /**
     * Controls the placement of the menu item's checkmark or radio icon.
     */
    checkTrailing: { type: Boolean, default: void 0 },
    /**
     * The checked value of the element.
     */
    checked: { type: Boolean, default: void 0 },
    /**
     * The checked value of the element.
     */
    modelValue: { type: Boolean, default: void 0 },
    /**
     * The connotation the fab should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Whether the menu item should behave as a checkbox or radio button.
     */
    controlType: {
      type: String,
      default: void 0
    },
    /**
     * The disabled state of the element.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * The expanded state of the element.
     */
    expanded: { type: Boolean, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * The role of the element.
     */
    role: {
      type: String,
      default: void 0
    },
    /**
     * Indicates the menu item's text.
     */
    text: { type: String, default: void 0 },
    /**
     * Indicates the menu item's secondary text.
     */
    textSecondary: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fired when the item is triggered. Does not fire when a submenu is collapsed or expanded.
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fired when the expanded state changes.
     * @type {CustomEvent<HTMLElement>}
     */
    "expanded-change",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {boolean}
     */
    "update:modelValue"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("menu-item", registerMenuItem);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.checkAppearance !== void 0 ? { "check-appearance": this.checkAppearance } : {},
            ...this.checkTrailing !== void 0 ? { "check-trailing": this.checkTrailing } : {},
            ...(this.modelValue ?? this.checked) !== void 0 ? { checked: this.modelValue ?? this.checked } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.controlType !== void 0 ? { "control-type": this.controlType } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.expanded !== void 0 ? { expanded: this.expanded } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.role !== void 0 ? { role: this.role } : {},
            ...this.text !== void 0 ? { text: this.text } : {},
            ...this.textSecondary !== void 0 ? { "text-secondary": this.textSecondary } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("update:modelValue", event.currentTarget.checked);
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            "expanded-change": (event) => {
              this.$emit("expanded-change", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot meta Assign nodes to the `meta` slot to set a badge or an additional icon.
          handleNamedSlotV2("meta", this.$slots["meta"]),
          // @slot submenu Assign a Menu to the `submenu` slot to add a submenu.
          handleNamedSlotV2("submenu", this.$slots["submenu"]),
          // @slot trailing-meta Assign nodes to the `meta` slot to set a badge or an additional icon.
          handleNamedSlotV2("trailing-meta", this.$slots["trailing-meta"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.checkAppearance !== void 0 ? { "^check-appearance": this.checkAppearance } : {},
          ...this.checkTrailing !== void 0 && this.checkTrailing !== false ? { "^check-trailing": this.checkTrailing } : {},
          ...(this.modelValue ?? this.checked) !== void 0 && (this.modelValue ?? this.checked) !== false ? { "^checked": this.modelValue ?? this.checked } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.controlType !== void 0 ? { "^control-type": this.controlType } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.expanded !== void 0 && this.expanded !== false ? { "^expanded": this.expanded } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.role !== void 0 ? { "^role": this.role } : {},
          ...this.text !== void 0 ? { "^text": this.text } : {},
          ...this.textSecondary !== void 0 ? { "^text-secondary": this.textSecondary } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("update:modelValue", event.currentTarget.checked);
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onExpandedChange: (event) => {
          this.$emit("expanded-change", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot meta Assign nodes to the `meta` slot to set a badge or an additional icon.
        handleNamedSlotV3("meta", this.$slots["meta"]),
        // @slot submenu Assign a Menu to the `submenu` slot to add a submenu.
        handleNamedSlotV3("submenu", this.$slots["submenu"]),
        // @slot trailing-meta Assign nodes to the `meta` slot to set a badge or an additional icon.
        handleNamedSlotV3("trailing-meta", this.$slots["trailing-meta"])
      ]
    );
  }
});

const VNav = defineComponent({
  name: "VNav",
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("nav", registerNav);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {},
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({}),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VNavDisclosure = defineComponent({
  name: "VNavDisclosure",
  props: {
    /**
     * The appearance nav-disclosure should have.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * The connotation nav-disclosure should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Indicates there's a nested active nav-item within nav-disclosure
     */
    current: { type: Boolean, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    label: { type: String, default: void 0 },
    /**
     * Indicates whether the nav-disclosure is open
     */
    open: { type: Boolean, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Event emitted when the nav disclosure is toggled.
     * @type {CustomEvent<undefined>}
     */
    "toggle"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent(
      "nav-disclosure",
      registerNavDisclosure
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.current !== void 0 ? { current: this.current } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.open !== void 0 ? { open: this.open } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            toggle: (event) => {
              this.$emit("toggle", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"]),
          // @slot meta A slot to add additional content to the nav disclosure.
          handleNamedSlotV2("meta", this.$slots["meta"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.current !== void 0 && this.current !== false ? { "^current": this.current } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.open !== void 0 && this.open !== false ? { "^open": this.open } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onToggle: (event) => {
          this.$emit("toggle", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"]),
        // @slot meta A slot to add additional content to the nav disclosure.
        handleNamedSlotV3("meta", this.$slots["meta"])
      ]
    );
  }
});

const VNavItem = defineComponent({
  name: "VNavItem",
  props: {
    /**
     * Sets the nav item's appearance
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * Sets the connotation color of the selected nav item
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Sets the nav item as a current
     */
    current: { type: Boolean, default: void 0 },
    /**
     * Prompts the user to save the linked URL. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    download: { type: String, default: void 0 },
    /**
     * The URL the hyperlink references. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    href: { type: String, default: void 0 },
    /**
     * Hints at the language of the referenced resource. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    hreflang: { type: String, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    ping: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    referrerpolicy: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    rel: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    target: {
      type: String,
      default: void 0
    },
    /**
     * Text of the nav item
     */
    text: { type: String, default: void 0 },
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a | <a> element  for more information.
     */
    type: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("nav-item", registerNavItem);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.current !== void 0 ? { current: this.current } : {},
            ...this.download !== void 0 ? { download: this.download } : {},
            ...this.href !== void 0 ? { href: this.href } : {},
            ...this.hreflang !== void 0 ? { hreflang: this.hreflang } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.ping !== void 0 ? { ping: this.ping } : {},
            ...this.referrerpolicy !== void 0 ? { referrerpolicy: this.referrerpolicy } : {},
            ...this.rel !== void 0 ? { rel: this.rel } : {},
            ...this.target !== void 0 ? { target: this.target } : {},
            ...this.text !== void 0 ? { text: this.text } : {},
            ...this.type !== void 0 ? { type: this.type } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"]),
          // @slot meta A slot to add additional content to the nav item.
          handleNamedSlotV2("meta", this.$slots["meta"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.current !== void 0 && this.current !== false ? { "^current": this.current } : {},
          ...this.download !== void 0 ? { "^download": this.download } : {},
          ...this.href !== void 0 ? { "^href": this.href } : {},
          ...this.hreflang !== void 0 ? { "^hreflang": this.hreflang } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.ping !== void 0 ? { "^ping": this.ping } : {},
          ...this.referrerpolicy !== void 0 ? { "^referrerpolicy": this.referrerpolicy } : {},
          ...this.rel !== void 0 ? { "^rel": this.rel } : {},
          ...this.target !== void 0 ? { "^target": this.target } : {},
          ...this.text !== void 0 ? { "^text": this.text } : {},
          ...this.type !== void 0 ? { "^type": this.type } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"]),
        // @slot meta A slot to add additional content to the nav item.
        handleNamedSlotV3("meta", this.$slots["meta"])
      ]
    );
  }
});

const VNote = defineComponent({
  name: "VNote",
  props: {
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Indicates the note's headline text
     */
    headline: { type: String, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("note", registerNote);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.headline !== void 0 ? { headline: this.headline } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.headline !== void 0 ? { "^headline": this.headline } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"])
      ]
    );
  }
});

const VNumberField = defineComponent({
  name: "VNumberField",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    appearance: {
      type: String,
      default: void 0
    },
    autoComplete: { type: String, default: void 0 },
    /**
     * Indicates that this element should get focus after the page finishes loading. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefautofocus | autofocus HTML attribute for more information.
     */
    autofocus: { type: Boolean, default: void 0 },
    decrementButtonAriaLabel: {
      type: String,
      default: void 0
    },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    incrementButtonAriaLabel: {
      type: String,
      default: void 0
    },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * Allows associating a https://developer.mozilla.org/en-US/docs/Web/HTML/Element/datalist | datalist to the element by https://developer.mozilla.org/en-US/docs/Web/API/Element/id.
     */
    list: { type: String, default: void 0 },
    /**
     * The maximum the value can be
     */
    max: { type: Number, default: void 0 },
    /**
     * The maximum number of characters a user can enter.
     */
    maxlength: { type: Number, default: void 0 },
    /**
     * The minimum the value can be
     */
    min: { type: Number, default: void 0 },
    /**
     * The minimum number of characters a user can enter.
     */
    minlength: { type: Number, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * Sets the placeholder value of the element, generally used to provide a hint to the user.
     */
    placeholder: { type: String, default: void 0 },
    /**
     * When true, the control will be immutable by user interaction. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly | readonly HTML attribute for more information.
     */
    readonly: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The size the number-field should have.
     */
    scale: {
      type: String,
      default: void 0
    },
    shape: { type: String, default: void 0 },
    /**
     * Sets the width of the element to a specified number of characters.
     */
    size: { type: Number, default: void 0 },
    /**
     * Amount to increment or decrement the value by
     */
    step: { type: Number, default: void 0 },
    /**
     * Provides a custom success message. Any current error state will be overridden.
     */
    successText: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    modelValue: { type: String, default: void 0 },
    /**
     * The value property, typed as a number.
     */
    valueAsNumber: { type: Number, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires a custom 'change' event when the value has changed
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires a custom 'input' event when the value has changed
     * @type {CustomEvent<undefined>}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue",
    /**
     * Fires when the valueAsNumber value changes
     * @type {number}
     */
    "update:valueAsNumber"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    },
    /**
     * Selects all the text in the number field
     */
    select() {
      return this.element?.select();
    },
    stepChanged(_, next) {
      return this.element?.stepChanged(_, next);
    },
    /**
     * Decrements the value using the step value
     */
    stepDown() {
      return this.element?.stepDown();
    },
    /**
     * Increments the value using the step value
     */
    stepUp() {
      return this.element?.stepUp();
    },
    /**
     * {@inheritDoc (FormAssociated:interface).validate}
     */
    validate() {
      return this.element?.validate();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent(
      "number-field",
      registerNumberField
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.autoComplete !== void 0 ? { autoComplete: this.autoComplete } : {},
            ...this.autofocus !== void 0 ? { autofocus: this.autofocus } : {},
            ...this.decrementButtonAriaLabel !== void 0 ? { "decrement-button-aria-label": this.decrementButtonAriaLabel } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.incrementButtonAriaLabel !== void 0 ? { "increment-button-aria-label": this.incrementButtonAriaLabel } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.list !== void 0 ? { list: this.list } : {},
            ...this.max !== void 0 ? { max: this.max } : {},
            ...this.maxlength !== void 0 ? { maxlength: this.maxlength } : {},
            ...this.min !== void 0 ? { min: this.min } : {},
            ...this.minlength !== void 0 ? { minlength: this.minlength } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.placeholder !== void 0 ? { placeholder: this.placeholder } : {},
            ...this.readonly !== void 0 ? { readonly: this.readonly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.scale !== void 0 ? { scale: this.scale } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.size !== void 0 ? { size: this.size } : {},
            ...this.step !== void 0 ? { step: this.step } : {},
            ...this.successText !== void 0 ? { "success-text": this.successText } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { "current-value": this.modelValue ?? this.value } : {}
          },
          class: "vvd-component",
          domProps: {
            ...this.valueAsNumber !== void 0 ? { valueAsNumber: this.valueAsNumber } : {}
          },
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit(
                "update:valueAsNumber",
                event.currentTarget.valueAsNumber
              );
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the number-field. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.autoComplete !== void 0 ? { "^autoComplete": this.autoComplete } : {},
          ...this.autofocus !== void 0 && this.autofocus !== false ? { "^autofocus": this.autofocus } : {},
          ...this.decrementButtonAriaLabel !== void 0 ? { "^decrement-button-aria-label": this.decrementButtonAriaLabel } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.incrementButtonAriaLabel !== void 0 ? { "^increment-button-aria-label": this.incrementButtonAriaLabel } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.list !== void 0 ? { "^list": this.list } : {},
          ...this.max !== void 0 ? { "^max": this.max } : {},
          ...this.maxlength !== void 0 ? { "^maxlength": this.maxlength } : {},
          ...this.min !== void 0 ? { "^min": this.min } : {},
          ...this.minlength !== void 0 ? { "^minlength": this.minlength } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.placeholder !== void 0 ? { "^placeholder": this.placeholder } : {},
          ...this.readonly !== void 0 && this.readonly !== false ? { "^readonly": this.readonly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.scale !== void 0 ? { "^scale": this.scale } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {},
          ...this.step !== void 0 ? { "^step": this.step } : {},
          ...this.successText !== void 0 ? { "^success-text": this.successText } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^current-value": this.modelValue ?? this.value } : {},
          ...this.valueAsNumber !== void 0 ? { ".valueAsNumber": this.valueAsNumber } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("update:valueAsNumber", event.currentTarget.valueAsNumber);
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the number-field. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"])
      ]
    );
  }
});

const VOption = defineComponent({
  name: "VOption",
  props: {
    /**
     * The defaultSelected state of the option.
     */
    defaultSelected: { type: Boolean, default: void 0 },
    /**
     * The disabled state of the option.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * Indicates the icon affix alignment.
     */
    iconTrailing: { type: Boolean, default: void 0 },
    label: { type: String, default: void 0 },
    /**
     * Text to highlighted as matching a search query.
     */
    matchedText: { type: String, default: void 0 },
    /**
     * The checked state of the control.
     */
    selected: { type: Boolean, default: void 0 },
    /**
     * When displayed as a tag, the connotation of the tag.
     */
    tagConnotation: {
      type: String,
      default: void 0
    },
    text: { type: String, default: void 0 },
    /**
     * The value of the option.
     */
    value: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("option", registerOption);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.defaultSelected !== void 0 ? { selected: this.defaultSelected } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.iconTrailing !== void 0 ? { "icon-trailing": this.iconTrailing } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.matchedText !== void 0 ? { "matched-text": this.matchedText } : {},
            ...this.selected !== void 0 ? { "current-selected": this.selected } : {},
            ...this.tagConnotation !== void 0 ? { "tag-connotation": this.tagConnotation } : {},
            ...this.text !== void 0 ? { text: this.text } : {},
            ...this.value !== void 0 ? { value: this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.defaultSelected !== void 0 && this.defaultSelected !== false ? { "^selected": this.defaultSelected } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.iconTrailing !== void 0 && this.iconTrailing !== false ? { "^icon-trailing": this.iconTrailing } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.matchedText !== void 0 ? { "^matched-text": this.matchedText } : {},
          ...this.selected !== void 0 && this.selected !== false ? { "^current-selected": this.selected } : {},
          ...this.tagConnotation !== void 0 ? { "^tag-connotation": this.tagConnotation } : {},
          ...this.text !== void 0 ? { "^text": this.text } : {},
          ...this.value !== void 0 ? { "^value": this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"])
      ]
    );
  }
});

const VPagination = defineComponent({
  name: "VPagination",
  props: {
    navIcons: { type: Boolean, default: void 0 },
    selectedIndex: { type: Number, default: void 0 },
    /**
     * The shape the pagination should have.
     */
    shape: { type: String, default: void 0 },
    /**
     * The size the pagination should have.
     */
    size: {
      type: String,
      default: void 0
    },
    total: { type: Number, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the page changes.
     * @type {CustomEvent<{selectedIndex: number, total: number, oldIndex: number}>}
     */
    "pagination-change"
  ],
  methods: {
    paginationButtonsChanged(_, newValue) {
      return this.element?.paginationButtonsChanged(_, newValue);
    },
    selectedIndexChanged(oldValue, newValue) {
      return this.element?.selectedIndexChanged(oldValue, newValue);
    },
    totalChanged(_, newValue) {
      return this.element?.totalChanged(_, newValue);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("pagination", registerPagination);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.navIcons !== void 0 ? { "nav-icons": this.navIcons } : {},
            ...this.selectedIndex !== void 0 ? { "selected-index": this.selectedIndex } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.size !== void 0 ? { size: this.size } : {},
            ...this.total !== void 0 ? { total: this.total } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            "pagination-change": (event) => {
              this.$emit("pagination-change", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.navIcons !== void 0 && this.navIcons !== false ? { "^nav-icons": this.navIcons } : {},
          ...this.selectedIndex !== void 0 ? { "^selected-index": this.selectedIndex } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {},
          ...this.total !== void 0 ? { "^total": this.total } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onPaginationChange: (event) => {
          this.$emit("pagination-change", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VProgress = defineComponent({
  name: "VProgress",
  props: {
    /**
     * Indicates the progress' connotation.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * The maximum value
     */
    max: { type: Number, default: void 0 },
    /**
     * The minimum value
     */
    min: { type: Number, default: void 0 },
    /**
     * Indicates the progress is paused
     */
    paused: { type: Boolean, default: void 0 },
    /**
     * Indicates the progress' reverse status.
     */
    reverse: { type: Boolean, default: void 0 },
    /**
     * Indicates the progress' shape.
     */
    shape: {
      type: String,
      default: void 0
    },
    /**
     * The value of the progress
     */
    value: { type: Number, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("progress", registerProgress);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.max !== void 0 ? { max: this.max } : {},
            ...this.min !== void 0 ? { min: this.min } : {},
            ...this.paused !== void 0 ? { paused: this.paused } : {},
            ...this.reverse !== void 0 ? { reverse: this.reverse } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.value !== void 0 ? { value: this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.max !== void 0 ? { "^max": this.max } : {},
          ...this.min !== void 0 ? { "^min": this.min } : {},
          ...this.paused !== void 0 && this.paused !== false ? { "^paused": this.paused } : {},
          ...this.reverse !== void 0 && this.reverse !== false ? { "^reverse": this.reverse } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.value !== void 0 ? { "^value": this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VProgressRing = defineComponent({
  name: "VProgressRing",
  props: {
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * The maximum value
     */
    max: { type: Number, default: void 0 },
    /**
     * The minimum value
     */
    min: { type: Number, default: void 0 },
    /**
     * Indicates the progress is paused
     */
    paused: { type: Boolean, default: void 0 },
    size: {
      type: Number,
      default: void 0
    },
    /**
     * The value of the progress
     */
    value: { type: Number, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent(
      "progress-ring",
      registerProgressRing
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.max !== void 0 ? { max: this.max } : {},
            ...this.min !== void 0 ? { min: this.min } : {},
            ...this.paused !== void 0 ? { paused: this.paused } : {},
            ...this.size !== void 0 ? { size: this.size } : {},
            ...this.value !== void 0 ? { value: this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.max !== void 0 ? { "^max": this.max } : {},
          ...this.min !== void 0 ? { "^min": this.min } : {},
          ...this.paused !== void 0 && this.paused !== false ? { "^paused": this.paused } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {},
          ...this.value !== void 0 ? { "^value": this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VRadio = defineComponent({
  name: "VRadio",
  props: {
    ariaLabel: { type: String, default: void 0 },
    /**
     * The current checkedness of the element.
     */
    checked: { type: Boolean, default: void 0 },
    /**
     * The connotation the radioButton should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * The default checkedness of the element. This value sets the `checked` property
     * only when the `checked` property has not been explicitly set.
     */
    defaultChecked: { type: Boolean, default: void 0 },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * When true, the control will be immutable by user interaction. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly | readonly HTML attribute for more information.
     */
    readonly: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires a custom 'change' event when the value changes
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("radio", registerRadio);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.ariaLabel !== void 0 ? { "aria-label": this.ariaLabel } : {},
            ...this.checked !== void 0 ? { "current-checked": this.checked } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.defaultChecked !== void 0 ? { checked: this.defaultChecked } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.readonly !== void 0 ? { readonly: this.readonly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.value !== void 0 ? { "current-value": this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.ariaLabel !== void 0 ? { "^aria-label": this.ariaLabel } : {},
          ...this.checked !== void 0 && this.checked !== false ? { "^current-checked": this.checked } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.defaultChecked !== void 0 && this.defaultChecked !== false ? { "^checked": this.defaultChecked } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.readonly !== void 0 && this.readonly !== false ? { "^readonly": this.readonly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.value !== void 0 ? { "^current-value": this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VRadioGroup = defineComponent({
  name: "VRadioGroup",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * Disables the radio group and child radios.
     */
    disabled: { type: Boolean, default: void 0 },
    errorText: { type: String, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * The label for the radio group
     */
    label: { type: String, default: void 0 },
    /**
     * The name of the radio group. Setting this value will set the name value
     * for all child radio elements.
     */
    name: { type: String, default: void 0 },
    /**
     * The orientation of the group
     */
    orientation: {
      type: String,
      default: void 0
    },
    /**
     * When true, the child radios will be immutable by user interaction. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly | readonly HTML attribute for more information.
     */
    readonly: { type: Boolean, default: void 0 },
    /**
     * When true, the radio group and all its radio buttons will be required
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The value of the checked radio
     */
    value: { type: String, default: void 0 },
    /**
     * The value of the checked radio
     */
    modelValue: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires a custom 'change' event when the value changes
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue"
  ],
  methods: {
    nameChanged() {
      return this.element?.nameChanged();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("radio-group", registerRadioGroup);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.orientation !== void 0 ? { orientation: this.orientation } : {},
            ...this.readonly !== void 0 ? { readonly: this.readonly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { value: this.modelValue ?? this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the text-field. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.orientation !== void 0 ? { "^orientation": this.orientation } : {},
          ...this.readonly !== void 0 && this.readonly !== false ? { "^readonly": this.readonly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^value": this.modelValue ?? this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the text-field. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"])
      ]
    );
  }
});

const VRangeSlider = defineComponent({
  name: "VRangeSlider",
  props: {
    /**
     * Aria label for the end thumb
     */
    ariaEndLabel: { type: String, default: void 0 },
    /**
     * Aria label for the start thumb
     */
    ariaStartLabel: { type: String, default: void 0 },
    /**
     * The connotation of the component
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * The current end value of the element. This property serves as a mechanism
     * to set the `end` property through both property assignment and the
     * .setAttribute() method. This is useful for setting the field's value
     * in UI libraries that bind data through the .setAttribute() API
     * and don't support IDL attribute binding.
     */
    end: { type: String, default: void 0 },
    /**
     * The initial end value. This value sets the `end` property
     * only when the `end` property has not been explicitly set.
     */
    initialEnd: { type: String, default: void 0 },
    /**
     * The initial start value. This value sets the `start` property
     * only when the `start` property has not been explicitly set.
     */
    initialStart: { type: String, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * Display markers on/off
     */
    markers: { type: Boolean, default: void 0 },
    /**
     * The maximum value of the range.
     */
    max: { type: Number, default: void 0 },
    /**
     * The minimum value of the range.
     */
    min: { type: Number, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * The orientation of the slider.
     */
    orientation: {
      type: String,
      default: void 0
    },
    /**
     * Show current values on the thumbs.
     */
    pin: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The current start value of the element. This property serves as a mechanism
     * to set the `start` property through both property assignment and the
     * .setAttribute() method. This is useful for setting the field's value
     * in UI libraries that bind data through the .setAttribute() API
     * and don't support IDL attribute binding.
     */
    start: { type: String, default: void 0 },
    /**
     * Value to increment or decrement via arrow keys, mouse click or drag.
     */
    step: { type: Number, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Event emitted when either the start or end value changes.
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Event emitted when either the start or end value changes.
     * @type {CustomEvent<undefined>}
     */
    "input",
    /**
     * Event emitted when the end value changes
     * @type {CustomEvent<undefined>}
     */
    "input:end",
    /**
     * Event emitted when the start value changes
     * @type {CustomEvent<undefined>}
     */
    "input:start",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the end value changes
     * @type {string}
     */
    "update:end",
    /**
     * Fires when the start value changes
     * @type {string}
     */
    "update:start"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    formResetCallback() {
      return this.element?.formResetCallback();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent(
      "range-slider",
      registerRangeSlider
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.ariaEndLabel !== void 0 ? { "aria-end-label": this.ariaEndLabel } : {},
            ...this.ariaStartLabel !== void 0 ? { "aria-start-label": this.ariaStartLabel } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.end !== void 0 ? { "current-end": this.end } : {},
            ...this.initialEnd !== void 0 ? { end: this.initialEnd } : {},
            ...this.initialStart !== void 0 ? { start: this.initialStart } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.markers !== void 0 ? { markers: this.markers } : {},
            ...this.max !== void 0 ? { max: this.max } : {},
            ...this.min !== void 0 ? { min: this.min } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.orientation !== void 0 ? { orientation: this.orientation } : {},
            ...this.pin !== void 0 ? { pin: this.pin } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.start !== void 0 ? { "current-start": this.start } : {},
            ...this.step !== void 0 ? { step: this.step } : {},
            ...this.value !== void 0 ? { "current-value": this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            "input:end": (event) => {
              this.$emit("update:end", event.currentTarget.end);
              this.$emit("input:end", event);
            },
            "input:start": (event) => {
              this.$emit("update:start", event.currentTarget.start);
              this.$emit("input:start", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.ariaEndLabel !== void 0 ? { "^aria-end-label": this.ariaEndLabel } : {},
          ...this.ariaStartLabel !== void 0 ? { "^aria-start-label": this.ariaStartLabel } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.end !== void 0 ? { "^current-end": this.end } : {},
          ...this.initialEnd !== void 0 ? { "^end": this.initialEnd } : {},
          ...this.initialStart !== void 0 ? { "^start": this.initialStart } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.markers !== void 0 && this.markers !== false ? { "^markers": this.markers } : {},
          ...this.max !== void 0 ? { "^max": this.max } : {},
          ...this.min !== void 0 ? { "^min": this.min } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.orientation !== void 0 ? { "^orientation": this.orientation } : {},
          ...this.pin !== void 0 && this.pin !== false ? { "^pin": this.pin } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.start !== void 0 ? { "^current-start": this.start } : {},
          ...this.step !== void 0 ? { "^step": this.step } : {},
          ...this.value !== void 0 ? { "^current-value": this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        "onInput:end": (event) => {
          this.$emit("update:end", event.currentTarget.end);
          this.$emit("input:end", event);
        },
        "onInput:start": (event) => {
          this.$emit("update:start", event.currentTarget.start);
          this.$emit("input:start", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VRichTextEditor = defineComponent({
  name: "VRichTextEditor",
  props: {
    placeholder: { type: String, default: void 0 },
    selectionEnd: { type: Number, default: void 0 },
    selectionStart: { type: Number, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    addInlineImage(imageProps) {
      return this.element?.addInlineImage(imageProps);
    },
    focus() {
      return this.element?.focus();
    },
    placeholderChanged() {
      return this.element?.placeholderChanged();
    },
    scrollToAttachments(additionalPixels) {
      return this.element?.scrollToAttachments(additionalPixels);
    },
    selectionEndChanged() {
      return this.element?.selectionEndChanged();
    },
    selectionStartChanged() {
      return this.element?.selectionStartChanged();
    },
    setSelectionDecoration(decoration) {
      return this.element?.setSelectionDecoration(decoration);
    },
    setSelectionTextSize(textSize) {
      return this.element?.setSelectionTextSize(textSize);
    },
    setTextBlock(blockType) {
      return this.element?.setTextBlock(blockType);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent(
      "rich-text-editor",
      registerRichTextEditor
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.placeholder !== void 0 ? { placeholder: this.placeholder } : {},
            ...this.selectionEnd !== void 0 ? { "selection-end": this.selectionEnd } : {},
            ...this.selectionStart !== void 0 ? { "selection-start": this.selectionStart } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.placeholder !== void 0 ? { "^placeholder": this.placeholder } : {},
          ...this.selectionEnd !== void 0 ? { "^selection-end": this.selectionEnd } : {},
          ...this.selectionStart !== void 0 ? { "^selection-start": this.selectionStart } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VSearchableSelect = defineComponent({
  name: "VSearchableSelect",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * Adds a clear button to the input field that clears the selected values.
     */
    clearable: { type: Boolean, default: void 0 },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    externalTags: { type: Boolean, default: void 0 },
    fixedDropdown: { type: Boolean, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * Indicates the icon affix alignment.
     */
    iconTrailing: { type: Boolean, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * Whether the component is in a loading state.
     */
    loading: { type: Boolean, default: void 0 },
    maxLines: { type: Number, default: void 0 },
    maxSelected: { type: Number, default: void 0 },
    multiple: { type: Boolean, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    open: { type: Boolean, default: void 0 },
    /**
     * Function to filter the options to display.
     */
    optionFilter: {
      type: null,
      default: void 0
    },
    placeholder: { type: String, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    shape: { type: String, default: void 0 },
    /**
     * Provides a custom success message. Any current error state will be overridden.
     */
    successText: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    modelValue: { type: String, default: void 0 },
    /**
     * List of selected option values.
     */
    values: { type: Array, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fired when the selected options change
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fired when the selected options change
     * @type {CustomEvent<undefined>}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fired when the search text changes
     * @type {CustomEvent<undefined>}
     */
    "search-text-change",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue",
    /**
     * Fires when the values value changes
     * @type {string[]}
     */
    "update:values"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent(
      "searchable-select",
      registerSearchableSelect
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.clearable !== void 0 ? { clearable: this.clearable } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.externalTags !== void 0 ? { "external-tags": this.externalTags } : {},
            ...this.fixedDropdown !== void 0 ? { "fixed-dropdown": this.fixedDropdown } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.iconTrailing !== void 0 ? { "icon-trailing": this.iconTrailing } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.loading !== void 0 ? { loading: this.loading } : {},
            ...this.maxLines !== void 0 ? { "max-lines": this.maxLines } : {},
            ...this.maxSelected !== void 0 ? { "max-selected": this.maxSelected } : {},
            ...this.multiple !== void 0 ? { multiple: this.multiple } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.open !== void 0 ? { open: this.open } : {},
            ...this.placeholder !== void 0 ? { placeholder: this.placeholder } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.successText !== void 0 ? { "success-text": this.successText } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { "current-value": this.modelValue ?? this.value } : {}
          },
          class: "vvd-component",
          domProps: {
            ...this.optionFilter !== void 0 ? { optionFilter: this.optionFilter } : {},
            ...this.values !== void 0 ? { values: this.values } : {}
          },
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit("update:values", event.currentTarget.values);
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            "search-text-change": (event) => {
              this.$emit("search-text-change", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the component. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"]),
          // @slot icon The preferred way to add an icon to the control.
          handleNamedSlotV2("icon", this.$slots["icon"]),
          // @slot loading-options Message that appears there are no options to display and the component is in a loading state.
          handleNamedSlotV2("loading-options", this.$slots["loading-options"]),
          // @slot meta Slot to add meta content to the control.
          handleNamedSlotV2("meta", this.$slots["meta"]),
          // @slot no-matches Message that appears when no options match the search query.
          handleNamedSlotV2("no-matches", this.$slots["no-matches"]),
          // @slot no-options Message that appears when no options are available.
          handleNamedSlotV2("no-options", this.$slots["no-options"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.clearable !== void 0 && this.clearable !== false ? { "^clearable": this.clearable } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.externalTags !== void 0 && this.externalTags !== false ? { "^external-tags": this.externalTags } : {},
          ...this.fixedDropdown !== void 0 && this.fixedDropdown !== false ? { "^fixed-dropdown": this.fixedDropdown } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.iconTrailing !== void 0 && this.iconTrailing !== false ? { "^icon-trailing": this.iconTrailing } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.loading !== void 0 && this.loading !== false ? { "^loading": this.loading } : {},
          ...this.maxLines !== void 0 ? { "^max-lines": this.maxLines } : {},
          ...this.maxSelected !== void 0 ? { "^max-selected": this.maxSelected } : {},
          ...this.multiple !== void 0 && this.multiple !== false ? { "^multiple": this.multiple } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.open !== void 0 && this.open !== false ? { "^open": this.open } : {},
          ...this.optionFilter !== void 0 ? { ".optionFilter": this.optionFilter } : {},
          ...this.placeholder !== void 0 ? { "^placeholder": this.placeholder } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.successText !== void 0 ? { "^success-text": this.successText } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^current-value": this.modelValue ?? this.value } : {},
          ...this.values !== void 0 ? { ".values": this.values } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("update:values", event.currentTarget.values);
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onSearchTextChange: (event) => {
          this.$emit("search-text-change", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the component. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"]),
        // @slot icon The preferred way to add an icon to the control.
        handleNamedSlotV3("icon", this.$slots["icon"]),
        // @slot loading-options Message that appears there are no options to display and the component is in a loading state.
        handleNamedSlotV3(
          "loading-options",
          this.$slots["loading-options"]
        ),
        // @slot meta Slot to add meta content to the control.
        handleNamedSlotV3("meta", this.$slots["meta"]),
        // @slot no-matches Message that appears when no options match the search query.
        handleNamedSlotV3("no-matches", this.$slots["no-matches"]),
        // @slot no-options Message that appears when no options are available.
        handleNamedSlotV3("no-options", this.$slots["no-options"])
      ]
    );
  }
});

const VSelect = defineComponent({
  name: "VSelect",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * The appearance attribute.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * The fixed-dropdown attribute.
     */
    fixedDropdown: { type: Boolean, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * Indicates the icon affix alignment.
     */
    iconTrailing: { type: Boolean, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * Indicates if the listbox is in multi-selection mode.
     */
    multiple: { type: Boolean, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * The open attribute.
     */
    open: { type: Boolean, default: void 0 },
    /**
     * The placeholder attribute.
     */
    placeholder: { type: String, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The size the select should have.
     */
    scale: {
      type: String,
      default: void 0
    },
    /**
     * The shape attribute.
     */
    shape: { type: String, default: void 0 },
    /**
     * Provides a custom success message. Any current error state will be overridden.
     */
    successText: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    modelValue: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires a custom 'change' event when the value updates
     * @type {CustomEvent<HTMLElement>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires a custom 'input' event when the value updates
     * @type {CustomEvent<undefined>}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    formResetCallback() {
      return this.element?.formResetCallback();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    },
    /**
     * Moves focus to the first selectable option.
     */
    selectFirstOption() {
      return this.element?.selectFirstOption();
    },
    setDefaultSelectedOption() {
      return this.element?.setDefaultSelectedOption();
    },
    /**
     * Sets an option as selected and gives it focus.
     */
    setSelectedOptions() {
      return this.element?.setSelectedOptions();
    },
    slottedOptionsChanged(prev, next) {
      return this.element?.slottedOptionsChanged(prev, next);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("select", registerSelect);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.fixedDropdown !== void 0 ? { "fixed-dropdown": this.fixedDropdown } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.iconTrailing !== void 0 ? { "icon-trailing": this.iconTrailing } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.multiple !== void 0 ? { multiple: this.multiple } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.open !== void 0 ? { open: this.open } : {},
            ...this.placeholder !== void 0 ? { placeholder: this.placeholder } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.scale !== void 0 ? { scale: this.scale } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.successText !== void 0 ? { "success-text": this.successText } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { "current-value": this.modelValue ?? this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the select. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"]),
          // @slot icon The preferred way to add an icon to the select control.
          handleNamedSlotV2("icon", this.$slots["icon"]),
          // @slot meta Slot to add meta content to the select control.
          handleNamedSlotV2("meta", this.$slots["meta"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.fixedDropdown !== void 0 && this.fixedDropdown !== false ? { "^fixed-dropdown": this.fixedDropdown } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.iconTrailing !== void 0 && this.iconTrailing !== false ? { "^icon-trailing": this.iconTrailing } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.multiple !== void 0 && this.multiple !== false ? { "^multiple": this.multiple } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.open !== void 0 && this.open !== false ? { "^open": this.open } : {},
          ...this.placeholder !== void 0 ? { "^placeholder": this.placeholder } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.scale !== void 0 ? { "^scale": this.scale } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.successText !== void 0 ? { "^success-text": this.successText } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^current-value": this.modelValue ?? this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the select. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"]),
        // @slot icon The preferred way to add an icon to the select control.
        handleNamedSlotV3("icon", this.$slots["icon"]),
        // @slot meta Slot to add meta content to the select control.
        handleNamedSlotV3("meta", this.$slots["meta"])
      ]
    );
  }
});

const VSelectableBox = defineComponent({
  name: "VSelectableBox",
  props: {
    /**
     * Controls the checked state of the box
     */
    checked: { type: Boolean, default: void 0 },
    clickable: { type: Boolean, default: void 0 },
    /**
     * Makes the entire selectable box clickable
     */
    clickableBox: { type: Boolean, default: void 0 },
    /**
     * The connotation the selectable box should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Defines where the control (checkbox or radio) should be located.
     */
    controlPlacement: {
      type: String,
      default: void 0
    },
    /**
     * The type of control the box should have: checkbox or radio.
     */
    controlType: {
      type: String,
      default: void 0
    },
    /**
     * Removes the padding around the box's slot content
     */
    tight: { type: Boolean, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fired when the checked state changes
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent(
      "selectable-box",
      registerSelectableBox
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.checked !== void 0 ? { checked: this.checked } : {},
            ...this.clickable !== void 0 ? { clickable: this.clickable } : {},
            ...this.clickableBox !== void 0 ? { "clickable-box": this.clickableBox } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.controlPlacement !== void 0 ? { "control-placement": this.controlPlacement } : {},
            ...this.controlType !== void 0 ? { "control-type": this.controlType } : {},
            ...this.tight !== void 0 ? { tight: this.tight } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.checked !== void 0 && this.checked !== false ? { "^checked": this.checked } : {},
          ...this.clickable !== void 0 && this.clickable !== false ? { "^clickable": this.clickable } : {},
          ...this.clickableBox !== void 0 && this.clickableBox !== false ? { "^clickable-box": this.clickableBox } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.controlPlacement !== void 0 ? { "^control-placement": this.controlPlacement } : {},
          ...this.controlType !== void 0 ? { "^control-type": this.controlType } : {},
          ...this.tight !== void 0 && this.tight !== false ? { "^tight": this.tight } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VSideDrawer = defineComponent({
  name: "VSideDrawer",
  props: {
    /**
     * applies scheme alternate region
     */
    alternate: { type: Boolean, default: void 0 },
    /**
     * sets the side drawer's type to modal
     */
    modal: { type: Boolean, default: void 0 },
    /**
     * indicates whether the side drawer is open
     */
    open: { type: Boolean, default: void 0 },
    /**
     * sets the side of the side drawer
     */
    trailing: { type: Boolean, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fired when the user requests to close the side-drawer. You can prevent the side drawer from closing by calling `.preventDefault()` on the event.
     * @type {CustomEvent<undefined>}
     */
    "cancel",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fired when the side drawer is closed.
     * @type {CustomEvent<undefined>}
     */
    "close",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fired when the side drawer is opened.
     * @type {CustomEvent<undefined>}
     */
    "open"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("side-drawer", registerSideDrawer);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.alternate !== void 0 ? { alternate: this.alternate } : {},
            ...this.modal !== void 0 ? { modal: this.modal } : {},
            ...this.open !== void 0 ? { open: this.open } : {},
            ...this.trailing !== void 0 ? { trailing: this.trailing } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            cancel: (event) => {
              this.$emit("cancel", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            close: (event) => {
              this.$emit("close", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            open: (event) => {
              this.$emit("open", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot app-content Sets assigned nodes to the main application content, the side drawer is opened next to.
          handleNamedSlotV2("app-content", this.$slots["app-content"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.alternate !== void 0 && this.alternate !== false ? { "^alternate": this.alternate } : {},
          ...this.modal !== void 0 && this.modal !== false ? { "^modal": this.modal } : {},
          ...this.open !== void 0 && this.open !== false ? { "^open": this.open } : {},
          ...this.trailing !== void 0 && this.trailing !== false ? { "^trailing": this.trailing } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onCancel: (event) => {
          this.$emit("cancel", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onClose: (event) => {
          this.$emit("close", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onOpen: (event) => {
          this.$emit("open", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot app-content Sets assigned nodes to the main application content, the side drawer is opened next to.
        handleNamedSlotV3("app-content", this.$slots["app-content"])
      ]
    );
  }
});

const VSlider = defineComponent({
  name: "VSlider",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * slider connotation
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * Display markers on/off
     */
    markers: { type: Boolean, default: void 0 },
    /**
     * The maximum allowed value.
     */
    max: { type: Number, default: void 0 },
    /**
     * The minimum allowed value.
     */
    min: { type: Number, default: void 0 },
    /**
     * The selection mode.
     */
    mode: { type: String, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * The orientation of the slider.
     */
    orientation: {
      type: String,
      default: void 0
    },
    /**
     * Show current value on the thumb.
     */
    pin: { type: Boolean, default: void 0 },
    /**
     * When true, the control will be immutable by user interaction. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly | readonly HTML attribute for more information.
     */
    readonly: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * Value to increment or decrement via arrow keys, mouse click or drag.
     */
    step: { type: Number, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    modelValue: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires a custom 'change' event when the slider value changes
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Decrement the value by the step
     */
    decrement() {
      return this.element?.decrement();
    },
    /**
     * Increment the value by the step
     */
    increment() {
      return this.element?.increment();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    },
    /**
     * Places the thumb based on the current value
     */
    setThumbPositionForOrientation(_, direction) {
      return this.element?.setThumbPositionForOrientation(
        _,
        direction
      );
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("slider", registerSlider);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.markers !== void 0 ? { markers: this.markers } : {},
            ...this.max !== void 0 ? { max: this.max } : {},
            ...this.min !== void 0 ? { min: this.min } : {},
            ...this.mode !== void 0 ? { mode: this.mode } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.orientation !== void 0 ? { orientation: this.orientation } : {},
            ...this.pin !== void 0 ? { pin: this.pin } : {},
            ...this.readonly !== void 0 ? { readonly: this.readonly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.step !== void 0 ? { step: this.step } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { "current-value": this.modelValue ?? this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.markers !== void 0 && this.markers !== false ? { "^markers": this.markers } : {},
          ...this.max !== void 0 ? { "^max": this.max } : {},
          ...this.min !== void 0 ? { "^min": this.min } : {},
          ...this.mode !== void 0 ? { "^mode": this.mode } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.orientation !== void 0 ? { "^orientation": this.orientation } : {},
          ...this.pin !== void 0 && this.pin !== false ? { "^pin": this.pin } : {},
          ...this.readonly !== void 0 && this.readonly !== false ? { "^readonly": this.readonly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.step !== void 0 ? { "^step": this.step } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^current-value": this.modelValue ?? this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VSplitButton = defineComponent({
  name: "VSplitButton",
  props: {
    /**
     * The appearance the split button should have.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * The connotation the split button should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * Indicates if the button is disabled.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * Defines a string value that labels the indicator element.
     *
     * https://www.w3.org/TR/wai-aria-1.1/#aria-label
     */
    indicatorAriaLabel: {
      type: String,
      default: void 0
    },
    /**
     * Indicates the split button's label.
     */
    label: { type: String, default: void 0 },
    /**
     * The shape the split button should have.
     */
    shape: { type: String, default: void 0 },
    /**
     * The size the split button should have.
     */
    size: {
      type: String,
      default: void 0
    },
    /**
     * Indicates the split button's icon indicator.
     */
    splitIndicator: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Event emitted when the action button is clicked
     * @type {CustomEvent<undefined>}
     */
    "action-click",
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Event emitted when the indicator button is clicked
     * @type {CustomEvent<undefined>}
     */
    "indicator-click",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent(
      "split-button",
      registerSplitButton
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.indicatorAriaLabel !== void 0 ? { "indicator-aria-label": this.indicatorAriaLabel } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.size !== void 0 ? { size: this.size } : {},
            ...this.splitIndicator !== void 0 ? { "split-indicator": this.splitIndicator } : {}
          },
          class: "vvd-component",
          on: {
            "action-click": (event) => {
              this.$emit("action-click", event);
            },
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            "indicator-click": (event) => {
              this.$emit("indicator-click", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.indicatorAriaLabel !== void 0 ? { "^indicator-aria-label": this.indicatorAriaLabel } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {},
          ...this.splitIndicator !== void 0 ? { "^split-indicator": this.splitIndicator } : {}
        }),
        onActionClick: (event) => {
          this.$emit("action-click", event);
        },
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onIndicatorClick: (event) => {
          this.$emit("indicator-click", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"])
      ]
    );
  }
});

const VSwitch = defineComponent({
  name: "VSwitch",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * The current checkedness of the element.
     */
    checked: { type: Boolean, default: void 0 },
    /**
     * The current checkedness of the element.
     */
    modelValue: { type: Boolean, default: void 0 },
    /**
     * Indicates the switch's connotation.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * The default checkedness of the element. This value sets the `checked` property
     * only when the `checked` property has not been explicitly set.
     */
    defaultChecked: { type: Boolean, default: void 0 },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * Indicates the switch's label.
     */
    label: { type: String, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * When true, the control will be immutable by user interaction. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly | readonly HTML attribute for more information.
     */
    readonly: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Emits a custom change event when the checked state changes
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {boolean}
     */
    "update:modelValue"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("switch", registerSwitch);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...(this.modelValue ?? this.checked) !== void 0 ? { "current-checked": this.modelValue ?? this.checked } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.defaultChecked !== void 0 ? { checked: this.defaultChecked } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.readonly !== void 0 ? { readonly: this.readonly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.value !== void 0 ? { "current-value": this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("update:modelValue", event.currentTarget.checked);
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...(this.modelValue ?? this.checked) !== void 0 && (this.modelValue ?? this.checked) !== false ? { "^current-checked": this.modelValue ?? this.checked } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.defaultChecked !== void 0 && this.defaultChecked !== false ? { "^checked": this.defaultChecked } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.readonly !== void 0 && this.readonly !== false ? { "^readonly": this.readonly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.value !== void 0 ? { "^current-value": this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("update:modelValue", event.currentTarget.checked);
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VTab = defineComponent({
  name: "VTab",
  props: {
    /**
     * The connotation the tab should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * When true, the control will be immutable by user interaction. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled | disabled HTML attribute for more information.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * Indicates the icon affix alignment.
     */
    iconTrailing: { type: Boolean, default: void 0 },
    /**
     * Indicates the tab's label.
     */
    label: { type: String, default: void 0 },
    /**
     * Adds a close button
     */
    removable: { type: Boolean, default: void 0 },
    /**
     * The shape the tab should have.
     */
    shape: {
      type: String,
      default: void 0
    },
    tabIndex: { type: Number, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    _handleCloseClick(e) {
      return this.element?._handleCloseClick(e);
    },
    _onKeyDown(e) {
      return this.element?._onKeyDown(e);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("tab", registerTab);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.iconTrailing !== void 0 ? { "icon-trailing": this.iconTrailing } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.removable !== void 0 ? { removable: this.removable } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.tabIndex !== void 0 ? { tabIndex: this.tabIndex } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.iconTrailing !== void 0 && this.iconTrailing !== false ? { "^icon-trailing": this.iconTrailing } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.removable !== void 0 && this.removable !== false ? { "^removable": this.removable } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.tabIndex !== void 0 ? { "^tabIndex": this.tabIndex } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"])
      ]
    );
  }
});

const VTabPanel = defineComponent({
  name: "VTabPanel",
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("tab-panel", registerTabPanel);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {},
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({}),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VTabs = defineComponent({
  name: "VTabs",
  props: {
    /**
     * The id of the active tab
     */
    activeid: { type: String, default: void 0 },
    /**
     * Deprecated attribute. It has no effect.
     */
    activeindicator: { type: Boolean, default: void 0 },
    /**
     * The connotation the tabs should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * sets the initial preferred margin from predefined available options
     */
    gutters: { type: String, default: void 0 },
    /**
     * The orientation
     */
    orientation: {
      type: String,
      default: void 0
    },
    /**
     * sets overflow to the tab-panel
     */
    scrollablePanel: { type: Boolean, default: void 0 },
    /**
     * Controls the layout of the tabs.
     */
    tabsLayout: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires a custom 'change' event when a tab is clicked or during keyboard navigation
     * @type {CustomEvent<HTMLElement>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    /**
     * Adjusts the active index by numerical increments.
     * Only enabled tabs are considered.
     */
    adjust(adjustment) {
      return this.element?.adjust(adjustment);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("tabs", registerTabs);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.activeid !== void 0 ? { activeid: this.activeid } : {},
            ...this.activeindicator !== void 0 ? { activeindicator: this.activeindicator } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.gutters !== void 0 ? { gutters: this.gutters } : {},
            ...this.orientation !== void 0 ? { orientation: this.orientation } : {},
            ...this.scrollablePanel !== void 0 ? { "scrollable-panel": this.scrollablePanel } : {},
            ...this.tabsLayout !== void 0 ? { "tabs-layout": this.tabsLayout } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.activeid !== void 0 ? { "^activeid": this.activeid } : {},
          ...this.activeindicator !== void 0 && this.activeindicator !== false ? { "^activeindicator": this.activeindicator } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.gutters !== void 0 ? { "^gutters": this.gutters } : {},
          ...this.orientation !== void 0 ? { "^orientation": this.orientation } : {},
          ...this.scrollablePanel !== void 0 && this.scrollablePanel !== false ? { "^scrollable-panel": this.scrollablePanel } : {},
          ...this.tabsLayout !== void 0 ? { "^tabs-layout": this.tabsLayout } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VTag = defineComponent({
  name: "VTag",
  props: {
    /**
     * The appearance the tag should have.
     */
    appearance: {
      type: String,
      default: void 0
    },
    /**
     * The connotation the tag should have.
     */
    connotation: {
      type: String,
      default: void 0
    },
    /**
     * indicates whether the tag is disabled
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * Indicates the tag's label.
     */
    label: { type: String, default: void 0 },
    /**
     * indicates whether the tag is removable
     */
    removable: { type: Boolean, default: void 0 },
    /**
     * indicates whether the tag is selectable
     */
    selectable: { type: Boolean, default: void 0 },
    /**
     * indicates whether the tag is selected
     */
    selected: { type: Boolean, default: void 0 },
    /**
     * The shape the tag should have.
     */
    shape: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the tag is removed
     * @type {CustomEvent<undefined>}
     */
    "removed",
    /**
     * Fires when the selected state changes
     * @type {CustomEvent<undefined>}
     */
    "selected-change"
  ],
  methods: {
    handleClick() {
      return this.element?.handleClick();
    },
    handleKeydown(e) {
      return this.element?.handleKeydown(e);
    },
    remove() {
      return this.element?.remove();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("tag", registerTag);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.connotation !== void 0 ? { connotation: this.connotation } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.removable !== void 0 ? { removable: this.removable } : {},
            ...this.selectable !== void 0 ? { selectable: this.selectable } : {},
            ...this.selected !== void 0 ? { selected: this.selected } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            removed: (event) => {
              this.$emit("removed", event);
            },
            "selected-change": (event) => {
              this.$emit("selected-change", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.connotation !== void 0 ? { "^connotation": this.connotation } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.removable !== void 0 && this.removable !== false ? { "^removable": this.removable } : {},
          ...this.selectable !== void 0 && this.selectable !== false ? { "^selectable": this.selectable } : {},
          ...this.selected !== void 0 && this.selected !== false ? { "^selected": this.selected } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onRemoved: (event) => {
          this.$emit("removed", event);
        },
        onSelectedChange: (event) => {
          this.$emit("selected-change", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"])
      ]
    );
  }
});

const VTagGroup = defineComponent({
  name: "VTagGroup",
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("tag-group", registerTagGroup);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {},
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({}),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VTextArea = defineComponent({
  name: "VTextArea",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * Indicates that this element should get focus after the page finishes loading.
     */
    autofocus: { type: Boolean, default: void 0 },
    /**
     * Use in combination with `maxlength` to display a character count.
     */
    charCount: { type: Boolean, default: void 0 },
    /**
     * Sizes the element horizontally by a number of character columns.
     */
    cols: { type: Number, default: void 0 },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * The https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id | id of the https://developer.mozilla.org/en-US/docs/Web/HTML/Element/form | form the element is associated to
     */
    form: { type: String, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * Allows associating a https://developer.mozilla.org/en-US/docs/Web/HTML/Element/datalist | datalist to the element by https://developer.mozilla.org/en-US/docs/Web/API/Element/id.
     */
    list: { type: String, default: void 0 },
    /**
     * The maximum number of characters a user can enter.
     */
    maxlength: { type: Number, default: void 0 },
    /**
     * The minimum number of characters a user can enter.
     */
    minlength: { type: Number, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * Sets the placeholder value of the element, generally used to provide a hint to the user.
     */
    placeholder: { type: String, default: void 0 },
    /**
     * When true, the control will be immutable by user interaction. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly | readonly HTML attribute for more information.
     */
    readOnly: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The resize mode of the element.
     */
    resize: {
      type: String,
      default: void 0
    },
    /**
     * Sizes the element vertically by a number of character rows.
     */
    rows: { type: Number, default: void 0 },
    /**
     * Sets if the element is eligible for spell checking
     * but the UA.
     */
    spellcheck: { type: Boolean, default: void 0 },
    /**
     * Provides a custom success message. Any current error state will be overridden.
     */
    successText: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    modelValue: { type: String, default: void 0 },
    /**
     * The wrap attribute
     */
    wrap: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Emits a custom 'change' event when the textarea emits a change event
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    },
    /**
     * Selects all the text in the text area
     */
    select() {
      return this.element?.select();
    },
    /**
     * {@inheritDoc (FormAssociated:interface).validate}
     */
    validate() {
      return this.element?.validate();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("text-area", registerTextArea);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.autofocus !== void 0 ? { autofocus: this.autofocus } : {},
            ...this.charCount !== void 0 ? { "char-count": this.charCount } : {},
            ...this.cols !== void 0 ? { cols: this.cols } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.form !== void 0 ? { form: this.form } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.list !== void 0 ? { list: this.list } : {},
            ...this.maxlength !== void 0 ? { maxlength: this.maxlength } : {},
            ...this.minlength !== void 0 ? { minlength: this.minlength } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.placeholder !== void 0 ? { placeholder: this.placeholder } : {},
            ...this.readOnly !== void 0 ? { readOnly: this.readOnly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.resize !== void 0 ? { resize: this.resize } : {},
            ...this.rows !== void 0 ? { rows: this.rows } : {},
            ...this.spellcheck !== void 0 ? { spellcheck: this.spellcheck } : {},
            ...this.successText !== void 0 ? { "success-text": this.successText } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { "current-value": this.modelValue ?? this.value } : {},
            ...this.wrap !== void 0 ? { wrap: this.wrap } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the text-area. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.autofocus !== void 0 && this.autofocus !== false ? { "^autofocus": this.autofocus } : {},
          ...this.charCount !== void 0 && this.charCount !== false ? { "^char-count": this.charCount } : {},
          ...this.cols !== void 0 ? { "^cols": this.cols } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.form !== void 0 ? { "^form": this.form } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.list !== void 0 ? { "^list": this.list } : {},
          ...this.maxlength !== void 0 ? { "^maxlength": this.maxlength } : {},
          ...this.minlength !== void 0 ? { "^minlength": this.minlength } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.placeholder !== void 0 ? { "^placeholder": this.placeholder } : {},
          ...this.readOnly !== void 0 && this.readOnly !== false ? { "^readOnly": this.readOnly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.resize !== void 0 ? { "^resize": this.resize } : {},
          ...this.rows !== void 0 ? { "^rows": this.rows } : {},
          ...this.spellcheck !== void 0 && this.spellcheck !== false ? { "^spellcheck": this.spellcheck } : {},
          ...this.successText !== void 0 ? { "^success-text": this.successText } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^current-value": this.modelValue ?? this.value } : {},
          ...this.wrap !== void 0 ? { "^wrap": this.wrap } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the text-area. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"])
      ]
    );
  }
});

const VTextField = defineComponent({
  name: "VTextField",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    appearance: {
      type: String,
      default: void 0
    },
    autoComplete: { type: String, default: void 0 },
    /**
     * Indicates that this element should get focus after the page finishes loading. See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#htmlattrdefautofocus | autofocus HTML attribute for more information.
     */
    autofocus: { type: Boolean, default: void 0 },
    /**
     * Use in combination with `maxlength` to display a character count.
     */
    charCount: { type: Boolean, default: void 0 },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * Hints at the type of data that might be entered by the user while editing the element or its contents.
     * This allows a browser to display an appropriate virtual keyboard.
     */
    inputmode: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * Allows associating a https://developer.mozilla.org/en-US/docs/Web/HTML/Element/datalist | datalist to the element by https://developer.mozilla.org/en-US/docs/Web/API/Element/id.
     */
    list: { type: String, default: void 0 },
    /**
     * The maximum number of characters a user can enter.
     */
    maxlength: { type: Number, default: void 0 },
    /**
     * The minimum number of characters a user can enter.
     */
    minlength: { type: Number, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * A regular expression that the value must match to pass validation.
     */
    pattern: { type: String, default: void 0 },
    /**
     * Sets the placeholder value of the element, generally used to provide a hint to the user.
     */
    placeholder: { type: String, default: void 0 },
    /**
     * When true, the control will be immutable by user interaction. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly | readonly HTML attribute for more information.
     */
    readonly: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * The size the text-field should have.
     */
    scale: {
      type: String,
      default: void 0
    },
    shape: { type: String, default: void 0 },
    /**
     * Sets the width of the element to a specified number of characters.
     */
    size: { type: Number, default: void 0 },
    /**
     * Controls whether or not to enable spell checking for the input field, or if the default spell checking configuration should be used.
     */
    spellcheck: { type: Boolean, default: void 0 },
    /**
     * Provides a custom success message. Any current error state will be overridden.
     */
    successText: { type: String, default: void 0 },
    /**
     * Allows setting a type or mode of text.
     */
    type: {
      type: String,
      default: void 0
    },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    modelValue: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires a custom 'change' event when the value has changed
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    focus() {
      return this.element?.focus();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    },
    /**
     * Selects all the text in the text field
     */
    select() {
      return this.element?.select();
    },
    /**
     * {@inheritDoc (FormAssociated:interface).validate}
     */
    validate() {
      return this.element?.validate();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("text-field", registerTextField);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.appearance !== void 0 ? { appearance: this.appearance } : {},
            ...this.autoComplete !== void 0 ? { autoComplete: this.autoComplete } : {},
            ...this.autofocus !== void 0 ? { autofocus: this.autofocus } : {},
            ...this.charCount !== void 0 ? { "char-count": this.charCount } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.inputmode !== void 0 ? { inputmode: this.inputmode } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.list !== void 0 ? { list: this.list } : {},
            ...this.maxlength !== void 0 ? { maxlength: this.maxlength } : {},
            ...this.minlength !== void 0 ? { minlength: this.minlength } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.pattern !== void 0 ? { pattern: this.pattern } : {},
            ...this.placeholder !== void 0 ? { placeholder: this.placeholder } : {},
            ...this.readonly !== void 0 ? { readonly: this.readonly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.scale !== void 0 ? { scale: this.scale } : {},
            ...this.shape !== void 0 ? { shape: this.shape } : {},
            ...this.size !== void 0 ? { size: this.size } : {},
            ...this.spellcheck !== void 0 ? { spellcheck: this.spellcheck } : {},
            ...this.successText !== void 0 ? { "success-text": this.successText } : {},
            ...this.type !== void 0 ? { type: this.type } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { "current-value": this.modelValue ?? this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot action-items Used to add action items to the end of the text-field.
          handleNamedSlotV2("action-items", this.$slots["action-items"]),
          // @slot helper-text Describes how to use the text-field. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"]),
          // @slot leading-action-items Used to add action items to the start of the text-field.
          handleNamedSlotV2(
            "leading-action-items",
            this.$slots["leading-action-items"]
          )
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.appearance !== void 0 ? { "^appearance": this.appearance } : {},
          ...this.autoComplete !== void 0 ? { "^autoComplete": this.autoComplete } : {},
          ...this.autofocus !== void 0 && this.autofocus !== false ? { "^autofocus": this.autofocus } : {},
          ...this.charCount !== void 0 && this.charCount !== false ? { "^char-count": this.charCount } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.inputmode !== void 0 ? { "^inputmode": this.inputmode } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.list !== void 0 ? { "^list": this.list } : {},
          ...this.maxlength !== void 0 ? { "^maxlength": this.maxlength } : {},
          ...this.minlength !== void 0 ? { "^minlength": this.minlength } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.pattern !== void 0 ? { "^pattern": this.pattern } : {},
          ...this.placeholder !== void 0 ? { "^placeholder": this.placeholder } : {},
          ...this.readonly !== void 0 && this.readonly !== false ? { "^readonly": this.readonly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.scale !== void 0 ? { "^scale": this.scale } : {},
          ...this.shape !== void 0 ? { "^shape": this.shape } : {},
          ...this.size !== void 0 ? { "^size": this.size } : {},
          ...this.spellcheck !== void 0 && this.spellcheck !== false ? { "^spellcheck": this.spellcheck } : {},
          ...this.successText !== void 0 ? { "^success-text": this.successText } : {},
          ...this.type !== void 0 ? { "^type": this.type } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^current-value": this.modelValue ?? this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot action-items Used to add action items to the end of the text-field.
        handleNamedSlotV3("action-items", this.$slots["action-items"]),
        // @slot helper-text Describes how to use the text-field. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"]),
        // @slot leading-action-items Used to add action items to the start of the text-field.
        handleNamedSlotV3(
          "leading-action-items",
          this.$slots["leading-action-items"]
        )
      ]
    );
  }
});

const VTimePicker = defineComponent({
  name: "VTimePicker",
  model: isVue2 ? {
    prop: "modelValue",
    event: "update:modelValue"
  } : void 0,
  props: {
    /**
     * Forces a 12h or 24h clock to be used.
     */
    clock: { type: String, default: void 0 },
    /**
     * Sets the element's disabled state. A disabled element will not be included during form submission.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * Provides a custom error message. Any current error state will be overridden.
     */
    errorText: { type: String, default: void 0 },
    /**
     * Provides additional information to help the user enter the correct information.
     * To add HTML to the helper text, use the helper-text slot instead.
     */
    helperText: { type: String, default: void 0 },
    /**
     * The default value of the element. This value sets the `value` property
     * only when the `value` property has not been explicitly set.
     */
    initialValue: { type: String, default: void 0 },
    /**
     * The label for the form element.
     */
    label: { type: String, default: void 0 },
    /**
     * The latest accepted time of the time-picker.
     */
    max: { type: String, default: void 0 },
    /**
     * The earliest accepted time of the time-picker.
     */
    min: { type: String, default: void 0 },
    /**
     * Distance between presented minute options.
     */
    minutesStep: { type: Number, default: void 0 },
    /**
     * The name of the element. This element's value will be surfaced during form submission under the provided name.
     */
    name: { type: String, default: void 0 },
    /**
     * Whether the date-picker is readonly.
     */
    readonly: { type: Boolean, default: void 0 },
    /**
     * Require the field to be completed prior to form submission.
     */
    required: { type: Boolean, default: void 0 },
    /**
     * Distance between presented seconds options. If null, seconds are not presented.
     */
    secondsStep: { type: Number, default: void 0 },
    /**
     * The current value of the element.
     */
    value: { type: String, default: void 0 },
    /**
     * The current value of the element.
     */
    modelValue: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Emitted when the time is changed by the user.
     * @type {CustomEvent<undefined>}
     */
    "change",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Emitted when the time is changed by the user.
     * @type {CustomEvent<undefined>}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires when the modelValue value changes
     * @type {string}
     */
    "update:modelValue"
  ],
  methods: {
    /**
     * Return the current validity of the element.
     */
    checkValidity() {
      return this.element?.checkValidity();
    },
    /**
     * Return the current validity of the element.
     * If false, fires an invalid event at the element.
     */
    reportValidity() {
      return this.element?.reportValidity();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("time-picker", registerTimePicker);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.clock !== void 0 ? { clock: this.clock } : {},
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.errorText !== void 0 ? { "error-text": this.errorText } : {},
            ...this.helperText !== void 0 ? { "helper-text": this.helperText } : {},
            ...this.initialValue !== void 0 ? { value: this.initialValue } : {},
            ...this.label !== void 0 ? { label: this.label } : {},
            ...this.max !== void 0 ? { max: this.max } : {},
            ...this.min !== void 0 ? { min: this.min } : {},
            ...this.minutesStep !== void 0 ? { "minutes-step": this.minutesStep } : {},
            ...this.name !== void 0 ? { name: this.name } : {},
            ...this.readonly !== void 0 ? { readonly: this.readonly } : {},
            ...this.required !== void 0 ? { required: this.required } : {},
            ...this.secondsStep !== void 0 ? { "seconds-step": this.secondsStep } : {},
            ...(this.modelValue ?? this.value) !== void 0 ? { "current-value": this.modelValue ?? this.value } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            change: (event) => {
              this.$emit("change", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("update:modelValue", event.currentTarget.value);
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot helper-text Describes how to use the time-picker. Alternative to the `helper-text` attribute.
          handleNamedSlotV2("helper-text", this.$slots["helper-text"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.clock !== void 0 ? { "^clock": this.clock } : {},
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.errorText !== void 0 ? { "^error-text": this.errorText } : {},
          ...this.helperText !== void 0 ? { "^helper-text": this.helperText } : {},
          ...this.initialValue !== void 0 ? { "^value": this.initialValue } : {},
          ...this.label !== void 0 ? { "^label": this.label } : {},
          ...this.max !== void 0 ? { "^max": this.max } : {},
          ...this.min !== void 0 ? { "^min": this.min } : {},
          ...this.minutesStep !== void 0 ? { "^minutes-step": this.minutesStep } : {},
          ...this.name !== void 0 ? { "^name": this.name } : {},
          ...this.readonly !== void 0 && this.readonly !== false ? { "^readonly": this.readonly } : {},
          ...this.required !== void 0 && this.required !== false ? { "^required": this.required } : {},
          ...this.secondsStep !== void 0 ? { "^seconds-step": this.secondsStep } : {},
          ...(this.modelValue ?? this.value) !== void 0 ? { "^current-value": this.modelValue ?? this.value } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onChange: (event) => {
          this.$emit("change", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("update:modelValue", event.currentTarget.value);
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot helper-text Describes how to use the time-picker. Alternative to the `helper-text` attribute.
        handleNamedSlotV3("helper-text", this.$slots["helper-text"])
      ]
    );
  }
});

const VToggletip = defineComponent({
  name: "VToggletip",
  props: {
    /**
     * toggle color scheme
     */
    alternate: { type: Boolean, default: void 0 },
    /**
     * ID or direct reference to the component's anchor element.
     */
    anchor: {
      type: [String, HTMLElement],
      default: void 0
    },
    /**
     * the optional title of the toggletip
     */
    headline: { type: String, default: void 0 },
    /**
     * indicates whether the toggletip is open
     */
    open: { type: Boolean, default: void 0 },
    /**
     * placement of the toggletip
     */
    placement: {
      type: String,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    openChanged(oldValue, newValue) {
      return this.element?.openChanged(oldValue, newValue);
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("toggletip", registerToggletip);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.alternate !== void 0 ? { alternate: this.alternate } : {},
            ...this.headline !== void 0 ? { headline: this.headline } : {},
            ...this.open !== void 0 ? { open: this.open } : {},
            ...this.placement !== void 0 ? { placement: this.placement } : {}
          },
          class: "vvd-component",
          domProps: {
            ...this.anchor !== void 0 ? { anchor: this.anchor } : {}
          },
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot action-items The content to display in the toggletip action items.
          handleNamedSlotV2("action-items", this.$slots["action-items"]),
          // @slot anchor Used to set the anchor element for the toggletip.
          handleNamedSlotV2("anchor", this.$slots["anchor"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.alternate !== void 0 && this.alternate !== false ? { "^alternate": this.alternate } : {},
          ...this.anchor !== void 0 ? { ".anchor": this.anchor } : {},
          ...this.headline !== void 0 ? { "^headline": this.headline } : {},
          ...this.open !== void 0 && this.open !== false ? { "^open": this.open } : {},
          ...this.placement !== void 0 ? { "^placement": this.placement } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot action-items The content to display in the toggletip action items.
        handleNamedSlotV3("action-items", this.$slots["action-items"]),
        // @slot anchor Used to set the anchor element for the toggletip.
        handleNamedSlotV3("anchor", this.$slots["anchor"])
      ]
    );
  }
});

const VTooltip = defineComponent({
  name: "VTooltip",
  props: {
    /**
     * ID or direct reference to the component's anchor element.
     */
    anchor: {
      type: [String, HTMLElement],
      default: void 0
    },
    open: { type: Boolean, default: void 0 },
    placement: {
      type: String,
      default: void 0
    },
    /**
     * the text of the tooltip
     * accepts string
     */
    text: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent("tooltip", registerTooltip);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.open !== void 0 ? { open: this.open } : {},
            ...this.placement !== void 0 ? { placement: this.placement } : {},
            ...this.text !== void 0 ? { text: this.text } : {}
          },
          class: "vvd-component",
          domProps: {
            ...this.anchor !== void 0 ? { anchor: this.anchor } : {}
          },
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot anchor Used to set the anchor element for the tooltip.
          handleNamedSlotV2("anchor", this.$slots["anchor"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.anchor !== void 0 ? { ".anchor": this.anchor } : {},
          ...this.open !== void 0 && this.open !== false ? { "^open": this.open } : {},
          ...this.placement !== void 0 ? { "^placement": this.placement } : {},
          ...this.text !== void 0 ? { "^text": this.text } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot anchor Used to set the anchor element for the tooltip.
        handleNamedSlotV3("anchor", this.$slots["anchor"])
      ]
    );
  }
});

const VTreeItem = defineComponent({
  name: "VTreeItem",
  props: {
    /**
     * When true, the control will be immutable by user interaction. See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled | disabled HTML attribute for more information.
     */
    disabled: { type: Boolean, default: void 0 },
    /**
     * When true, the control will be appear expanded by user interaction.
     */
    expanded: { type: Boolean, default: void 0 },
    /**
     * A decorative icon the custom element should have.
     * See the Vivid Icon Gallery for available icons: https://icons.vivid.vonage.com/
     */
    icon: { type: String, default: void 0 },
    /**
     * When true, the control will appear selected by user interaction.
     */
    selected: { type: Boolean, default: void 0 },
    /**
     * Indicates the text's text.
     */
    text: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires a custom 'expanded-change' event when the expanded state changes
     * @type {CustomEvent<HTMLElement>}
     */
    "expanded-change",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fires a custom 'selected-change' event when the selected state changes
     * @type {CustomEvent<HTMLElement>}
     */
    "selected-change"
  ],
  methods: {
    expandedChanged() {
      return this.element?.expandedChanged();
    },
    itemsChanged() {
      return this.element?.itemsChanged();
    },
    selectedChanged() {
      return this.element?.selectedChanged();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("tree-item", registerTreeItem);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.disabled !== void 0 ? { disabled: this.disabled } : {},
            ...this.expanded !== void 0 ? { expanded: this.expanded } : {},
            ...this.icon !== void 0 ? { icon: this.icon } : {},
            ...this.selected !== void 0 ? { selected: this.selected } : {},
            ...this.text !== void 0 ? { text: this.text } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            "expanded-change": (event) => {
              this.$emit("expanded-change", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            "selected-change": (event) => {
              this.$emit("selected-change", event);
            }
          }
        },
        [
          this.$slots.default,
          // @slot icon The preferred way to add an icon to the component.
          handleNamedSlotV2("icon", this.$slots["icon"]),
          // @slot item To specify a child tree item.
          handleNamedSlotV2("item", this.$slots["item"])
        ]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.disabled !== void 0 && this.disabled !== false ? { "^disabled": this.disabled } : {},
          ...this.expanded !== void 0 && this.expanded !== false ? { "^expanded": this.expanded } : {},
          ...this.icon !== void 0 ? { "^icon": this.icon } : {},
          ...this.selected !== void 0 && this.selected !== false ? { "^selected": this.selected } : {},
          ...this.text !== void 0 ? { "^text": this.text } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onExpandedChange: (event) => {
          this.$emit("expanded-change", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onSelectedChange: (event) => {
          this.$emit("selected-change", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default(),
        // @slot icon The preferred way to add an icon to the component.
        handleNamedSlotV3("icon", this.$slots["icon"]),
        // @slot item To specify a child tree item.
        handleNamedSlotV3("item", this.$slots["item"])
      ]
    );
  }
});

const VTreeView = defineComponent({
  name: "VTreeView",
  props: {
    /**
     * /**
     *  When true, the control will be appear expanded by user interaction.
     */
    renderCollapsedNodes: {
      type: Boolean,
      default: void 0
    }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup"
  ],
  methods: {
    slottedTreeItemsChanged() {
      return this.element?.slottedTreeItemsChanged();
    }
  },
  setup(props, ctx) {
    const componentName = registerComponent("tree-view", registerTreeView);
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.renderCollapsedNodes !== void 0 ? { "render-collapsed-nodes": this.renderCollapsedNodes } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.renderCollapsedNodes !== void 0 && this.renderCollapsedNodes !== false ? { "^render-collapsed-nodes": this.renderCollapsedNodes } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

const VVideoPlayer = defineComponent({
  name: "VVideoPlayer",
  props: {
    /**
     * Allows the video will play automatically (muted)
     */
    autoplay: { type: Boolean, default: void 0 },
    /**
     * Allows the video to loop back to the beginning when finished
     */
    loop: { type: Boolean, default: void 0 },
    /**
     * Sets the available playback rates. When an empty string, no choices will be available
     */
    playbackRates: { type: String, default: void 0 },
    /**
     * Reference to an image which is displayed before the video is played
     */
    poster: { type: String, default: void 0 },
    /**
     * Allows the video to loop back to the beginning when finished
     */
    skipBy: {
      type: String,
      default: void 0
    },
    /**
     * URL of a video file
     */
    src: { type: String, default: void 0 }
  },
  emits: [
    /**
     * Fires when the element loses focus.
     * @type {FocusEvent}
     */
    "blur",
    /**
     * Fires when a pointing device button (such as a mouse's primary mouse button) is both pressed and released while the pointer is located inside the element.
     * @type {MouseEvent}
     */
    "click",
    /**
     * Fired when the video is ended
     * @type {CustomEvent<undefined>}
     */
    "ended",
    /**
     * Fires when the element receives focus.
     * @type {FocusEvent}
     */
    "focus",
    /**
     * Fires when the value of an element has been changed.
     * @type {Event}
     */
    "input",
    /**
     * Fires when a key is pressed.
     * @type {KeyboardEvent}
     */
    "keydown",
    /**
     * Fires when a key is released.
     * @type {KeyboardEvent}
     */
    "keyup",
    /**
     * Fired when the video is paused
     * @type {CustomEvent<undefined>}
     */
    "pause",
    /**
     * Fired when the video is played
     * @type {CustomEvent<undefined>}
     */
    "play"
  ],
  methods: {},
  setup(props, ctx) {
    const componentName = registerComponent(
      "video-player",
      registerVideoPlayer
    );
    const element = ref(null);
    return { componentName, element };
  },
  render() {
    if (isVue2) {
      return h(
        this.componentName,
        {
          ref: "element",
          attrs: {
            ...this.autoplay !== void 0 ? { autoplay: this.autoplay } : {},
            ...this.loop !== void 0 ? { loop: this.loop } : {},
            ...this.playbackRates !== void 0 ? { "playback-rates": this.playbackRates } : {},
            ...this.poster !== void 0 ? { poster: this.poster } : {},
            ...this.skipBy !== void 0 ? { "skip-by": this.skipBy } : {},
            ...this.src !== void 0 ? { src: this.src } : {}
          },
          class: "vvd-component",
          on: {
            blur: (event) => {
              this.$emit("blur", event);
            },
            click: (event) => {
              this.$emit("click", event);
            },
            ended: (event) => {
              this.$emit("ended", event);
            },
            focus: (event) => {
              this.$emit("focus", event);
            },
            input: (event) => {
              this.$emit("input", event);
            },
            keydown: (event) => {
              this.$emit("keydown", event);
            },
            keyup: (event) => {
              this.$emit("keyup", event);
            },
            pause: (event) => {
              this.$emit("pause", event);
            },
            play: (event) => {
              this.$emit("play", event);
            }
          }
        },
        [this.$slots.default]
      );
    }
    return h(
      this.componentName,
      {
        ref: "element",
        class: "vvd-component",
        ...handleVue3Props({
          ...this.autoplay !== void 0 && this.autoplay !== false ? { "^autoplay": this.autoplay } : {},
          ...this.loop !== void 0 && this.loop !== false ? { "^loop": this.loop } : {},
          ...this.playbackRates !== void 0 ? { "^playback-rates": this.playbackRates } : {},
          ...this.poster !== void 0 ? { "^poster": this.poster } : {},
          ...this.skipBy !== void 0 ? { "^skip-by": this.skipBy } : {},
          ...this.src !== void 0 ? { "^src": this.src } : {}
        }),
        onBlur: (event) => {
          this.$emit("blur", event);
        },
        onClick: (event) => {
          this.$emit("click", event);
        },
        onEnded: (event) => {
          this.$emit("ended", event);
        },
        onFocus: (event) => {
          this.$emit("focus", event);
        },
        onInput: (event) => {
          this.$emit("input", event);
        },
        onKeydown: (event) => {
          this.$emit("keydown", event);
        },
        onKeyup: (event) => {
          this.$emit("keyup", event);
        },
        onPause: (event) => {
          this.$emit("pause", event);
        },
        onPlay: (event) => {
          this.$emit("play", event);
        }
      },
      [
        // @ts-ignore
        this.$slots.default && this.$slots.default()
      ]
    );
  }
});

var Icon = /* @__PURE__ */ ((Icon2) => {
  Icon2["10SecBackwardLine"] = "10-sec-backward-line";
  Icon2["10SecBackwardSolid"] = "10-sec-backward-solid";
  Icon2["10SecForwardLine"] = "10-sec-forward-line";
  Icon2["10SecForwardSolid"] = "10-sec-forward-solid";
  Icon2["30SecBackwardLine"] = "30-sec-backward-line";
  Icon2["30SecBackwardSolid"] = "30-sec-backward-solid";
  Icon2["30SecForwardLine"] = "30-sec-forward-line";
  Icon2["30SecForwardSolid"] = "30-sec-forward-solid";
  Icon2["5SecBackwardLine"] = "5-sec-backward-line";
  Icon2["5SecBackwardSolid"] = "5-sec-backward-solid";
  Icon2["5SecForwardLine"] = "5-sec-forward-line";
  Icon2["5SecForwardSolid"] = "5-sec-forward-solid";
  Icon2["abcKeyboardLine"] = "abc-keyboard-line";
  Icon2["abcKeyboardSolid"] = "abc-keyboard-solid";
  Icon2["accessibilityLine"] = "accessibility-line";
  Icon2["accessibilitySolid"] = "accessibility-solid";
  Icon2["activeCallLine"] = "active-call-line";
  Icon2["activeCallSolid"] = "active-call-solid";
  Icon2["addCallLine"] = "add-call-line";
  Icon2["addCallSolid"] = "add-call-solid";
  Icon2["addGroupLine"] = "add-group-line";
  Icon2["addGroupSolid"] = "add-group-solid";
  Icon2["addLine"] = "add-line";
  Icon2["addSolid"] = "add-solid";
  Icon2["addTimeLine"] = "add-time-line";
  Icon2["addTimeSolid"] = "add-time-solid";
  Icon2["addUserLine"] = "add-user-line";
  Icon2["addUserSolid"] = "add-user-solid";
  Icon2["addressBookLine"] = "address-book-line";
  Icon2["addressBookSolid"] = "address-book-solid";
  Icon2["ai2Line"] = "ai-2-line";
  Icon2["ai2Solid"] = "ai-2-solid";
  Icon2["ai3Line"] = "ai-3-line";
  Icon2["ai3Solid"] = "ai-3-solid";
  Icon2["aiLine"] = "ai-line";
  Icon2["aiSolid"] = "ai-solid";
  Icon2["alarmLine"] = "alarm-line";
  Icon2["alarmSolid"] = "alarm-solid";
  Icon2["alignCenterLine"] = "align-center-line";
  Icon2["alignCenterSolid"] = "align-center-solid";
  Icon2["alignLeftLine"] = "align-left-line";
  Icon2["alignLeftSolid"] = "align-left-solid";
  Icon2["alignRightLine"] = "align-right-line";
  Icon2["alignRightSolid"] = "align-right-solid";
  Icon2["ambulanceLine"] = "ambulance-line";
  Icon2["ambulanceSolid"] = "ambulance-solid";
  Icon2["androidColor"] = "android-color";
  Icon2["androidMono"] = "android-mono";
  Icon2["angularColor"] = "angular-color";
  Icon2["angularMono"] = "angular-mono";
  Icon2["appLine"] = "app-line";
  Icon2["appPlusLine"] = "app-plus-line";
  Icon2["appPlusSolid"] = "app-plus-solid";
  Icon2["appSolid"] = "app-solid";
  Icon2["appStoreLine"] = "app-store-line";
  Icon2["appStoreSolid"] = "app-store-solid";
  Icon2["appleColor"] = "apple-color";
  Icon2["appleMono"] = "apple-mono";
  Icon2["appsLine"] = "apps-line";
  Icon2["appsSolid"] = "apps-solid";
  Icon2["arrowBoldLeftLine"] = "arrow-bold-left-line";
  Icon2["arrowBoldLeftSolid"] = "arrow-bold-left-solid";
  Icon2["arrowBoldRightLine"] = "arrow-bold-right-line";
  Icon2["arrowBoldRightSolid"] = "arrow-bold-right-solid";
  Icon2["arrowDownLine"] = "arrow-down-line";
  Icon2["arrowDownSolid"] = "arrow-down-solid";
  Icon2["arrowInboundLine"] = "arrow-inbound-line";
  Icon2["arrowInboundSolid"] = "arrow-inbound-solid";
  Icon2["arrowLeftLine"] = "arrow-left-line";
  Icon2["arrowLeftSolid"] = "arrow-left-solid";
  Icon2["arrowOutboundLine"] = "arrow-outbound-line";
  Icon2["arrowOutboundSolid"] = "arrow-outbound-solid";
  Icon2["arrowRightLine"] = "arrow-right-line";
  Icon2["arrowRightSolid"] = "arrow-right-solid";
  Icon2["arrowUpLine"] = "arrow-up-line";
  Icon2["arrowUpSolid"] = "arrow-up-solid";
  Icon2["arrowWidthLargeLine"] = "arrow-width-large-line";
  Icon2["arrowWidthLargeSolid"] = "arrow-width-large-solid";
  Icon2["arrowWidthMediumLine"] = "arrow-width-medium-line";
  Icon2["arrowWidthMediumSolid"] = "arrow-width-medium-solid";
  Icon2["arrowWidthSmallLine"] = "arrow-width-small-line";
  Icon2["arrowWidthSmallSolid"] = "arrow-width-small-solid";
  Icon2["assignUserLine"] = "assign-user-line";
  Icon2["assignUserSolid"] = "assign-user-solid";
  Icon2["asterisk2Line"] = "asterisk-2-line";
  Icon2["asterisk2Solid"] = "asterisk-2-solid";
  Icon2["asteriskLine"] = "asterisk-line";
  Icon2["asteriskSolid"] = "asterisk-solid";
  Icon2["atomsLine"] = "atoms-line";
  Icon2["atomsSolid"] = "atoms-solid";
  Icon2["attachmentLine"] = "attachment-line";
  Icon2["attachmentSolid"] = "attachment-solid";
  Icon2["audioMaxLine"] = "audio-max-line";
  Icon2["audioMaxSolid"] = "audio-max-solid";
  Icon2["audioMidLine"] = "audio-mid-line";
  Icon2["audioMidSolid"] = "audio-mid-solid";
  Icon2["audioMinLine"] = "audio-min-line";
  Icon2["audioMinSolid"] = "audio-min-solid";
  Icon2["audioMuteLine"] = "audio-mute-line";
  Icon2["audioMuteSolid"] = "audio-mute-solid";
  Icon2["audioOff2Line"] = "audio-off-2-line";
  Icon2["audioOff2Solid"] = "audio-off-2-solid";
  Icon2["audioOffLine"] = "audio-off-line";
  Icon2["audioOffSolid"] = "audio-off-solid";
  Icon2["backLine"] = "back-line";
  Icon2["backSolid"] = "back-solid";
  Icon2["backspaceLine"] = "backspace-line";
  Icon2["backspaceSolid"] = "backspace-solid";
  Icon2["backwardLine"] = "backward-line";
  Icon2["backwardSolid"] = "backward-solid";
  Icon2["badgeLine"] = "badge-line";
  Icon2["badgeSolid"] = "badge-solid";
  Icon2["binocularsLine"] = "binoculars-line";
  Icon2["binocularsSolid"] = "binoculars-solid";
  Icon2["blockCallLine"] = "block-call-line";
  Icon2["blockCallSolid"] = "block-call-solid";
  Icon2["blockLine"] = "block-line";
  Icon2["blockSolid"] = "block-solid";
  Icon2["bloggerColor"] = "blogger-color";
  Icon2["bloggerMono"] = "blogger-mono";
  Icon2["blurLine"] = "blur-line";
  Icon2["blurOffLine"] = "blur-off-line";
  Icon2["blurOffSolid"] = "blur-off-solid";
  Icon2["blurSolid"] = "blur-solid";
  Icon2["boldLine"] = "bold-line";
  Icon2["boldSolid"] = "bold-solid";
  Icon2["bookmarkLine"] = "bookmark-line";
  Icon2["bookmarkSolid"] = "bookmark-solid";
  Icon2["booksLine"] = "books-line";
  Icon2["booksSolid"] = "books-solid";
  Icon2["botLine"] = "bot-line";
  Icon2["botSolid"] = "bot-solid";
  Icon2["bottomTabsLine"] = "bottom-tabs-line";
  Icon2["bottomTabsSolid"] = "bottom-tabs-solid";
  Icon2["bounceLine"] = "bounce-line";
  Icon2["bounceSolid"] = "bounce-solid";
  Icon2["brainLine"] = "brain-line";
  Icon2["brainSolid"] = "brain-solid";
  Icon2["bringToFrontLine"] = "bring-to-front-line";
  Icon2["bringToFrontSolid"] = "bring-to-front-solid";
  Icon2["broadcastLine"] = "broadcast-line";
  Icon2["broadcastSolid"] = "broadcast-solid";
  Icon2["brokenImageLine"] = "broken-image-line";
  Icon2["brokenImageSolid"] = "broken-image-solid";
  Icon2["bulletLine"] = "bullet-line";
  Icon2["bulletList2Line"] = "bullet-list-2-line";
  Icon2["bulletList2Solid"] = "bullet-list-2-solid";
  Icon2["bulletListLine"] = "bullet-list-line";
  Icon2["bulletListOffLine"] = "bullet-list-off-line";
  Icon2["bulletListOffSolid"] = "bullet-list-off-solid";
  Icon2["bulletListSolid"] = "bullet-list-solid";
  Icon2["bulletSolid"] = "bullet-solid";
  Icon2["bullhornColor"] = "bullhorn-color";
  Icon2["bullhornMono"] = "bullhorn-mono";
  Icon2["calculatorLine"] = "calculator-line";
  Icon2["calculatorSolid"] = "calculator-solid";
  Icon2["calendarCheckLine"] = "calendar-check-line";
  Icon2["calendarCheckSolid"] = "calendar-check-solid";
  Icon2["calendarClockLine"] = "calendar-clock-line";
  Icon2["calendarClockSolid"] = "calendar-clock-solid";
  Icon2["calendarLine"] = "calendar-line";
  Icon2["calendarPlusLine"] = "calendar-plus-line";
  Icon2["calendarPlusSolid"] = "calendar-plus-solid";
  Icon2["calendarSolid"] = "calendar-solid";
  Icon2["callLine"] = "call-line";
  Icon2["callSolid"] = "call-solid";
  Icon2["callStarLine"] = "call-star-line";
  Icon2["callStarSolid"] = "call-star-solid";
  Icon2["cameraLine"] = "camera-line";
  Icon2["cameraSolid"] = "camera-solid";
  Icon2["cameraSwitchLine"] = "camera-switch-line";
  Icon2["cameraSwitchSolid"] = "camera-switch-solid";
  Icon2["carLine"] = "car-line";
  Icon2["carSolid"] = "car-solid";
  Icon2["cartFullLine"] = "cart-full-line";
  Icon2["cartFullSolid"] = "cart-full-solid";
  Icon2["cartLine"] = "cart-line";
  Icon2["cartSolid"] = "cart-solid";
  Icon2["cellReceptionLine"] = "cell-reception-line";
  Icon2["cellReceptionSolid"] = "cell-reception-solid";
  Icon2["chainLine"] = "chain-line";
  Icon2["chainSolid"] = "chain-solid";
  Icon2["charityLine"] = "charity-line";
  Icon2["charitySolid"] = "charity-solid";
  Icon2["chartLine"] = "chart-line";
  Icon2["chartSolid"] = "chart-solid";
  Icon2["chat2Line"] = "chat-2-line";
  Icon2["chat2Solid"] = "chat-2-solid";
  Icon2["chatCallLine"] = "chat-call-line";
  Icon2["chatCallSolid"] = "chat-call-solid";
  Icon2["chatHideLine"] = "chat-hide-line";
  Icon2["chatHideSolid"] = "chat-hide-solid";
  Icon2["chatLine"] = "chat-line";
  Icon2["chatSolid"] = "chat-solid";
  Icon2["checkAllLine"] = "check-all-line";
  Icon2["checkAllSolid"] = "check-all-solid";
  Icon2["checkCallLine"] = "check-call-line";
  Icon2["checkCallSolid"] = "check-call-solid";
  Icon2["checkCircleLine"] = "check-circle-line";
  Icon2["checkCircleSolid"] = "check-circle-solid";
  Icon2["checkDoubleLine"] = "check-double-line";
  Icon2["checkDoubleSolid"] = "check-double-solid";
  Icon2["checkLine"] = "check-line";
  Icon2["checkSentLine"] = "check-sent-line";
  Icon2["checkSentSolid"] = "check-sent-solid";
  Icon2["checkSolid"] = "check-solid";
  Icon2["checkboxChecked2Line"] = "checkbox-checked-2-line";
  Icon2["checkboxChecked2Solid"] = "checkbox-checked-2-solid";
  Icon2["checkboxCheckedLine"] = "checkbox-checked-line";
  Icon2["checkboxCheckedSolid"] = "checkbox-checked-solid";
  Icon2["checkboxPartCheckedLine"] = "checkbox-part-checked-line";
  Icon2["checkboxPartCheckedSolid"] = "checkbox-part-checked-solid";
  Icon2["checkboxUnchecked2Line"] = "checkbox-unchecked-2-line";
  Icon2["checkboxUnchecked2Solid"] = "checkbox-unchecked-2-solid";
  Icon2["checkboxUncheckedLine"] = "checkbox-unchecked-line";
  Icon2["checkboxUncheckedSolid"] = "checkbox-unchecked-solid";
  Icon2["chevronCircleDownLine"] = "chevron-circle-down-line";
  Icon2["chevronCircleDownSolid"] = "chevron-circle-down-solid";
  Icon2["chevronDownLine"] = "chevron-down-line";
  Icon2["chevronDownSolid"] = "chevron-down-solid";
  Icon2["chevronLeftLine"] = "chevron-left-line";
  Icon2["chevronLeftSolid"] = "chevron-left-solid";
  Icon2["chevronRightLine"] = "chevron-right-line";
  Icon2["chevronRightSolid"] = "chevron-right-solid";
  Icon2["chevronUpLine"] = "chevron-up-line";
  Icon2["chevronUpSolid"] = "chevron-up-solid";
  Icon2["chimeColor"] = "chime-color";
  Icon2["chimeMono"] = "chime-mono";
  Icon2["circleLine"] = "circle-line";
  Icon2["circleSolid"] = "circle-solid";
  Icon2["clearFileLine"] = "clear-file-line";
  Icon2["clearFileSolid"] = "clear-file-solid";
  Icon2["clickToCallLine"] = "click-to-call-line";
  Icon2["clickToCallSolid"] = "click-to-call-solid";
  Icon2["clioColor"] = "clio-color";
  Icon2["clioMono"] = "clio-mono";
  Icon2["clipboardAddLine"] = "clipboard-add-line";
  Icon2["clipboardAddSolid"] = "clipboard-add-solid";
  Icon2["clipboardAssignLine"] = "clipboard-assign-line";
  Icon2["clipboardAssignSolid"] = "clipboard-assign-solid";
  Icon2["clockLine"] = "clock-line";
  Icon2["clockOffLine"] = "clock-off-line";
  Icon2["clockOffSolid"] = "clock-off-solid";
  Icon2["clockSolid"] = "clock-solid";
  Icon2["closeCircleLine"] = "close-circle-line";
  Icon2["closeCircleSolid"] = "close-circle-solid";
  Icon2["closeLine"] = "close-line";
  Icon2["closeSmallLine"] = "close-small-line";
  Icon2["closeSmallSolid"] = "close-small-solid";
  Icon2["closeSolid"] = "close-solid";
  Icon2["closedCaptioningLine"] = "closed-captioning-line";
  Icon2["closedCaptioningOffLine"] = "closed-captioning-off-line";
  Icon2["closedCaptioningOffSolid"] = "closed-captioning-off-solid";
  Icon2["closedCaptioningSolid"] = "closed-captioning-solid";
  Icon2["cloudDownload2Line"] = "cloud-download-2-line";
  Icon2["cloudDownload2Solid"] = "cloud-download-2-solid";
  Icon2["cloudLine"] = "cloud-line";
  Icon2["cloudOfficePhoneLine"] = "cloud-office-phone-line";
  Icon2["cloudOfficePhoneSolid"] = "cloud-office-phone-solid";
  Icon2["cloudSolid"] = "cloud-solid";
  Icon2["cloudUpload2Line"] = "cloud-upload-2-line";
  Icon2["cloudUpload2Solid"] = "cloud-upload-2-solid";
  Icon2["cloudUploadLine"] = "cloud-upload-line";
  Icon2["cloudUploadSolid"] = "cloud-upload-solid";
  Icon2["codeLine"] = "code-line";
  Icon2["codeSnippetLine"] = "code-snippet-line";
  Icon2["codeSnippetSolid"] = "code-snippet-solid";
  Icon2["codeSolid"] = "code-solid";
  Icon2["coffeeLine"] = "coffee-line";
  Icon2["coffeeSolid"] = "coffee-solid";
  Icon2["collapseLine"] = "collapse-line";
  Icon2["collapseSolid"] = "collapse-solid";
  Icon2["commsOnlineColor"] = "comms-online-color";
  Icon2["commsOnlineMono"] = "comms-online-mono";
  Icon2["companyLine"] = "company-line";
  Icon2["companySolid"] = "company-solid";
  Icon2["composeLine"] = "compose-line";
  Icon2["composeSolid"] = "compose-solid";
  Icon2["conditionLine"] = "condition-line";
  Icon2["conditionSolid"] = "condition-solid";
  Icon2["connectLine"] = "connect-line";
  Icon2["connectSolid"] = "connect-solid";
  Icon2["connectwiseColor"] = "connectwise-color";
  Icon2["connectwiseMono"] = "connectwise-mono";
  Icon2["cooktailLine"] = "cooktail-line";
  Icon2["cooktailSolid"] = "cooktail-solid";
  Icon2["copy2Line"] = "copy-2-line";
  Icon2["copy2Solid"] = "copy-2-solid";
  Icon2["copyLine"] = "copy-line";
  Icon2["copySolid"] = "copy-solid";
  Icon2["creditCardLine"] = "credit-card-line";
  Icon2["creditCardSolid"] = "credit-card-solid";
  Icon2["cropLine"] = "crop-line";
  Icon2["cropSolid"] = "crop-solid";
  Icon2["curlColor"] = "curl-color";
  Icon2["curlMono"] = "curl-mono";
  Icon2["curlyBracketsLine"] = "curly-brackets-line";
  Icon2["curlyBracketsSolid"] = "curly-brackets-solid";
  Icon2["cursor2Line"] = "cursor-2-line";
  Icon2["cursor2Solid"] = "cursor-2-solid";
  Icon2["cursorLine"] = "cursor-line";
  Icon2["cursorSolid"] = "cursor-solid";
  Icon2["darkModeLine"] = "dark-mode-line";
  Icon2["darkModeSolid"] = "dark-mode-solid";
  Icon2["deleteLine"] = "delete-line";
  Icon2["deleteSolid"] = "delete-solid";
  Icon2["densityHighLine"] = "density-high-line";
  Icon2["densityHighSolid"] = "density-high-solid";
  Icon2["densityMediumLine"] = "density-medium-line";
  Icon2["densityMediumSolid"] = "density-medium-solid";
  Icon2["designToolsLine"] = "design-tools-line";
  Icon2["designToolsSolid"] = "design-tools-solid";
  Icon2["devicesLine"] = "devices-line";
  Icon2["devicesSolid"] = "devices-solid";
  Icon2["disableCallLine"] = "disable-call-line";
  Icon2["disableCallSolid"] = "disable-call-solid";
  Icon2["distortLine"] = "distort-line";
  Icon2["distortSolid"] = "distort-solid";
  Icon2["dividerLine"] = "divider-line";
  Icon2["dividerSolid"] = "divider-solid";
  Icon2["document2Line"] = "document-2-line";
  Icon2["document2Solid"] = "document-2-solid";
  Icon2["documentLine"] = "document-line";
  Icon2["documentSolid"] = "document-solid";
  Icon2["dollarLine"] = "dollar-line";
  Icon2["dollarSolid"] = "dollar-solid";
  Icon2["dotnetColor"] = "dotnet-color";
  Icon2["dotnetMono"] = "dotnet-mono";
  Icon2["doubleChevronLeftLine"] = "double-chevron-left-line";
  Icon2["doubleChevronLeftSolid"] = "double-chevron-left-solid";
  Icon2["doubleChevronRightLine"] = "double-chevron-right-line";
  Icon2["doubleChevronRightSolid"] = "double-chevron-right-solid";
  Icon2["downloadLine"] = "download-line";
  Icon2["downloadSolid"] = "download-solid";
  Icon2["drupalColor"] = "drupal-color";
  Icon2["drupalMono"] = "drupal-mono";
  Icon2["duplicateLine"] = "duplicate-line";
  Icon2["duplicateSolid"] = "duplicate-solid";
  Icon2["dynamics365Color"] = "dynamics-365-color";
  Icon2["dynamics365Mono"] = "dynamics-365-mono";
  Icon2["dynamicsColor"] = "dynamics-color";
  Icon2["dynamicsMono"] = "dynamics-mono";
  Icon2["echoLine"] = "echo-line";
  Icon2["echoSolid"] = "echo-solid";
  Icon2["editLine"] = "edit-line";
  Icon2["editSolid"] = "edit-solid";
  Icon2["editUserLine"] = "edit-user-line";
  Icon2["editUserSolid"] = "edit-user-solid";
  Icon2["eightLine"] = "eight-line";
  Icon2["eightSolid"] = "eight-solid";
  Icon2["emojiLine"] = "emoji-line";
  Icon2["emojiSolid"] = "emoji-solid";
  Icon2["endCallLine"] = "end-call-line";
  Icon2["endCallSolid"] = "end-call-solid";
  Icon2["enlargeScreen2Line"] = "enlarge-screen-2-line";
  Icon2["enlargeScreen2Solid"] = "enlarge-screen-2-solid";
  Icon2["enlargeScreenLine"] = "enlarge-screen-line";
  Icon2["enlargeScreenSolid"] = "enlarge-screen-solid";
  Icon2["enterLine"] = "enter-line";
  Icon2["enterSolid"] = "enter-solid";
  Icon2["envelopeLine"] = "envelope-line";
  Icon2["envelopeSolid"] = "envelope-solid";
  Icon2["eraserLine"] = "eraser-line";
  Icon2["eraserSolid"] = "eraser-solid";
  Icon2["errorLine"] = "error-line";
  Icon2["errorSolid"] = "error-solid";
  Icon2["errorStarLine"] = "error-star-line";
  Icon2["errorStarSolid"] = "error-star-solid";
  Icon2["exitLine"] = "exit-line";
  Icon2["exitSolid"] = "exit-solid";
  Icon2["expand2Line"] = "expand-2-line";
  Icon2["expand2Solid"] = "expand-2-solid";
  Icon2["expandLine"] = "expand-line";
  Icon2["expandSolid"] = "expand-solid";
  Icon2["exportLine"] = "export-line";
  Icon2["exportSolid"] = "export-solid";
  Icon2["expressColor"] = "express-color";
  Icon2["expressMono"] = "express-mono";
  Icon2["eyeHideLine"] = "eye-hide-line";
  Icon2["eyeHideSolid"] = "eye-hide-solid";
  Icon2["eyeLine"] = "eye-line";
  Icon2["eyeSolid"] = "eye-solid";
  Icon2["facebookColor"] = "facebook-color";
  Icon2["facebookLine"] = "facebook-line";
  Icon2["facebookMono"] = "facebook-mono";
  Icon2["facebookSolid"] = "facebook-solid";
  Icon2["fax2Line"] = "fax-2-line";
  Icon2["fax2Solid"] = "fax-2-solid";
  Icon2["faxAddLine"] = "fax-add-line";
  Icon2["faxAddSolid"] = "fax-add-solid";
  Icon2["faxFailedLine"] = "fax-failed-line";
  Icon2["faxFailedSolid"] = "fax-failed-solid";
  Icon2["faxInboundLine"] = "fax-inbound-line";
  Icon2["faxInboundSolid"] = "fax-inbound-solid";
  Icon2["faxLine"] = "fax-line";
  Icon2["faxOutboundLine"] = "fax-outbound-line";
  Icon2["faxOutboundSolid"] = "fax-outbound-solid";
  Icon2["faxPendingLine"] = "fax-pending-line";
  Icon2["faxPendingSolid"] = "fax-pending-solid";
  Icon2["faxScheduledLine"] = "fax-scheduled-line";
  Icon2["faxScheduledSolid"] = "fax-scheduled-solid";
  Icon2["faxSolid"] = "fax-solid";
  Icon2["feedbackLine"] = "feedback-line";
  Icon2["feedbackSolid"] = "feedback-solid";
  Icon2["fileAddLine"] = "file-add-line";
  Icon2["fileAddSolid"] = "file-add-solid";
  Icon2["fileAudioLine"] = "file-audio-line";
  Icon2["fileAudioSolid"] = "file-audio-solid";
  Icon2["fileCsvLine"] = "file-csv-line";
  Icon2["fileCsvSolid"] = "file-csv-solid";
  Icon2["fileDocLine"] = "file-doc-line";
  Icon2["fileDocSolid"] = "file-doc-solid";
  Icon2["fileDollarLine"] = "file-dollar-line";
  Icon2["fileDollarSolid"] = "file-dollar-solid";
  Icon2["fileEditLine"] = "file-edit-line";
  Icon2["fileEditSolid"] = "file-edit-solid";
  Icon2["fileGearLine"] = "file-gear-line";
  Icon2["fileGearSolid"] = "file-gear-solid";
  Icon2["fileGif2Line"] = "file-gif-2-line";
  Icon2["fileGif2Solid"] = "file-gif-2-solid";
  Icon2["fileGifLine"] = "file-gif-line";
  Icon2["fileGifSolid"] = "file-gif-solid";
  Icon2["fileJpgLine"] = "file-jpg-line";
  Icon2["fileJpgSolid"] = "file-jpg-solid";
  Icon2["fileLine"] = "file-line";
  Icon2["fileParagraphLine"] = "file-paragraph-line";
  Icon2["fileParagraphSolid"] = "file-paragraph-solid";
  Icon2["filePdfLine"] = "file-pdf-line";
  Icon2["filePdfSolid"] = "file-pdf-solid";
  Icon2["filePlusLine"] = "file-plus-line";
  Icon2["filePlusSolid"] = "file-plus-solid";
  Icon2["filePngLine"] = "file-png-line";
  Icon2["filePngSolid"] = "file-png-solid";
  Icon2["filePptLine"] = "file-ppt-line";
  Icon2["filePptSolid"] = "file-ppt-solid";
  Icon2["fileSearchLine"] = "file-search-line";
  Icon2["fileSearchSolid"] = "file-search-solid";
  Icon2["fileSolid"] = "file-solid";
  Icon2["fileTxtLine"] = "file-txt-line";
  Icon2["fileTxtSolid"] = "file-txt-solid";
  Icon2["fileVideo2Line"] = "file-video-2-line";
  Icon2["fileVideo2Solid"] = "file-video-2-solid";
  Icon2["fileVideoLine"] = "file-video-line";
  Icon2["fileVideoSolid"] = "file-video-solid";
  Icon2["fileXlsLine"] = "file-xls-line";
  Icon2["fileXlsSolid"] = "file-xls-solid";
  Icon2["fileZipLine"] = "file-zip-line";
  Icon2["fileZipSolid"] = "file-zip-solid";
  Icon2["filesLine"] = "files-line";
  Icon2["filesSolid"] = "files-solid";
  Icon2["filterLine"] = "filter-line";
  Icon2["filterSolid"] = "filter-solid";
  Icon2["fiveLine"] = "five-line";
  Icon2["fiveSolid"] = "five-solid";
  Icon2["flagAfghanistan"] = "flag-afghanistan";
  Icon2["flagAlandIslands"] = "flag-aland-islands";
  Icon2["flagAlbania"] = "flag-albania";
  Icon2["flagAlgeria"] = "flag-algeria";
  Icon2["flagArgentina"] = "flag-argentina";
  Icon2["flagArmenia"] = "flag-armenia";
  Icon2["flagAruba"] = "flag-aruba";
  Icon2["flagAustralia"] = "flag-australia";
  Icon2["flagAustria"] = "flag-austria";
  Icon2["flagBahamas"] = "flag-bahamas";
  Icon2["flagBahrain"] = "flag-bahrain";
  Icon2["flagBangladesh"] = "flag-bangladesh";
  Icon2["flagBelarus"] = "flag-belarus";
  Icon2["flagBelgium"] = "flag-belgium";
  Icon2["flagBenin"] = "flag-benin";
  Icon2["flagBolivia"] = "flag-bolivia";
  Icon2["flagBotswana"] = "flag-botswana";
  Icon2["flagBrazil"] = "flag-brazil";
  Icon2["flagBulgaria"] = "flag-bulgaria";
  Icon2["flagBurkinaFaso"] = "flag-burkina-faso";
  Icon2["flagCambodia"] = "flag-cambodia";
  Icon2["flagCanada"] = "flag-canada";
  Icon2["flagCaymanIslands"] = "flag-cayman-islands";
  Icon2["flagCentralAfricanRepublic"] = "flag-central-african-republic";
  Icon2["flagChad"] = "flag-chad";
  Icon2["flagChile"] = "flag-chile";
  Icon2["flagChina"] = "flag-china";
  Icon2["flagColombia"] = "flag-colombia";
  Icon2["flagCostaRica"] = "flag-costa-rica";
  Icon2["flagCroatia"] = "flag-croatia";
  Icon2["flagCyprus"] = "flag-cyprus";
  Icon2["flagCzechRepublic"] = "flag-czech-republic";
  Icon2["flagDenmark"] = "flag-denmark";
  Icon2["flagDominicanRepublic"] = "flag-dominican-republic";
  Icon2["flagEgypt"] = "flag-egypt";
  Icon2["flagElSalvador"] = "flag-el-salvador";
  Icon2["flagEstonia"] = "flag-estonia";
  Icon2["flagEuropeanUnion"] = "flag-european-union";
  Icon2["flagFinland"] = "flag-finland";
  Icon2["flagFrance"] = "flag-france";
  Icon2["flagGeorgia"] = "flag-georgia";
  Icon2["flagGermany"] = "flag-germany";
  Icon2["flagGhana"] = "flag-ghana";
  Icon2["flagGreece"] = "flag-greece";
  Icon2["flagGrenada"] = "flag-grenada";
  Icon2["flagGuatemala"] = "flag-guatemala";
  Icon2["flagHonduras"] = "flag-honduras";
  Icon2["flagHongKong"] = "flag-hong-kong";
  Icon2["flagHungary"] = "flag-hungary";
  Icon2["flagIceland"] = "flag-iceland";
  Icon2["flagIndia"] = "flag-india";
  Icon2["flagIndonesia"] = "flag-indonesia";
  Icon2["flagIreland"] = "flag-ireland";
  Icon2["flagIsrael"] = "flag-israel";
  Icon2["flagItaly"] = "flag-italy";
  Icon2["flagJamaica"] = "flag-jamaica";
  Icon2["flagJapan"] = "flag-japan";
  Icon2["flagLatvia"] = "flag-latvia";
  Icon2["flagLiechtenstein"] = "flag-liechtenstein";
  Icon2["flagLithuania"] = "flag-lithuania";
  Icon2["flagLuxembourg"] = "flag-luxembourg";
  Icon2["flagMacao"] = "flag-macao";
  Icon2["flagMalaysia"] = "flag-malaysia";
  Icon2["flagMalta"] = "flag-malta";
  Icon2["flagMauritius"] = "flag-mauritius";
  Icon2["flagMexico"] = "flag-mexico";
  Icon2["flagMoldova"] = "flag-moldova";
  Icon2["flagNetherlands"] = "flag-netherlands";
  Icon2["flagNewZealand"] = "flag-new-zealand";
  Icon2["flagNigeria"] = "flag-nigeria";
  Icon2["flagNorway"] = "flag-norway";
  Icon2["flagPakistan"] = "flag-pakistan";
  Icon2["flagPanama"] = "flag-panama";
  Icon2["flagPeru"] = "flag-peru";
  Icon2["flagPhilippines"] = "flag-philippines";
  Icon2["flagPoland"] = "flag-poland";
  Icon2["flagPortugal"] = "flag-portugal";
  Icon2["flagPuertorico"] = "flag-puertorico";
  Icon2["flagRomania"] = "flag-romania";
  Icon2["flagRussia"] = "flag-russia";
  Icon2["flagRwanda"] = "flag-rwanda";
  Icon2["flagSaudiArabia"] = "flag-saudi-arabia";
  Icon2["flagSerbia"] = "flag-serbia";
  Icon2["flagSingapore"] = "flag-singapore";
  Icon2["flagSlovakia"] = "flag-slovakia";
  Icon2["flagSlovenia"] = "flag-slovenia";
  Icon2["flagSouthAfrica"] = "flag-south-africa";
  Icon2["flagSouthKorea"] = "flag-south-korea";
  Icon2["flagSpain"] = "flag-spain";
  Icon2["flagSriLanka"] = "flag-sri-lanka";
  Icon2["flagSweden"] = "flag-sweden";
  Icon2["flagSwitzerland"] = "flag-switzerland";
  Icon2["flagTaiwan"] = "flag-taiwan";
  Icon2["flagTajikistan"] = "flag-tajikistan";
  Icon2["flagTanzania"] = "flag-tanzania";
  Icon2["flagThailand"] = "flag-thailand";
  Icon2["flagTrinidadAndTobago"] = "flag-trinidad-and-tobago";
  Icon2["flagTurkey"] = "flag-turkey";
  Icon2["flagUkraine"] = "flag-ukraine";
  Icon2["flagUnitedArabEmirates"] = "flag-united-arab-emirates";
  Icon2["flagUnitedKingdom"] = "flag-united-kingdom";
  Icon2["flagUnitedStates"] = "flag-united-states";
  Icon2["flagUruguay"] = "flag-uruguay";
  Icon2["flagUzbekistan"] = "flag-uzbekistan";
  Icon2["flagVenezuela"] = "flag-venezuela";
  Icon2["flagVietnam"] = "flag-vietnam";
  Icon2["flagZambia"] = "flag-zambia";
  Icon2["flash2Line"] = "flash-2-line";
  Icon2["flash2Solid"] = "flash-2-solid";
  Icon2["flashLine"] = "flash-line";
  Icon2["flashSolid"] = "flash-solid";
  Icon2["flipHorizontalLine"] = "flip-horizontal-line";
  Icon2["flipHorizontalSolid"] = "flip-horizontal-solid";
  Icon2["flipLine"] = "flip-line";
  Icon2["flipSolid"] = "flip-solid";
  Icon2["flipVerticalLine"] = "flip-vertical-line";
  Icon2["flipVerticalSolid"] = "flip-vertical-solid";
  Icon2["flow2Line"] = "flow-2-line";
  Icon2["flow2Solid"] = "flow-2-solid";
  Icon2["flowLine"] = "flow-line";
  Icon2["flowPermissionLine"] = "flow-permission-line";
  Icon2["flowPermissionSolid"] = "flow-permission-solid";
  Icon2["flowSolid"] = "flow-solid";
  Icon2["forwardCallLine"] = "forward-call-line";
  Icon2["forwardCallSolid"] = "forward-call-solid";
  Icon2["forwardLine"] = "forward-line";
  Icon2["forwardSolid"] = "forward-solid";
  Icon2["fourLine"] = "four-line";
  Icon2["fourSolid"] = "four-solid";
  Icon2["fullScreenLine"] = "full-screen-line";
  Icon2["fullScreenSolid"] = "full-screen-solid";
  Icon2["fullscreenOffLine"] = "fullscreen-off-line";
  Icon2["fullscreenOffSolid"] = "fullscreen-off-solid";
  Icon2["galleryLine"] = "gallery-line";
  Icon2["gallerySolid"] = "gallery-solid";
  Icon2["gearLine"] = "gear-line";
  Icon2["gearSolid"] = "gear-solid";
  Icon2["genderFemaleLine"] = "gender-female-line";
  Icon2["genderFemaleSolid"] = "gender-female-solid";
  Icon2["genderMaleLine"] = "gender-male-line";
  Icon2["genderMaleSolid"] = "gender-male-solid";
  Icon2["genderUnspecifiedLine"] = "gender-unspecified-line";
  Icon2["genderUnspecifiedSolid"] = "gender-unspecified-solid";
  Icon2["githubColor"] = "github-color";
  Icon2["githubMono"] = "github-mono";
  Icon2["globeLine"] = "globe-line";
  Icon2["globeSolid"] = "globe-solid";
  Icon2["googleColor"] = "google-color";
  Icon2["googleMono"] = "google-mono";
  Icon2["gradleColor"] = "gradle-color";
  Icon2["gradleMono"] = "gradle-mono";
  Icon2["gridTwoLine"] = "grid-two-line";
  Icon2["gridTwoSolid"] = "grid-two-solid";
  Icon2["groovyColor"] = "groovy-color";
  Icon2["groovyMono"] = "groovy-mono";
  Icon2["group2Line"] = "group-2-line";
  Icon2["group2Solid"] = "group-2-solid";
  Icon2["group3Line"] = "group-3-line";
  Icon2["group3Solid"] = "group-3-solid";
  Icon2["group4Line"] = "group-4-line";
  Icon2["group4Solid"] = "group-4-solid";
  Icon2["group5Line"] = "group-5-line";
  Icon2["group5Solid"] = "group-5-solid";
  Icon2["group6Line"] = "group-6-line";
  Icon2["group6Solid"] = "group-6-solid";
  Icon2["group7Line"] = "group-7-line";
  Icon2["group7Solid"] = "group-7-solid";
  Icon2["group8Line"] = "group-8-line";
  Icon2["group8Solid"] = "group-8-solid";
  Icon2["group9Line"] = "group-9-line";
  Icon2["group9Solid"] = "group-9-solid";
  Icon2["groupLine"] = "group-line";
  Icon2["groupPermission2Line"] = "group-permission-2-line";
  Icon2["groupPermission2Solid"] = "group-permission-2-solid";
  Icon2["groupPermissionLine"] = "group-permission-line";
  Icon2["groupPermissionSolid"] = "group-permission-solid";
  Icon2["groupSolid"] = "group-solid";
  Icon2["handLine"] = "hand-line";
  Icon2["handOffLine"] = "hand-off-line";
  Icon2["handOffSolid"] = "hand-off-solid";
  Icon2["handShakeLine"] = "hand-shake-line";
  Icon2["handShakeSolid"] = "hand-shake-solid";
  Icon2["handSolid"] = "hand-solid";
  Icon2["happyLine"] = "happy-line";
  Icon2["happySolid"] = "happy-solid";
  Icon2["hashLine"] = "hash-line";
  Icon2["hashSolid"] = "hash-solid";
  Icon2["hashtagLine"] = "hashtag-line";
  Icon2["hashtagSolid"] = "hashtag-solid";
  Icon2["headset2Line"] = "headset-2-line";
  Icon2["headset2Solid"] = "headset-2-solid";
  Icon2["headsetLine"] = "headset-line";
  Icon2["headsetSolid"] = "headset-solid";
  Icon2["hearingLine"] = "hearing-line";
  Icon2["hearingMaxLine"] = "hearing-max-line";
  Icon2["hearingMaxSolid"] = "hearing-max-solid";
  Icon2["hearingSolid"] = "hearing-solid";
  Icon2["heartLine"] = "heart-line";
  Icon2["heartSolid"] = "heart-solid";
  Icon2["helpLine"] = "help-line";
  Icon2["helpSolid"] = "help-solid";
  Icon2["highlightLine"] = "highlight-line";
  Icon2["highlightSolid"] = "highlight-solid";
  Icon2["historyLine"] = "history-line";
  Icon2["historySolid"] = "history-solid";
  Icon2["homeLine"] = "home-line";
  Icon2["homeSolid"] = "home-solid";
  Icon2["homeUserLine"] = "home-user-line";
  Icon2["homeUserSolid"] = "home-user-solid";
  Icon2["hospitalLine"] = "hospital-line";
  Icon2["hospitalSolid"] = "hospital-solid";
  Icon2["hotelingLine"] = "hoteling-line";
  Icon2["hotelingSolid"] = "hoteling-solid";
  Icon2["hubspotColor"] = "hubspot-color";
  Icon2["hubspotMono"] = "hubspot-mono";
  Icon2["idLine"] = "id-line";
  Icon2["idSolid"] = "id-solid";
  Icon2["imageLine"] = "image-line";
  Icon2["imageSolid"] = "image-solid";
  Icon2["importLine"] = "import-line";
  Icon2["importSolid"] = "import-solid";
  Icon2["inboundCallLine"] = "inbound-call-line";
  Icon2["inboundCallSolid"] = "inbound-call-solid";
  Icon2["inbox2Line"] = "inbox-2-line";
  Icon2["inbox2Solid"] = "inbox-2-solid";
  Icon2["inbox3Line"] = "inbox-3-line";
  Icon2["inbox3Solid"] = "inbox-3-solid";
  Icon2["inboxLine"] = "inbox-line";
  Icon2["inboxSolid"] = "inbox-solid";
  Icon2["increaseLine"] = "increase-line";
  Icon2["increaseSolid"] = "increase-solid";
  Icon2["infinityLine"] = "infinity-line";
  Icon2["infinitySolid"] = "infinity-solid";
  Icon2["infoLine"] = "info-line";
  Icon2["infoSolid"] = "info-solid";
  Icon2["instagramColor"] = "instagram-color";
  Icon2["instagramMono"] = "instagram-mono";
  Icon2["interactiveMessageLine"] = "interactive-message-line";
  Icon2["interactiveMessageSolid"] = "interactive-message-solid";
  Icon2["iosColor"] = "ios-color";
  Icon2["iosMono"] = "ios-mono";
  Icon2["italicLine"] = "italic-line";
  Icon2["italicSolid"] = "italic-solid";
  Icon2["javaColor"] = "java-color";
  Icon2["javaMono"] = "java-mono";
  Icon2["javascriptColor"] = "javascript-color";
  Icon2["javascriptMono"] = "javascript-mono";
  Icon2["jobdivaColor"] = "jobdiva-color";
  Icon2["jobdivaMono"] = "jobdiva-mono";
  Icon2["keyLine"] = "key-line";
  Icon2["keySolid"] = "key-solid";
  Icon2["keypad2Line"] = "keypad-2-line";
  Icon2["keypad2Solid"] = "keypad-2-solid";
  Icon2["keypadLine"] = "keypad-line";
  Icon2["keypadSolid"] = "keypad-solid";
  Icon2["knowledgeBaseLine"] = "knowledge-base-line";
  Icon2["knowledgeBaseSolid"] = "knowledge-base-solid";
  Icon2["kotlinColor"] = "kotlin-color";
  Icon2["kotlinMono"] = "kotlin-mono";
  Icon2["laptopLine"] = "laptop-line";
  Icon2["laptopSolid"] = "laptop-solid";
  Icon2["laravelColor"] = "laravel-color";
  Icon2["laravelMono"] = "laravel-mono";
  Icon2["layout1Line"] = "layout-1-line";
  Icon2["layout1Solid"] = "layout-1-solid";
  Icon2["layout2Line"] = "layout-2-line";
  Icon2["layout2Solid"] = "layout-2-solid";
  Icon2["leaveLine"] = "leave-line";
  Icon2["leaveSolid"] = "leave-solid";
  Icon2["lightBulbLine"] = "light-bulb-line";
  Icon2["lightBulbSolid"] = "light-bulb-solid";
  Icon2["lightModeLine"] = "light-mode-line";
  Icon2["lightModeSolid"] = "light-mode-solid";
  Icon2["lineWidthLargeLine"] = "line-width-large-line";
  Icon2["lineWidthLargeSolid"] = "line-width-large-solid";
  Icon2["lineWidthMediumLine"] = "line-width-medium-line";
  Icon2["lineWidthMediumSolid"] = "line-width-medium-solid";
  Icon2["lineWidthSmallLine"] = "line-width-small-line";
  Icon2["lineWidthSmallSolid"] = "line-width-small-solid";
  Icon2["linkLine"] = "link-line";
  Icon2["linkSolid"] = "link-solid";
  Icon2["linkedinColor"] = "linkedin-color";
  Icon2["linkedinMono"] = "linkedin-mono";
  Icon2["linuxColor"] = "linux-color";
  Icon2["linuxMono"] = "linux-mono";
  Icon2["list2Line"] = "list-2-line";
  Icon2["list2Solid"] = "list-2-solid";
  Icon2["listFilterLine"] = "list-filter-line";
  Icon2["listFilterSolid"] = "list-filter-solid";
  Icon2["listLine"] = "list-line";
  Icon2["listNumberedLine"] = "list-numbered-line";
  Icon2["listNumberedSolid"] = "list-numbered-solid";
  Icon2["listSolid"] = "list-solid";
  Icon2["lockLine"] = "lock-line";
  Icon2["lockSolid"] = "lock-solid";
  Icon2["mailboxLine"] = "mailbox-line";
  Icon2["mailboxSolid"] = "mailbox-solid";
  Icon2["maximizeLine"] = "maximize-line";
  Icon2["maximizeSolid"] = "maximize-solid";
  Icon2["mediaPlayerLine"] = "media-player-line";
  Icon2["mediaPlayerOffLine"] = "media-player-off-line";
  Icon2["mediaPlayerOffSolid"] = "media-player-off-solid";
  Icon2["mediaPlayerSolid"] = "media-player-solid";
  Icon2["megaphoneLine"] = "megaphone-line";
  Icon2["megaphoneSolid"] = "megaphone-solid";
  Icon2["menuLine"] = "menu-line";
  Icon2["menuSolid"] = "menu-solid";
  Icon2["merge2Line"] = "merge-2-line";
  Icon2["merge2Solid"] = "merge-2-solid";
  Icon2["mergeLine"] = "merge-line";
  Icon2["mergeSolid"] = "merge-solid";
  Icon2["messageLine"] = "message-line";
  Icon2["messageSentLine"] = "message-sent-line";
  Icon2["messageSentSolid"] = "message-sent-solid";
  Icon2["messageSolid"] = "message-solid";
  Icon2["messageTemplateLine"] = "message-template-line";
  Icon2["messageTemplateSolid"] = "message-template-solid";
  Icon2["micMuteLine"] = "mic-mute-line";
  Icon2["micMuteSolid"] = "mic-mute-solid";
  Icon2["microphone2Line"] = "microphone-2-line";
  Icon2["microphone2Solid"] = "microphone-2-solid";
  Icon2["microphoneLine"] = "microphone-line";
  Icon2["microphoneSolid"] = "microphone-solid";
  Icon2["microsoftDynamics365Color"] = "microsoft-dynamics-365-color";
  Icon2["microsoftDynamics365Mono"] = "microsoft-dynamics-365-mono";
  Icon2["microsoftTeamsColor"] = "microsoft-teams-color";
  Icon2["microsoftTeamsMono"] = "microsoft-teams-mono";
  Icon2["mindMap2Line"] = "mind-map-2-line";
  Icon2["mindMap2Solid"] = "mind-map-2-solid";
  Icon2["mindMapLine"] = "mind-map-line";
  Icon2["mindMapSolid"] = "mind-map-solid";
  Icon2["minimize2Line"] = "minimize-2-line";
  Icon2["minimize2Solid"] = "minimize-2-solid";
  Icon2["minimizeLine"] = "minimize-line";
  Icon2["minimizeSolid"] = "minimize-solid";
  Icon2["minusLine"] = "minus-line";
  Icon2["minusSolid"] = "minus-solid";
  Icon2["missedCallLine"] = "missed-call-line";
  Icon2["missedCallSolid"] = "missed-call-solid";
  Icon2["mobile2Line"] = "mobile-2-line";
  Icon2["mobile2Solid"] = "mobile-2-solid";
  Icon2["mobileArrowRightLine"] = "mobile-arrow-right-line";
  Icon2["mobileArrowRightSolid"] = "mobile-arrow-right-solid";
  Icon2["mobileDevicesLine"] = "mobile-devices-line";
  Icon2["mobileDevicesSolid"] = "mobile-devices-solid";
  Icon2["mobileLine"] = "mobile-line";
  Icon2["mobileSolid"] = "mobile-solid";
  Icon2["mockupLine"] = "mockup-line";
  Icon2["mockupSolid"] = "mockup-solid";
  Icon2["modalLine"] = "modal-line";
  Icon2["modalSolid"] = "modal-solid";
  Icon2["monospaceLine"] = "monospace-line";
  Icon2["monospaceSolid"] = "monospace-solid";
  Icon2["moreHorizontalLine"] = "more-horizontal-line";
  Icon2["moreHorizontalSolid"] = "more-horizontal-solid";
  Icon2["moreVerticalLine"] = "more-vertical-line";
  Icon2["moreVerticalSolid"] = "more-vertical-solid";
  Icon2["musicLine"] = "music-line";
  Icon2["musicSolid"] = "music-solid";
  Icon2["netsuiteColor"] = "netsuite-color";
  Icon2["netsuiteMono"] = "netsuite-mono";
  Icon2["nexmoColor"] = "nexmo-color";
  Icon2["nexmoMono"] = "nexmo-mono";
  Icon2["nextLine"] = "next-line";
  Icon2["nextSolid"] = "next-solid";
  Icon2["nineLine"] = "nine-line";
  Icon2["nineSolid"] = "nine-solid";
  Icon2["nodeColor"] = "node-color";
  Icon2["nodeMono"] = "node-mono";
  Icon2["noteLine"] = "note-line";
  Icon2["noteSolid"] = "note-solid";
  Icon2["notesLine"] = "notes-line";
  Icon2["notesSolid"] = "notes-solid";
  Icon2["notificationOffLine"] = "notification-off-line";
  Icon2["notificationOffSolid"] = "notification-off-solid";
  Icon2["notificationOnLine"] = "notification-on-line";
  Icon2["notificationOnSolid"] = "notification-on-solid";
  Icon2["objectiveCColor"] = "objective-c-color";
  Icon2["objectiveCMono"] = "objective-c-mono";
  Icon2["officeColor"] = "office-color";
  Icon2["officeMono"] = "office-mono";
  Icon2["officePhone2Line"] = "office-phone-2-line";
  Icon2["officePhone2Solid"] = "office-phone-2-solid";
  Icon2["officePhoneLine"] = "office-phone-line";
  Icon2["officePhoneSolid"] = "office-phone-solid";
  Icon2["oneLine"] = "one-line";
  Icon2["oneSolid"] = "one-solid";
  Icon2["openFolderLine"] = "open-folder-line";
  Icon2["openFolderSolid"] = "open-folder-solid";
  Icon2["openLine"] = "open-line";
  Icon2["openSolid"] = "open-solid";
  Icon2["outboundCallLine"] = "outbound-call-line";
  Icon2["outboundCallSolid"] = "outbound-call-solid";
  Icon2["packet2Line"] = "packet-2-line";
  Icon2["packet2Solid"] = "packet-2-solid";
  Icon2["packetLine"] = "packet-line";
  Icon2["packetSolid"] = "packet-solid";
  Icon2["paletteLine"] = "palette-line";
  Icon2["paletteSolid"] = "palette-solid";
  Icon2["parkCallLine"] = "park-call-line";
  Icon2["parkCallSolid"] = "park-call-solid";
  Icon2["participantsLine"] = "participants-line";
  Icon2["participantsOffLine"] = "participants-off-line";
  Icon2["participantsOffSolid"] = "participants-off-solid";
  Icon2["participantsSolid"] = "participants-solid";
  Icon2["passwordRestartLine"] = "password-restart-line";
  Icon2["passwordRestartSolid"] = "password-restart-solid";
  Icon2["pause2Line"] = "pause-2-line";
  Icon2["pause2Solid"] = "pause-2-solid";
  Icon2["pauseCallLine"] = "pause-call-line";
  Icon2["pauseCallSolid"] = "pause-call-solid";
  Icon2["pauseLine"] = "pause-line";
  Icon2["pauseSolid"] = "pause-solid";
  Icon2["penEffectLine"] = "pen-effect-line";
  Icon2["penEffectSolid"] = "pen-effect-solid";
  Icon2["penTipLargeLine"] = "pen-tip-large-line";
  Icon2["penTipLargeSolid"] = "pen-tip-large-solid";
  Icon2["penTipMediumLine"] = "pen-tip-medium-line";
  Icon2["penTipMediumSolid"] = "pen-tip-medium-solid";
  Icon2["penTipSmallLine"] = "pen-tip-small-line";
  Icon2["penTipSmallSolid"] = "pen-tip-small-solid";
  Icon2["pendingCallLine"] = "pending-call-line";
  Icon2["pendingCallSolid"] = "pending-call-solid";
  Icon2["pentoolLine"] = "pentool-line";
  Icon2["pentoolSolid"] = "pentool-solid";
  Icon2["permissionCallLine"] = "permission-call-line";
  Icon2["permissionCallSolid"] = "permission-call-solid";
  Icon2["phoneNumberLine"] = "phone-number-line";
  Icon2["phoneNumberSolid"] = "phone-number-solid";
  Icon2["phpColor"] = "php-color";
  Icon2["phpMono"] = "php-mono";
  Icon2["pieChartLine"] = "pie-chart-line";
  Icon2["pieChartSolid"] = "pie-chart-solid";
  Icon2["pin2Line"] = "pin-2-line";
  Icon2["pin2OffLine"] = "pin-2-off-line";
  Icon2["pin2OffSolid"] = "pin-2-off-solid";
  Icon2["pin2Solid"] = "pin-2-solid";
  Icon2["pinLine"] = "pin-line";
  Icon2["pinOffLine"] = "pin-off-line";
  Icon2["pinOffSolid"] = "pin-off-solid";
  Icon2["pinSolid"] = "pin-solid";
  Icon2["pinUnverifiedLine"] = "pin-unverified-line";
  Icon2["pinUnverifiedSolid"] = "pin-unverified-solid";
  Icon2["pinVerifiedLine"] = "pin-verified-line";
  Icon2["pinVerifiedSolid"] = "pin-verified-solid";
  Icon2["play2Line"] = "play-2-line";
  Icon2["play2Solid"] = "play-2-solid";
  Icon2["playLine"] = "play-line";
  Icon2["playSolid"] = "play-solid";
  Icon2["playbackSpeedLine"] = "playback-speed-line";
  Icon2["playbackSpeedSolid"] = "playback-speed-solid";
  Icon2["playlistLine"] = "playlist-line";
  Icon2["playlistSolid"] = "playlist-solid";
  Icon2["plugLine"] = "plug-line";
  Icon2["plugSolid"] = "plug-solid";
  Icon2["plusLine"] = "plus-line";
  Icon2["plusSolid"] = "plus-solid";
  Icon2["preferencesLine"] = "preferences-line";
  Icon2["preferencesSolid"] = "preferences-solid";
  Icon2["presentationLine"] = "presentation-line";
  Icon2["presentationSolid"] = "presentation-solid";
  Icon2["printLine"] = "print-line";
  Icon2["printSolid"] = "print-solid";
  Icon2["profileLine"] = "profile-line";
  Icon2["profileSolid"] = "profile-solid";
  Icon2["puzzleLine"] = "puzzle-line";
  Icon2["puzzleSolid"] = "puzzle-solid";
  Icon2["puzzledLine"] = "puzzled-line";
  Icon2["puzzledSolid"] = "puzzled-solid";
  Icon2["pythonColor"] = "python-color";
  Icon2["pythonMono"] = "python-mono";
  Icon2["quitLine"] = "quit-line";
  Icon2["quitSolid"] = "quit-solid";
  Icon2["quoteLine"] = "quote-line";
  Icon2["quoteSolid"] = "quote-solid";
  Icon2["radioChecked2Line"] = "radio-checked-2-line";
  Icon2["radioChecked2Solid"] = "radio-checked-2-solid";
  Icon2["radioCheckedLine"] = "radio-checked-line";
  Icon2["radioCheckedSolid"] = "radio-checked-solid";
  Icon2["radioUnchecked2Line"] = "radio-unchecked-2-line";
  Icon2["radioUnchecked2Solid"] = "radio-unchecked-2-solid";
  Icon2["radioUncheckedLine"] = "radio-unchecked-line";
  Icon2["radioUncheckedSolid"] = "radio-unchecked-solid";
  Icon2["railsColor"] = "rails-color";
  Icon2["railsMono"] = "rails-mono";
  Icon2["reactColor"] = "react-color";
  Icon2["reactMono"] = "react-mono";
  Icon2["recLine"] = "rec-line";
  Icon2["recSolid"] = "rec-solid";
  Icon2["receptionistLine"] = "receptionist-line";
  Icon2["receptionistSolid"] = "receptionist-solid";
  Icon2["recordUserLine"] = "record-user-line";
  Icon2["recordUserSolid"] = "record-user-solid";
  Icon2["redoLine"] = "redo-line";
  Icon2["redoSolid"] = "redo-solid";
  Icon2["refreshCallLine"] = "refresh-call-line";
  Icon2["refreshCallSolid"] = "refresh-call-solid";
  Icon2["refreshDollarLine"] = "refresh-dollar-line";
  Icon2["refreshDollarSolid"] = "refresh-dollar-solid";
  Icon2["refreshLine"] = "refresh-line";
  Icon2["refreshSolid"] = "refresh-solid";
  Icon2["reloadLine"] = "reload-line";
  Icon2["reloadSolid"] = "reload-solid";
  Icon2["removeLine"] = "remove-line";
  Icon2["removeSolid"] = "remove-solid";
  Icon2["removeUserLine"] = "remove-user-line";
  Icon2["removeUserSolid"] = "remove-user-solid";
  Icon2["reorderHorizontalLine"] = "reorder-horizontal-line";
  Icon2["reorderHorizontalSolid"] = "reorder-horizontal-solid";
  Icon2["reorderVerticalLine"] = "reorder-vertical-line";
  Icon2["reorderVerticalSolid"] = "reorder-vertical-solid";
  Icon2["replyLine"] = "reply-line";
  Icon2["replySolid"] = "reply-solid";
  Icon2["reportLine"] = "report-line";
  Icon2["reportSolid"] = "report-solid";
  Icon2["resetLine"] = "reset-line";
  Icon2["resetSolid"] = "reset-solid";
  Icon2["rocketDisabledLine"] = "rocket-disabled-line";
  Icon2["rocketDisabledSolid"] = "rocket-disabled-solid";
  Icon2["rocketLine"] = "rocket-line";
  Icon2["rocketSolid"] = "rocket-solid";
  Icon2["rssColor"] = "rss-color";
  Icon2["rssLine"] = "rss-line";
  Icon2["rssMono"] = "rss-mono";
  Icon2["rssSolid"] = "rss-solid";
  Icon2["rubyColor"] = "ruby-color";
  Icon2["rubyMono"] = "ruby-mono";
  Icon2["salesforceColor"] = "salesforce-color";
  Icon2["salesforceLine"] = "salesforce-line";
  Icon2["salesforceMono"] = "salesforce-mono";
  Icon2["salesforceSolid"] = "salesforce-solid";
  Icon2["sandClockLine"] = "sand-clock-line";
  Icon2["sandClockSolid"] = "sand-clock-solid";
  Icon2["saveLine"] = "save-line";
  Icon2["saveSolid"] = "save-solid";
  Icon2["screenShareLine"] = "screen-share-line";
  Icon2["screenShareOffLine"] = "screen-share-off-line";
  Icon2["screenShareOffSolid"] = "screen-share-off-solid";
  Icon2["screenShareSolid"] = "screen-share-solid";
  Icon2["searchLine"] = "search-line";
  Icon2["searchSolid"] = "search-solid";
  Icon2["sendToBackLine"] = "send-to-back-line";
  Icon2["sendToBackSolid"] = "send-to-back-solid";
  Icon2["sevenLine"] = "seven-line";
  Icon2["sevenSolid"] = "seven-solid";
  Icon2["share2Line"] = "share-2-line";
  Icon2["share2Solid"] = "share-2-solid";
  Icon2["shareLine"] = "share-line";
  Icon2["shareSolid"] = "share-solid";
  Icon2["shieldLine"] = "shield-line";
  Icon2["shieldSolid"] = "shield-solid";
  Icon2["shoutLine"] = "shout-line";
  Icon2["shoutSolid"] = "shout-solid";
  Icon2["shuffleLine"] = "shuffle-line";
  Icon2["shuffleSolid"] = "shuffle-solid";
  Icon2["sidebarLine"] = "sidebar-line";
  Icon2["sidebarSolid"] = "sidebar-solid";
  Icon2["sixLine"] = "six-line";
  Icon2["sixSolid"] = "six-solid";
  Icon2["slackColor"] = "slack-color";
  Icon2["slackMono"] = "slack-mono";
  Icon2["smileLine"] = "smile-line";
  Icon2["smileSolid"] = "smile-solid";
  Icon2["smsLine"] = "sms-line";
  Icon2["smsSolid"] = "sms-solid";
  Icon2["snoozeLine"] = "snooze-line";
  Icon2["snoozeSolid"] = "snooze-solid";
  Icon2["sofaLine"] = "sofa-line";
  Icon2["sofaSolid"] = "sofa-solid";
  Icon2["sortAscLine"] = "sort-asc-line";
  Icon2["sortAscSolid"] = "sort-asc-solid";
  Icon2["sortDescLine"] = "sort-desc-line";
  Icon2["sortDescSolid"] = "sort-desc-solid";
  Icon2["sortLine"] = "sort-line";
  Icon2["sortSolid"] = "sort-solid";
  Icon2["sparklesLine"] = "sparkles-line";
  Icon2["sparklesSolid"] = "sparkles-solid";
  Icon2["squareLine"] = "square-line";
  Icon2["squareSolid"] = "square-solid";
  Icon2["stackLine"] = "stack-line";
  Icon2["stackSolid"] = "stack-solid";
  Icon2["stackoverflowColor"] = "stackoverflow-color";
  Icon2["stackoverflowMono"] = "stackoverflow-mono";
  Icon2["starDollarLine"] = "star-dollar-line";
  Icon2["starDollarSolid"] = "star-dollar-solid";
  Icon2["starLine"] = "star-line";
  Icon2["starSolid"] = "star-solid";
  Icon2["stickerLine"] = "sticker-line";
  Icon2["stickerSolid"] = "sticker-solid";
  Icon2["stopLine"] = "stop-line";
  Icon2["stopSolid"] = "stop-solid";
  Icon2["stopwatchLine"] = "stopwatch-line";
  Icon2["stopwatchSolid"] = "stopwatch-solid";
  Icon2["storeLine"] = "store-line";
  Icon2["storeSolid"] = "store-solid";
  Icon2["streamLine"] = "stream-line";
  Icon2["streamSolid"] = "stream-solid";
  Icon2["strikethroughLine"] = "strikethrough-line";
  Icon2["strikethroughSolid"] = "strikethrough-solid";
  Icon2["subAccountLine"] = "sub-account-line";
  Icon2["subAccountSolid"] = "sub-account-solid";
  Icon2["subtitlesLine"] = "subtitles-line";
  Icon2["subtitlesSolid"] = "subtitles-solid";
  Icon2["sugarcrmColor"] = "sugarcrm-color";
  Icon2["sugarcrmMono"] = "sugarcrm-mono";
  Icon2["surprisedLine"] = "surprised-line";
  Icon2["surprisedSolid"] = "surprised-solid";
  Icon2["swiftColor"] = "swift-color";
  Icon2["swiftMono"] = "swift-mono";
  Icon2["switchLine"] = "switch-line";
  Icon2["switchSolid"] = "switch-solid";
  Icon2["symfonyColor"] = "symfony-color";
  Icon2["symfonyMono"] = "symfony-mono";
  Icon2["table2Line"] = "table-2-line";
  Icon2["table2Solid"] = "table-2-solid";
  Icon2["tableLine"] = "table-line";
  Icon2["tableSolid"] = "table-solid";
  Icon2["tagAddLine"] = "tag-add-line";
  Icon2["tagAddSolid"] = "tag-add-solid";
  Icon2["tagDetachLine"] = "tag-detach-line";
  Icon2["tagDetachSolid"] = "tag-detach-solid";
  Icon2["tagLine"] = "tag-line";
  Icon2["tagSearchLine"] = "tag-search-line";
  Icon2["tagSearchSolid"] = "tag-search-solid";
  Icon2["tagSolid"] = "tag-solid";
  Icon2["telephoneLine"] = "telephone-line";
  Icon2["telephoneSolid"] = "telephone-solid";
  Icon2["textLine"] = "text-line";
  Icon2["textSizeLine"] = "text-size-line";
  Icon2["textSizeSolid"] = "text-size-solid";
  Icon2["textSolid"] = "text-solid";
  Icon2["textcolorLine"] = "textcolor-line";
  Icon2["textcolorSolid"] = "textcolor-solid";
  Icon2["textureLine"] = "texture-line";
  Icon2["textureSolid"] = "texture-solid";
  Icon2["threeLine"] = "three-line";
  Icon2["threeSolid"] = "three-solid";
  Icon2["thumbsDownLine"] = "thumbs-down-line";
  Icon2["thumbsDownSolid"] = "thumbs-down-solid";
  Icon2["thumbsUpLine"] = "thumbs-up-line";
  Icon2["thumbsUpSolid"] = "thumbs-up-solid";
  Icon2["timelineLine"] = "timeline-line";
  Icon2["timelineSolid"] = "timeline-solid";
  Icon2["toggleLine"] = "toggle-line";
  Icon2["toggleSolid"] = "toggle-solid";
  Icon2["tooltipLine"] = "tooltip-line";
  Icon2["tooltipSolid"] = "tooltip-solid";
  Icon2["transferCallLine"] = "transfer-call-line";
  Icon2["transferCallSolid"] = "transfer-call-solid";
  Icon2["transferLine"] = "transfer-line";
  Icon2["transferSolid"] = "transfer-solid";
  Icon2["triangleLine"] = "triangle-line";
  Icon2["triangleSolid"] = "triangle-solid";
  Icon2["triggerNodeLine"] = "trigger-node-line";
  Icon2["triggerNodeSolid"] = "trigger-node-solid";
  Icon2["twitchColor"] = "twitch-color";
  Icon2["twitchMono"] = "twitch-mono";
  Icon2["twitterColor"] = "twitter-color";
  Icon2["twitterMono"] = "twitter-mono";
  Icon2["twoLine"] = "two-line";
  Icon2["twoSolid"] = "two-solid";
  Icon2["umbrellaLine"] = "umbrella-line";
  Icon2["umbrellaSolid"] = "umbrella-solid";
  Icon2["underlineLine"] = "underline-line";
  Icon2["underlineSolid"] = "underline-solid";
  Icon2["undoLine"] = "undo-line";
  Icon2["undoSolid"] = "undo-solid";
  Icon2["unfavouriteLine"] = "unfavourite-line";
  Icon2["unfavouriteSolid"] = "unfavourite-solid";
  Icon2["unhappyLine"] = "unhappy-line";
  Icon2["unhappySolid"] = "unhappy-solid";
  Icon2["unlinkLine"] = "unlink-line";
  Icon2["unlinkSolid"] = "unlink-solid";
  Icon2["unlockLine"] = "unlock-line";
  Icon2["unlockSolid"] = "unlock-solid";
  Icon2["uploadLine"] = "upload-line";
  Icon2["uploadSolid"] = "upload-solid";
  Icon2["user2Line"] = "user-2-line";
  Icon2["user2Solid"] = "user-2-solid";
  Icon2["userAdminLine"] = "user-admin-line";
  Icon2["userAdminSolid"] = "user-admin-solid";
  Icon2["userClockLine"] = "user-clock-line";
  Icon2["userClockSolid"] = "user-clock-solid";
  Icon2["userLine"] = "user-line";
  Icon2["userLockLine"] = "user-lock-line";
  Icon2["userLockSolid"] = "user-lock-solid";
  Icon2["userRecordLine"] = "user-record-line";
  Icon2["userRecordSolid"] = "user-record-solid";
  Icon2["userSearchLine"] = "user-search-line";
  Icon2["userSearchSolid"] = "user-search-solid";
  Icon2["userSolid"] = "user-solid";
  Icon2["userSync2Line"] = "user-sync-2-line";
  Icon2["userSync2OffLine"] = "user-sync-2-off-line";
  Icon2["userSync2OffSolid"] = "user-sync-2-off-solid";
  Icon2["userSync2Solid"] = "user-sync-2-solid";
  Icon2["userSyncLine"] = "user-sync-line";
  Icon2["userSyncSolid"] = "user-sync-solid";
  Icon2["vanLine"] = "van-line";
  Icon2["vanSolid"] = "van-solid";
  Icon2["viberColor"] = "viber-color";
  Icon2["viberMono"] = "viber-mono";
  Icon2["videoActiveLine"] = "video-active-line";
  Icon2["videoActiveSolid"] = "video-active-solid";
  Icon2["videoConferenceLine"] = "video-conference-line";
  Icon2["videoConferenceSolid"] = "video-conference-solid";
  Icon2["videoLine"] = "video-line";
  Icon2["videoOffLine"] = "video-off-line";
  Icon2["videoOffSolid"] = "video-off-solid";
  Icon2["videoPreviewLine"] = "video-preview-line";
  Icon2["videoPreviewSolid"] = "video-preview-solid";
  Icon2["videoSolid"] = "video-solid";
  Icon2["videoWhiteboardLine"] = "video-whiteboard-line";
  Icon2["videoWhiteboardSolid"] = "video-whiteboard-solid";
  Icon2["video_round_tableLine"] = "video_round_table-line";
  Icon2["video_round_tableSolid"] = "video_round_table-solid";
  Icon2["virtualVoicemailLine"] = "virtual-voicemail-line";
  Icon2["virtualVoicemailSolid"] = "virtual-voicemail-solid";
  Icon2["voicemailLine"] = "voicemail-line";
  Icon2["voicemailSolid"] = "voicemail-solid";
  Icon2["voicemailTranscriptLine"] = "voicemail-transcript-line";
  Icon2["voicemailTranscriptSolid"] = "voicemail-transcript-solid";
  Icon2["volumeDecreaseLine"] = "volume-decrease-line";
  Icon2["volumeDecreaseSolid"] = "volume-decrease-solid";
  Icon2["volumeIncreaseLine"] = "volume-increase-line";
  Icon2["volumeIncreaseSolid"] = "volume-increase-solid";
  Icon2["volumeLine"] = "volume-line";
  Icon2["volumeOff2Line"] = "volume-off-2-line";
  Icon2["volumeOff2Solid"] = "volume-off-2-solid";
  Icon2["volumeOffLine"] = "volume-off-line";
  Icon2["volumeOffSolid"] = "volume-off-solid";
  Icon2["volumeOnLine"] = "volume-on-line";
  Icon2["volumeOnSolid"] = "volume-on-solid";
  Icon2["volumeSolid"] = "volume-solid";
  Icon2["vonageColor"] = "vonage-color";
  Icon2["vonageLine"] = "vonage-line";
  Icon2["vonageMono"] = "vonage-mono";
  Icon2["vonageSolid"] = "vonage-solid";
  Icon2["vueColor"] = "vue-color";
  Icon2["vueMono"] = "vue-mono";
  Icon2["walkLine"] = "walk-line";
  Icon2["walkSolid"] = "walk-solid";
  Icon2["walletLine"] = "wallet-line";
  Icon2["walletSolid"] = "wallet-solid";
  Icon2["wandLine"] = "wand-line";
  Icon2["wandSolid"] = "wand-solid";
  Icon2["warningLine"] = "warning-line";
  Icon2["warningSolid"] = "warning-solid";
  Icon2["wavingLine"] = "waving-line";
  Icon2["wavingSolid"] = "waving-solid";
  Icon2["webChatColor"] = "web-chat-color";
  Icon2["webChatMono"] = "web-chat-mono";
  Icon2["webhookLine"] = "webhook-line";
  Icon2["webhookSolid"] = "webhook-solid";
  Icon2["whatsappColor"] = "whatsapp-color";
  Icon2["whatsappMono"] = "whatsapp-mono";
  Icon2["whisperLine"] = "whisper-line";
  Icon2["whisperSolid"] = "whisper-solid";
  Icon2["wifiLine"] = "wifi-line";
  Icon2["wifiSolid"] = "wifi-solid";
  Icon2["windowsColor"] = "windows-color";
  Icon2["windowsMono"] = "windows-mono";
  Icon2["wordpressColor"] = "wordpress-color";
  Icon2["wordpressMono"] = "wordpress-mono";
  Icon2["workplaceColor"] = "workplace-color";
  Icon2["workplaceMono"] = "workplace-mono";
  Icon2["wrenchTool2Line"] = "wrench-tool-2-line";
  Icon2["wrenchTool2Solid"] = "wrench-tool-2-solid";
  Icon2["wrenchToolLine"] = "wrench-tool-line";
  Icon2["wrenchToolOffLine"] = "wrench-tool-off-line";
  Icon2["wrenchToolOffSolid"] = "wrench-tool-off-solid";
  Icon2["wrenchToolSolid"] = "wrench-tool-solid";
  Icon2["xTwitterColor"] = "x-twitter-color";
  Icon2["xTwitterMono"] = "x-twitter-mono";
  Icon2["xmlColor"] = "xml-color";
  Icon2["xmlMono"] = "xml-mono";
  Icon2["youtubeColor"] = "youtube-color";
  Icon2["youtubeMono"] = "youtube-mono";
  Icon2["zendeskColor"] = "zendesk-color";
  Icon2["zendeskMono"] = "zendesk-mono";
  Icon2["zeroLine"] = "zero-line";
  Icon2["zeroSolid"] = "zero-solid";
  Icon2["zohoColor"] = "zoho-color";
  Icon2["zohoMono"] = "zoho-mono";
  Icon2["zoomInLine"] = "zoom-in-line";
  Icon2["zoomInSolid"] = "zoom-in-solid";
  Icon2["zoomOutLine"] = "zoom-out-line";
  Icon2["zoomOutSolid"] = "zoom-out-solid";
  Icon2["zoomToFitLine"] = "zoom-to-fit-line";
  Icon2["zoomToFitSolid"] = "zoom-to-fit-solid";
  return Icon2;
})(Icon || {});

const tokensThemeLightCss = "/**\n * Do not edit directly\n * Generated on Thu, 14 Aug 2025 08:21:42 GMT\n */\n/**\n * Do not edit directly\n * Generated on Thu, 14 Aug 2025 08:21:42 GMT\n */\n/**\n * Do not edit directly\n * Generated on Thu, 14 Aug 2025 08:21:42 GMT\n */\n/**\n * Do not edit directly\n * Generated on Thu, 14 Aug 2025 08:21:43 GMT\n */\n@property --vvd-size-density {\n  syntax: \"<integer>\";\n  inherits: true;\n  initial-value: 0;\n}\n@property --vvd-size-font-scale-base {\n  syntax: \"<length>\";\n  inherits: true;\n  initial-value: 16px;\n}\n.vvd-root, ::part(vvd-root) {\n  --vvd-color-scheme: light;\n  --vvd-color-canvas: #ffffff;\n  --vvd-color-canvas-text: #0d0d0d;\n  --vvd-color-neutral-50: #F2F2F2;\n  --vvd-color-neutral-100: #E6E6E6;\n  --vvd-color-neutral-200: #cccccc;\n  --vvd-color-neutral-300: #b3b3b3;\n  --vvd-color-neutral-400: #929292;\n  --vvd-color-neutral-500: #757575;\n  --vvd-color-neutral-600: #666666;\n  --vvd-color-neutral-700: #4d4d4d;\n  --vvd-color-neutral-800: #333333;\n  --vvd-color-neutral-900: #1a1a1a;\n  --vvd-color-neutral-950: #0d0d0d;\n  --vvd-color-cta-50: #f5f0fd;\n  --vvd-color-cta-100: #ece2fa;\n  --vvd-color-cta-200: #dcc1fc;\n  --vvd-color-cta-300: #cba1fa;\n  --vvd-color-cta-400: #b27bf2;\n  --vvd-color-cta-500: #9941ff;\n  --vvd-color-cta-600: #871eff;\n  --vvd-color-cta-700: #6405d1;\n  --vvd-color-cta-800: #440291;\n  --vvd-color-cta-900: #26044d;\n  --vvd-color-cta-950: #140623;\n  --vvd-color-warning-50: #FDF5D4;\n  --vvd-color-warning-100: #FDEAA0;\n  --vvd-color-warning-200: #FACC4B;\n  --vvd-color-warning-300: #fa9f00;\n  --vvd-color-warning-400: #e07902;\n  --vvd-color-warning-500: #be5702;\n  --vvd-color-warning-600: #A64C03;\n  --vvd-color-warning-700: #893000;\n  --vvd-color-warning-800: #522801;\n  --vvd-color-warning-900: #2A1502;\n  --vvd-color-warning-950: #150B01;\n  --vvd-color-information-50: #e8f4fb;\n  --vvd-color-information-100: #d3e9fc;\n  --vvd-color-information-200: #9dd2fe;\n  --vvd-color-information-300: #65baff;\n  --vvd-color-information-400: #2997f0;\n  --vvd-color-information-500: #0276d5;\n  --vvd-color-information-600: #0e65c2;\n  --vvd-color-information-700: #094a9e;\n  --vvd-color-information-800: #0e306d;\n  --vvd-color-information-900: #071939;\n  --vvd-color-information-950: #040d1d;\n  --vvd-color-announcement-50: #ffedfb;\n  --vvd-color-announcement-100: #ffdcf7;\n  --vvd-color-announcement-200: #f8b9e7;\n  --vvd-color-announcement-300: #fb8fd8;\n  --vvd-color-announcement-400: #e560bb;\n  --vvd-color-announcement-500: #d6219c;\n  --vvd-color-announcement-600: #bb1e89;\n  --vvd-color-announcement-700: #8f1669;\n  --vvd-color-announcement-800: #620256;\n  --vvd-color-announcement-900: #32082c;\n  --vvd-color-announcement-950: #1d031a;\n  --vvd-color-success-50: #e1f8e5;\n  --vvd-color-success-100: #cfeed4;\n  --vvd-color-success-200: #86e090;\n  --vvd-color-success-300: #53ca6a;\n  --vvd-color-success-400: #30a849;\n  --vvd-color-success-500: #1c8731;\n  --vvd-color-success-600: #167629;\n  --vvd-color-success-700: #155923;\n  --vvd-color-success-800: #183a1e;\n  --vvd-color-success-900: #0a1e11;\n  --vvd-color-success-950: #060f09;\n  --vvd-color-alert-50: #ffeef2;\n  --vvd-color-alert-100: #fedfdf;\n  --vvd-color-alert-200: #ffbbbb;\n  --vvd-color-alert-300: #fe9696;\n  --vvd-color-alert-400: #f75959;\n  --vvd-color-alert-500: #e61d1d;\n  --vvd-color-alert-600: #cd0d0d;\n  --vvd-color-alert-700: #9f0202;\n  --vvd-color-alert-800: #6e0000;\n  --vvd-color-alert-900: #3e0004;\n  --vvd-color-alert-950: #250004;\n  --vvd-color-neutral-tint-50: #f2f3f9;\n  --vvd-color-neutral-tint-100: #e5e5ec;\n  --vvd-color-neutral-tint-200: #cbcbd9;\n  --vvd-color-neutral-tint-300: #b2b1c7;\n  --vvd-color-neutral-tint-400: #908fae;\n  --vvd-color-neutral-tint-500: #727198;\n  --vvd-color-neutral-tint-600: #63628d;\n  --vvd-color-neutral-tint-700: #494977;\n  --vvd-color-neutral-tint-800: #31304f;\n  --vvd-color-neutral-tint-900: #191828;\n  --vvd-color-neutral-tint-950: #0d0c15;\n  --vvd-color-surface-0dp: #ffffff;\n  --vvd-color-surface-2dp: #ffffff;\n  --vvd-color-surface-4dp: #ffffff;\n  --vvd-color-surface-8dp: #ffffff;\n  --vvd-color-surface-12dp: #ffffff;\n  --vvd-color-surface-16dp: #ffffff;\n  --vvd-color-surface-24dp: #ffffff;\n  --vvd-shadow-surface-0dp: drop-shadow(1px 0px 0px #cccccc) drop-shadow(0px 1px 0px #cccccc) drop-shadow(0px -1px 0px #cccccc) drop-shadow(-1px 0px 0px #cccccc);\n  --vvd-shadow-surface-2dp: drop-shadow(0px 1px 4px #0000001a) drop-shadow(0px 1px 2px #0000000d) drop-shadow(0px 2px 1px #0000000d);\n  --vvd-shadow-surface-4dp: drop-shadow(0px 2px 8px #0000001a) drop-shadow(0px 2px 4px #0000000d) drop-shadow(0px 4px 2px #0000000d);\n  --vvd-shadow-surface-8dp: drop-shadow(0px 2px 16px #0000001a) drop-shadow(0px 8px 8px #0000000d) drop-shadow(0px 4px 4px #0000000d);\n  --vvd-shadow-surface-12dp: drop-shadow(0px 4px 24px #0000001a) drop-shadow(0px 12px 16px #0000000d) drop-shadow(0px 6px 8px #0000000d);\n  --vvd-shadow-surface-16dp: drop-shadow(0px 6px 32px #0000001a) drop-shadow(0px 16px 24px #0000000d) drop-shadow(0px 8px 12px #0000000d);\n  --vvd-shadow-surface-24dp: drop-shadow(0px 12px 48px #00000033) drop-shadow(0px 24px 32px #0000000d) drop-shadow(0px 12px 16px #0000000d);\n  --vvd-neutral-tint-shadow-surface-0dp: drop-shadow(1px 0px 0px #31304f1a) drop-shadow(0px 1px 0px #31304f1a) drop-shadow(0px -1px 0px #31304f1a) drop-shadow(-1px 0px 0px #31304f1a);\n  --vvd-neutral-tint-shadow-surface-2dp: drop-shadow(0px 1px 4px #26044d1a) drop-shadow(0px 1px 2px #26044d0d) drop-shadow(0px 2px 1px #26044d0d);\n  --vvd-neutral-tint-shadow-surface-4dp: drop-shadow(0px 2px 8px #26044d1a) drop-shadow(0px 2px 4px #26044d0d) drop-shadow(0px 4px 2px #26044d0d);\n  --vvd-neutral-tint-shadow-surface-8dp: drop-shadow(0px 2px 16px #26044d1a) drop-shadow(0px 8px 8px #26044d0d) drop-shadow(0px 4px 4px #26044d0d);\n  --vvd-neutral-tint-shadow-surface-12dp: drop-shadow(0px 4px 24px #26044d1a) drop-shadow(0px 12px 16px #26044d0d) drop-shadow(0px 6px 8px #26044d0d);\n  --vvd-neutral-tint-shadow-surface-16dp: drop-shadow(0px 6px 32px #26044d1a) drop-shadow(0px 16px 24px #26044d0d) drop-shadow(0px 8px 12px #26044d0d);\n  --vvd-neutral-tint-shadow-surface-24dp: drop-shadow(0px 12px 48px #26044d33) drop-shadow(0px 24px 32px #26044d0d) drop-shadow(0px 12px 16px #26044d0d);\n  --vvd-typography-headline: 500 calc(var(--vvd-size-font-scale-base, 16px) * 4.125)/1.3333333333333333 Montserrat;\n  --vvd-typography-subtitle: 500 calc(var(--vvd-size-font-scale-base, 16px) * 3.25)/1.3076923076923077 Montserrat;\n  --vvd-typography-heading-1: 500 calc(var(--vvd-size-font-scale-base, 16px) * 2.5)/1.3 Montserrat;\n  --vvd-typography-heading-2: 500 calc(var(--vvd-size-font-scale-base, 16px) * 2)/1.375 Montserrat;\n  --vvd-typography-heading-3: 500 calc(var(--vvd-size-font-scale-base, 16px) * 1.625)/1.3846153846153846 Montserrat;\n  --vvd-typography-heading-4: 500 calc(var(--vvd-size-font-scale-base, 16px) * 1.25)/1.4 Montserrat;\n  --vvd-typography-base: 400 calc(var(--vvd-size-font-scale-base, 16px) * 0.875)/1.4285714285714286 Montserrat;\n  --vvd-typography-base-bold: 600 calc(var(--vvd-size-font-scale-base, 16px) * 0.875)/1.4285714285714286 Montserrat;\n  --vvd-typography-base-code: 400 calc(var(--vvd-size-font-scale-base, 16px) * 0.875)/1.4285714285714286 Roboto Mono;\n  --vvd-typography-base-condensed: 400 calc(var(--vvd-size-font-scale-base, 16px) * 0.75)/1.3333333333333333 Montserrat;\n  --vvd-typography-base-condensed-bold: 600 calc(var(--vvd-size-font-scale-base, 16px) * 0.75)/1.3333333333333333 Montserrat;\n  --vvd-typography-base-extended: 400 calc(var(--vvd-size-font-scale-base, 16px))/1.5 Montserrat;\n  --vvd-typography-base-extended-bold: 600 calc(var(--vvd-size-font-scale-base, 16px))/1.5 Montserrat;\n}\n\n.vvd-theme-alternate, ::part(vvd-theme-alternate) {\n  --vvd-color-scheme: dark;\n  --vvd-color-canvas: #000000;\n  --vvd-color-canvas-text: #ffffff;\n  --vvd-color-neutral-50: #1a1a1a;\n  --vvd-color-neutral-100: #333333;\n  --vvd-color-neutral-200: #4d4d4d;\n  --vvd-color-neutral-300: #666666;\n  --vvd-color-neutral-400: #757575;\n  --vvd-color-neutral-500: #929292;\n  --vvd-color-neutral-600: #b3b3b3;\n  --vvd-color-neutral-700: #cccccc;\n  --vvd-color-neutral-800: #E6E6E6;\n  --vvd-color-neutral-900: #F2F2F2;\n  --vvd-color-neutral-950: #ffffff;\n  --vvd-color-cta-50: #26044d;\n  --vvd-color-cta-100: #440291;\n  --vvd-color-cta-200: #6405d1;\n  --vvd-color-cta-300: #871eff;\n  --vvd-color-cta-400: #9941ff;\n  --vvd-color-cta-500: #b27bf2;\n  --vvd-color-cta-600: #cba1fa;\n  --vvd-color-cta-700: #dcc1fc;\n  --vvd-color-cta-800: #ece2fa;\n  --vvd-color-cta-900: #f5f0fd;\n  --vvd-color-warning-50: #2A1502;\n  --vvd-color-warning-100: #522801;\n  --vvd-color-warning-200: #803807;\n  --vvd-color-warning-300: #A64C03;\n  --vvd-color-warning-400: #be5702;\n  --vvd-color-warning-500: #e07902;\n  --vvd-color-warning-600: #fa9f00;\n  --vvd-color-warning-700: #FACC4B;\n  --vvd-color-warning-800: #FDEAA0;\n  --vvd-color-warning-900: #FDF5D4;\n  --vvd-color-information-50: #071939;\n  --vvd-color-information-100: #0e306d;\n  --vvd-color-information-200: #094a9e;\n  --vvd-color-information-300: #0e65c2;\n  --vvd-color-information-400: #0276d5;\n  --vvd-color-information-500: #2997f0;\n  --vvd-color-information-600: #65baff;\n  --vvd-color-information-700: #9dd2fe;\n  --vvd-color-information-800: #d3e9fc;\n  --vvd-color-information-900: #e8f4fb;\n  --vvd-color-announcement-50: #32082c;\n  --vvd-color-announcement-100: #620256;\n  --vvd-color-announcement-200: #8f1669;\n  --vvd-color-announcement-300: #bb1e89;\n  --vvd-color-announcement-400: #d6219c;\n  --vvd-color-announcement-500: #e560bb;\n  --vvd-color-announcement-600: #fb8fd8;\n  --vvd-color-announcement-700: #f8b9e7;\n  --vvd-color-announcement-800: #ffdcf7;\n  --vvd-color-announcement-900: #ffedfb;\n  --vvd-color-success-50: #0a1e11;\n  --vvd-color-success-100: #183a1e;\n  --vvd-color-success-200: #155923;\n  --vvd-color-success-300: #167629;\n  --vvd-color-success-400: #1c8731;\n  --vvd-color-success-500: #30a849;\n  --vvd-color-success-600: #53ca6a;\n  --vvd-color-success-700: #86e090;\n  --vvd-color-success-800: #cfeed4;\n  --vvd-color-success-900: #e1f8e5;\n  --vvd-color-alert-50: #3e0004;\n  --vvd-color-alert-100: #6e0000;\n  --vvd-color-alert-200: #9f0202;\n  --vvd-color-alert-300: #cd0d0d;\n  --vvd-color-alert-400: #e61d1d;\n  --vvd-color-alert-500: #f75959;\n  --vvd-color-alert-600: #fe9696;\n  --vvd-color-alert-700: #ffbbbb;\n  --vvd-color-alert-800: #fedfdf;\n  --vvd-color-alert-900: #ffeef2;\n  --vvd-color-neutral-tint-50: #191828;\n  --vvd-color-neutral-tint-100: #31304f;\n  --vvd-color-neutral-tint-200: #494977;\n  --vvd-color-neutral-tint-300: #63628d;\n  --vvd-color-neutral-tint-400: #727198;\n  --vvd-color-neutral-tint-500: #908fae;\n  --vvd-color-neutral-tint-600: #b2b1c7;\n  --vvd-color-neutral-tint-700: #cbcbd9;\n  --vvd-color-neutral-tint-800: #e5e5ec;\n  --vvd-color-neutral-tint-900: #f2f3f9;\n  --vvd-color-surface-0dp: #0d0d0d;\n  --vvd-color-surface-2dp: linear-gradient(#ffffff0f, #ffffff0f), #0d0d0d;\n  --vvd-color-surface-4dp: linear-gradient(#ffffff14, #ffffff14), #0d0d0d;\n  --vvd-color-surface-8dp: linear-gradient(#ffffff1a, #ffffff1a), #0d0d0d;\n  --vvd-color-surface-12dp: linear-gradient(#ffffff1f, #ffffff1f), #0d0d0d;\n  --vvd-color-surface-16dp: linear-gradient(#ffffff24, #ffffff24), #0d0d0d;\n  --vvd-color-surface-24dp: linear-gradient(#ffffff29, #ffffff29), #0d0d0d;\n  --vvd-shadow-surface-0dp: drop-shadow(1px 0px 0px #4d4d4d) drop-shadow(0px 1px 0px #4d4d4d) drop-shadow(0px -1px 0px #4d4d4d) drop-shadow(-1px 0px 0px #4d4d4d);\n  --vvd-shadow-surface-2dp: drop-shadow(0px 1px 4px #00000040) drop-shadow(0px 1px 2px #00000040) drop-shadow(0px 2px 1px #00000040);\n  --vvd-shadow-surface-4dp: drop-shadow(0px 2px 8px #00000040) drop-shadow(0px 2px 4px #00000040) drop-shadow(0px 4px 4px #00000040);\n  --vvd-shadow-surface-8dp: drop-shadow(0px 2px 16px #00000040) drop-shadow(0px 8px 8px #00000040) drop-shadow(0px 4px 4px #00000040);\n  --vvd-shadow-surface-12dp: drop-shadow(0px 4px 24px #00000040) drop-shadow(0px 12px 16px #00000040) drop-shadow(0px 6px 8px #00000040);\n  --vvd-shadow-surface-16dp: drop-shadow(0px 6px 32px #00000040) drop-shadow(0px 16px 24px #00000040) drop-shadow(0px 8px 12px #00000040);\n  --vvd-shadow-surface-24dp: drop-shadow(0px 12px 48px #00000040) drop-shadow(0px 24px 32px #00000040) drop-shadow(0px 12px 16px #00000040);\n  --vvd-neutral-tint-color-surface-0dp: #191828;\n  --vvd-neutral-tint-color-surface-2dp: linear-gradient(#ffffff08, #ffffff08), #191828;\n  --vvd-neutral-tint-color-surface-4dp: linear-gradient(#ffffff0f, #ffffff0f), #191828;\n  --vvd-neutral-tint-color-surface-8dp: linear-gradient(#ffffff14, #ffffff14), #191828;\n  --vvd-neutral-tint-color-surface-12dp: linear-gradient(#ffffff1f, #ffffff1f), #191828;\n  --vvd-neutral-tint-color-surface-16dp: linear-gradient(#ffffff24, #ffffff24), #191828;\n  --vvd-neutral-tint-color-surface-24dp: linear-gradient(#ffffff33, #ffffff33), #191828;\n  --vvd-neutral-tint-shadow-surface-0dp: drop-shadow(1px 0px 0px #f2f3f91a) drop-shadow(0px 1px 0px #f2f3f91a) drop-shadow(0px -1px 0px #f2f3f91a) drop-shadow(-1px 0px 0px #f2f3f91a);\n  --vvd-neutral-tint-shadow-surface-2dp: none;\n  --vvd-neutral-tint-shadow-surface-4dp: none;\n  --vvd-neutral-tint-shadow-surface-8dp: none;\n  --vvd-neutral-tint-shadow-surface-12dp: none;\n  --vvd-neutral-tint-shadow-surface-16dp: none;\n  --vvd-neutral-tint-shadow-surface-24dp: none;\n}\n";

const tokensThemeDarkCss = "/**\n * Do not edit directly\n * Generated on Thu, 14 Aug 2025 08:21:42 GMT\n */\n/**\n * Do not edit directly\n * Generated on Thu, 14 Aug 2025 08:21:42 GMT\n */\n/**\n * Do not edit directly\n * Generated on Thu, 14 Aug 2025 08:21:42 GMT\n */\n/**\n * Do not edit directly\n * Generated on Thu, 14 Aug 2025 08:21:43 GMT\n */\n@property --vvd-size-density {\n  syntax: \"<integer>\";\n  inherits: true;\n  initial-value: 0;\n}\n@property --vvd-size-font-scale-base {\n  syntax: \"<length>\";\n  inherits: true;\n  initial-value: 16px;\n}\n.vvd-root, ::part(vvd-root) {\n  --vvd-color-scheme: dark;\n  --vvd-color-canvas: #000000;\n  --vvd-color-canvas-text: #ffffff;\n  --vvd-color-neutral-50: #1a1a1a;\n  --vvd-color-neutral-100: #333333;\n  --vvd-color-neutral-200: #4d4d4d;\n  --vvd-color-neutral-300: #666666;\n  --vvd-color-neutral-400: #757575;\n  --vvd-color-neutral-500: #929292;\n  --vvd-color-neutral-600: #b3b3b3;\n  --vvd-color-neutral-700: #cccccc;\n  --vvd-color-neutral-800: #E6E6E6;\n  --vvd-color-neutral-900: #F2F2F2;\n  --vvd-color-neutral-950: #ffffff;\n  --vvd-color-cta-50: #26044d;\n  --vvd-color-cta-100: #440291;\n  --vvd-color-cta-200: #6405d1;\n  --vvd-color-cta-300: #871eff;\n  --vvd-color-cta-400: #9941ff;\n  --vvd-color-cta-500: #b27bf2;\n  --vvd-color-cta-600: #cba1fa;\n  --vvd-color-cta-700: #dcc1fc;\n  --vvd-color-cta-800: #ece2fa;\n  --vvd-color-cta-900: #f5f0fd;\n  --vvd-color-warning-50: #2A1502;\n  --vvd-color-warning-100: #522801;\n  --vvd-color-warning-200: #803807;\n  --vvd-color-warning-300: #A64C03;\n  --vvd-color-warning-400: #be5702;\n  --vvd-color-warning-500: #e07902;\n  --vvd-color-warning-600: #fa9f00;\n  --vvd-color-warning-700: #FACC4B;\n  --vvd-color-warning-800: #FDEAA0;\n  --vvd-color-warning-900: #FDF5D4;\n  --vvd-color-information-50: #071939;\n  --vvd-color-information-100: #0e306d;\n  --vvd-color-information-200: #094a9e;\n  --vvd-color-information-300: #0e65c2;\n  --vvd-color-information-400: #0276d5;\n  --vvd-color-information-500: #2997f0;\n  --vvd-color-information-600: #65baff;\n  --vvd-color-information-700: #9dd2fe;\n  --vvd-color-information-800: #d3e9fc;\n  --vvd-color-information-900: #e8f4fb;\n  --vvd-color-announcement-50: #32082c;\n  --vvd-color-announcement-100: #620256;\n  --vvd-color-announcement-200: #8f1669;\n  --vvd-color-announcement-300: #bb1e89;\n  --vvd-color-announcement-400: #d6219c;\n  --vvd-color-announcement-500: #e560bb;\n  --vvd-color-announcement-600: #fb8fd8;\n  --vvd-color-announcement-700: #f8b9e7;\n  --vvd-color-announcement-800: #ffdcf7;\n  --vvd-color-announcement-900: #ffedfb;\n  --vvd-color-success-50: #0a1e11;\n  --vvd-color-success-100: #183a1e;\n  --vvd-color-success-200: #155923;\n  --vvd-color-success-300: #167629;\n  --vvd-color-success-400: #1c8731;\n  --vvd-color-success-500: #30a849;\n  --vvd-color-success-600: #53ca6a;\n  --vvd-color-success-700: #86e090;\n  --vvd-color-success-800: #cfeed4;\n  --vvd-color-success-900: #e1f8e5;\n  --vvd-color-alert-50: #3e0004;\n  --vvd-color-alert-100: #6e0000;\n  --vvd-color-alert-200: #9f0202;\n  --vvd-color-alert-300: #cd0d0d;\n  --vvd-color-alert-400: #e61d1d;\n  --vvd-color-alert-500: #f75959;\n  --vvd-color-alert-600: #fe9696;\n  --vvd-color-alert-700: #ffbbbb;\n  --vvd-color-alert-800: #fedfdf;\n  --vvd-color-alert-900: #ffeef2;\n  --vvd-color-neutral-tint-50: #191828;\n  --vvd-color-neutral-tint-100: #31304f;\n  --vvd-color-neutral-tint-200: #494977;\n  --vvd-color-neutral-tint-300: #63628d;\n  --vvd-color-neutral-tint-400: #727198;\n  --vvd-color-neutral-tint-500: #908fae;\n  --vvd-color-neutral-tint-600: #b2b1c7;\n  --vvd-color-neutral-tint-700: #cbcbd9;\n  --vvd-color-neutral-tint-800: #e5e5ec;\n  --vvd-color-neutral-tint-900: #f2f3f9;\n  --vvd-color-surface-0dp: #0d0d0d;\n  --vvd-color-surface-2dp: linear-gradient(#ffffff0f, #ffffff0f), #0d0d0d;\n  --vvd-color-surface-4dp: linear-gradient(#ffffff14, #ffffff14), #0d0d0d;\n  --vvd-color-surface-8dp: linear-gradient(#ffffff1a, #ffffff1a), #0d0d0d;\n  --vvd-color-surface-12dp: linear-gradient(#ffffff1f, #ffffff1f), #0d0d0d;\n  --vvd-color-surface-16dp: linear-gradient(#ffffff24, #ffffff24), #0d0d0d;\n  --vvd-color-surface-24dp: linear-gradient(#ffffff29, #ffffff29), #0d0d0d;\n  --vvd-shadow-surface-0dp: drop-shadow(1px 0px 0px #4d4d4d) drop-shadow(0px 1px 0px #4d4d4d) drop-shadow(0px -1px 0px #4d4d4d) drop-shadow(-1px 0px 0px #4d4d4d);\n  --vvd-shadow-surface-2dp: drop-shadow(0px 1px 4px #00000040) drop-shadow(0px 1px 2px #00000040) drop-shadow(0px 2px 1px #00000040);\n  --vvd-shadow-surface-4dp: drop-shadow(0px 2px 8px #00000040) drop-shadow(0px 2px 4px #00000040) drop-shadow(0px 4px 4px #00000040);\n  --vvd-shadow-surface-8dp: drop-shadow(0px 2px 16px #00000040) drop-shadow(0px 8px 8px #00000040) drop-shadow(0px 4px 4px #00000040);\n  --vvd-shadow-surface-12dp: drop-shadow(0px 4px 24px #00000040) drop-shadow(0px 12px 16px #00000040) drop-shadow(0px 6px 8px #00000040);\n  --vvd-shadow-surface-16dp: drop-shadow(0px 6px 32px #00000040) drop-shadow(0px 16px 24px #00000040) drop-shadow(0px 8px 12px #00000040);\n  --vvd-shadow-surface-24dp: drop-shadow(0px 12px 48px #00000040) drop-shadow(0px 24px 32px #00000040) drop-shadow(0px 12px 16px #00000040);\n  --vvd-neutral-tint-color-surface-0dp: #191828;\n  --vvd-neutral-tint-color-surface-2dp: linear-gradient(#ffffff08, #ffffff08), #191828;\n  --vvd-neutral-tint-color-surface-4dp: linear-gradient(#ffffff0f, #ffffff0f), #191828;\n  --vvd-neutral-tint-color-surface-8dp: linear-gradient(#ffffff14, #ffffff14), #191828;\n  --vvd-neutral-tint-color-surface-12dp: linear-gradient(#ffffff1f, #ffffff1f), #191828;\n  --vvd-neutral-tint-color-surface-16dp: linear-gradient(#ffffff24, #ffffff24), #191828;\n  --vvd-neutral-tint-color-surface-24dp: linear-gradient(#ffffff33, #ffffff33), #191828;\n  --vvd-neutral-tint-shadow-surface-0dp: drop-shadow(1px 0px 0px #f2f3f91a) drop-shadow(0px 1px 0px #f2f3f91a) drop-shadow(0px -1px 0px #f2f3f91a) drop-shadow(-1px 0px 0px #f2f3f91a);\n  --vvd-neutral-tint-shadow-surface-2dp: none;\n  --vvd-neutral-tint-shadow-surface-4dp: none;\n  --vvd-neutral-tint-shadow-surface-8dp: none;\n  --vvd-neutral-tint-shadow-surface-12dp: none;\n  --vvd-neutral-tint-shadow-surface-16dp: none;\n  --vvd-neutral-tint-shadow-surface-24dp: none;\n  --vvd-typography-headline: 500 calc(var(--vvd-size-font-scale-base, 16px) * 4.125)/1.3333333333333333 Montserrat;\n  --vvd-typography-subtitle: 500 calc(var(--vvd-size-font-scale-base, 16px) * 3.25)/1.3076923076923077 Montserrat;\n  --vvd-typography-heading-1: 500 calc(var(--vvd-size-font-scale-base, 16px) * 2.5)/1.3 Montserrat;\n  --vvd-typography-heading-2: 500 calc(var(--vvd-size-font-scale-base, 16px) * 2)/1.375 Montserrat;\n  --vvd-typography-heading-3: 500 calc(var(--vvd-size-font-scale-base, 16px) * 1.625)/1.3846153846153846 Montserrat;\n  --vvd-typography-heading-4: 500 calc(var(--vvd-size-font-scale-base, 16px) * 1.25)/1.4 Montserrat;\n  --vvd-typography-base: 400 calc(var(--vvd-size-font-scale-base, 16px) * 0.875)/1.4285714285714286 Montserrat;\n  --vvd-typography-base-bold: 600 calc(var(--vvd-size-font-scale-base, 16px) * 0.875)/1.4285714285714286 Montserrat;\n  --vvd-typography-base-code: 400 calc(var(--vvd-size-font-scale-base, 16px) * 0.875)/1.4285714285714286 Roboto Mono;\n  --vvd-typography-base-condensed: 400 calc(var(--vvd-size-font-scale-base, 16px) * 0.75)/1.3333333333333333 Montserrat;\n  --vvd-typography-base-condensed-bold: 600 calc(var(--vvd-size-font-scale-base, 16px) * 0.75)/1.3333333333333333 Montserrat;\n  --vvd-typography-base-extended: 400 calc(var(--vvd-size-font-scale-base, 16px))/1.5 Montserrat;\n  --vvd-typography-base-extended-bold: 600 calc(var(--vvd-size-font-scale-base, 16px))/1.5 Montserrat;\n}\n\n.vvd-theme-alternate, ::part(vvd-theme-alternate) {\n  --vvd-color-scheme: light;\n  --vvd-color-canvas: #ffffff;\n  --vvd-color-canvas-text: #0d0d0d;\n  --vvd-color-neutral-50: #F2F2F2;\n  --vvd-color-neutral-100: #E6E6E6;\n  --vvd-color-neutral-200: #cccccc;\n  --vvd-color-neutral-300: #b3b3b3;\n  --vvd-color-neutral-400: #929292;\n  --vvd-color-neutral-500: #757575;\n  --vvd-color-neutral-600: #666666;\n  --vvd-color-neutral-700: #4d4d4d;\n  --vvd-color-neutral-800: #333333;\n  --vvd-color-neutral-900: #1a1a1a;\n  --vvd-color-neutral-950: #0d0d0d;\n  --vvd-color-cta-50: #f5f0fd;\n  --vvd-color-cta-100: #ece2fa;\n  --vvd-color-cta-200: #dcc1fc;\n  --vvd-color-cta-300: #cba1fa;\n  --vvd-color-cta-400: #b27bf2;\n  --vvd-color-cta-500: #9941ff;\n  --vvd-color-cta-600: #871eff;\n  --vvd-color-cta-700: #6405d1;\n  --vvd-color-cta-800: #440291;\n  --vvd-color-cta-900: #26044d;\n  --vvd-color-cta-950: #140623;\n  --vvd-color-warning-50: #FDF5D4;\n  --vvd-color-warning-100: #FDEAA0;\n  --vvd-color-warning-200: #FACC4B;\n  --vvd-color-warning-300: #fa9f00;\n  --vvd-color-warning-400: #e07902;\n  --vvd-color-warning-500: #be5702;\n  --vvd-color-warning-600: #A64C03;\n  --vvd-color-warning-700: #893000;\n  --vvd-color-warning-800: #522801;\n  --vvd-color-warning-900: #2A1502;\n  --vvd-color-warning-950: #150B01;\n  --vvd-color-information-50: #e8f4fb;\n  --vvd-color-information-100: #d3e9fc;\n  --vvd-color-information-200: #9dd2fe;\n  --vvd-color-information-300: #65baff;\n  --vvd-color-information-400: #2997f0;\n  --vvd-color-information-500: #0276d5;\n  --vvd-color-information-600: #0e65c2;\n  --vvd-color-information-700: #094a9e;\n  --vvd-color-information-800: #0e306d;\n  --vvd-color-information-900: #071939;\n  --vvd-color-information-950: #040d1d;\n  --vvd-color-announcement-50: #ffedfb;\n  --vvd-color-announcement-100: #ffdcf7;\n  --vvd-color-announcement-200: #f8b9e7;\n  --vvd-color-announcement-300: #fb8fd8;\n  --vvd-color-announcement-400: #e560bb;\n  --vvd-color-announcement-500: #d6219c;\n  --vvd-color-announcement-600: #bb1e89;\n  --vvd-color-announcement-700: #8f1669;\n  --vvd-color-announcement-800: #620256;\n  --vvd-color-announcement-900: #32082c;\n  --vvd-color-announcement-950: #1d031a;\n  --vvd-color-success-50: #e1f8e5;\n  --vvd-color-success-100: #cfeed4;\n  --vvd-color-success-200: #86e090;\n  --vvd-color-success-300: #53ca6a;\n  --vvd-color-success-400: #30a849;\n  --vvd-color-success-500: #1c8731;\n  --vvd-color-success-600: #167629;\n  --vvd-color-success-700: #155923;\n  --vvd-color-success-800: #183a1e;\n  --vvd-color-success-900: #0a1e11;\n  --vvd-color-success-950: #060f09;\n  --vvd-color-alert-50: #ffeef2;\n  --vvd-color-alert-100: #fedfdf;\n  --vvd-color-alert-200: #ffbbbb;\n  --vvd-color-alert-300: #fe9696;\n  --vvd-color-alert-400: #f75959;\n  --vvd-color-alert-500: #e61d1d;\n  --vvd-color-alert-600: #cd0d0d;\n  --vvd-color-alert-700: #9f0202;\n  --vvd-color-alert-800: #6e0000;\n  --vvd-color-alert-900: #3e0004;\n  --vvd-color-alert-950: #250004;\n  --vvd-color-neutral-tint-50: #f2f3f9;\n  --vvd-color-neutral-tint-100: #e5e5ec;\n  --vvd-color-neutral-tint-200: #cbcbd9;\n  --vvd-color-neutral-tint-300: #b2b1c7;\n  --vvd-color-neutral-tint-400: #908fae;\n  --vvd-color-neutral-tint-500: #727198;\n  --vvd-color-neutral-tint-600: #63628d;\n  --vvd-color-neutral-tint-700: #494977;\n  --vvd-color-neutral-tint-800: #31304f;\n  --vvd-color-neutral-tint-900: #191828;\n  --vvd-color-neutral-tint-950: #0d0c15;\n  --vvd-color-surface-0dp: #ffffff;\n  --vvd-color-surface-2dp: #ffffff;\n  --vvd-color-surface-4dp: #ffffff;\n  --vvd-color-surface-8dp: #ffffff;\n  --vvd-color-surface-12dp: #ffffff;\n  --vvd-color-surface-16dp: #ffffff;\n  --vvd-color-surface-24dp: #ffffff;\n  --vvd-shadow-surface-0dp: drop-shadow(1px 0px 0px #cccccc) drop-shadow(0px 1px 0px #cccccc) drop-shadow(0px -1px 0px #cccccc) drop-shadow(-1px 0px 0px #cccccc);\n  --vvd-shadow-surface-2dp: drop-shadow(0px 1px 4px #0000001a) drop-shadow(0px 1px 2px #0000000d) drop-shadow(0px 2px 1px #0000000d);\n  --vvd-shadow-surface-4dp: drop-shadow(0px 2px 8px #0000001a) drop-shadow(0px 2px 4px #0000000d) drop-shadow(0px 4px 2px #0000000d);\n  --vvd-shadow-surface-8dp: drop-shadow(0px 2px 16px #0000001a) drop-shadow(0px 8px 8px #0000000d) drop-shadow(0px 4px 4px #0000000d);\n  --vvd-shadow-surface-12dp: drop-shadow(0px 4px 24px #0000001a) drop-shadow(0px 12px 16px #0000000d) drop-shadow(0px 6px 8px #0000000d);\n  --vvd-shadow-surface-16dp: drop-shadow(0px 6px 32px #0000001a) drop-shadow(0px 16px 24px #0000000d) drop-shadow(0px 8px 12px #0000000d);\n  --vvd-shadow-surface-24dp: drop-shadow(0px 12px 48px #00000033) drop-shadow(0px 24px 32px #0000000d) drop-shadow(0px 12px 16px #0000000d);\n  --vvd-neutral-tint-shadow-surface-0dp: drop-shadow(1px 0px 0px #31304f1a) drop-shadow(0px 1px 0px #31304f1a) drop-shadow(0px -1px 0px #31304f1a) drop-shadow(-1px 0px 0px #31304f1a);\n  --vvd-neutral-tint-shadow-surface-2dp: drop-shadow(0px 1px 4px #26044d1a) drop-shadow(0px 1px 2px #26044d0d) drop-shadow(0px 2px 1px #26044d0d);\n  --vvd-neutral-tint-shadow-surface-4dp: drop-shadow(0px 2px 8px #26044d1a) drop-shadow(0px 2px 4px #26044d0d) drop-shadow(0px 4px 2px #26044d0d);\n  --vvd-neutral-tint-shadow-surface-8dp: drop-shadow(0px 2px 16px #26044d1a) drop-shadow(0px 8px 8px #26044d0d) drop-shadow(0px 4px 4px #26044d0d);\n  --vvd-neutral-tint-shadow-surface-12dp: drop-shadow(0px 4px 24px #26044d1a) drop-shadow(0px 12px 16px #26044d0d) drop-shadow(0px 6px 8px #26044d0d);\n  --vvd-neutral-tint-shadow-surface-16dp: drop-shadow(0px 6px 32px #26044d1a) drop-shadow(0px 16px 24px #26044d0d) drop-shadow(0px 8px 12px #26044d0d);\n  --vvd-neutral-tint-shadow-surface-24dp: drop-shadow(0px 12px 48px #26044d33) drop-shadow(0px 24px 32px #26044d0d) drop-shadow(0px 12px 16px #26044d0d);\n}\n";

const fontsSpeziaVariableCss = "@font-face {\n  font-display: block;\n  font-family: SpeziaCompleteVariableUpright;\n  font-stretch: 50% 200%;\n  font-weight: 1 1000;\n  src: url(\"https://fonts.resources.vonage.com/fonts/v2/SpeziaCompleteVariableUprightWeb.woff2\") format(\"woff2\");\n}\n@font-face {\n  font-display: block;\n  font-family: SpeziaMonoCompleteVariable;\n  font-stretch: 50% 200%;\n  font-weight: 1 1000;\n  src: url(\"https://fonts.resources.vonage.com/fonts/v2/SpeziaMonoCompleteVariableWeb.woff2\") format(\"woff2\");\n}\n.vvd-root {\n  --vvd-typography-headline: 500 condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px) * 4.125) / 1.3333333333333333\n  \tspeziacompletevariableupright;\n  --vvd-typography-subtitle: 500 condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px) * 3.25) / 1.3076923076923077\n  \tspeziacompletevariableupright;\n  --vvd-typography-heading-1: 500 condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px) * 2.5) / 1.3\n  \tspeziacompletevariableupright;\n  --vvd-typography-heading-2: 500 condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px) * 2) / 1.375\n  \tspeziacompletevariableupright;\n  --vvd-typography-heading-3: 500 condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px) * 1.625) / 1.3846153846153846\n  \tspeziacompletevariableupright;\n  --vvd-typography-heading-4: 500 condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px) * 1.25) / 1.4\n  \tspeziacompletevariableupright;\n  --vvd-typography-base: 400 ultra-condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px) * 0.875) / 1.4285714285714286\n  \tspeziacompletevariableupright;\n  --vvd-typography-base-bold: 600 ultra-condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px) * 0.875) / 1.4285714285714286\n  \tspeziacompletevariableupright;\n  --vvd-typography-base-code: 400 ultra-condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px) * 0.875) / 1.4285714285714286\n  \tspeziamonocompletevariable;\n  --vvd-typography-base-condensed: 400 ultra-condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px) * 0.75) / 1.3333333333333333\n  \tspeziacompletevariableupright;\n  --vvd-typography-base-condensed-bold: 600 ultra-condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px) * 0.75) / 1.3333333333333333\n  \tspeziacompletevariableupright;\n  --vvd-typography-base-extended: 400 ultra-condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px)) / 1.5\n  \tspeziacompletevariableupright;\n  --vvd-typography-base-extended-bold: 600 ultra-condensed\n  \tcalc(var(--vvd-size-font-scale-base, 16px)) / 1.5\n  \tspeziacompletevariableupright;\n}\n";

const tokensVivid2CompatCss = "/**\n * Do not edit directly\n * Generated on Thu, 14 Aug 2025 08:21:42 GMT\n */\n.vvd-root, ::part(vvd-root),\n.vvd-theme-alternate, ::part(vvd-theme-alternate) {\n  --_vvd3--vvd-color-neutral-50: var(\n  \t--vvd-color-neutral-50\n  );\n}\n\n.vvd-component:not(.vvd-theme-alternate) {\n  --vvd-color-neutral-50: var(--_vvd3--vvd-color-neutral-50);\n}\n";

const coreThemeCss = "/**\n * Do not edit directly\n * Generated on Thu, 14 Aug 2025 08:21:42 GMT\n */\n.vvd-root {\n  background-color: var(--vvd-color-canvas);\n  color: var(--vvd-color-canvas-text);\n  color-scheme: var(--vvd-color-scheme);\n}\n\n.vvd-scrollbar {\n  --scrollbar-track-color: transparent;\n  --scrollbar-thumb-color: color-mix(in srgb, var(--vvd-color-neutral-950), transparent 70%);\n}\n.vvd-scrollbar {\n  scrollbar-color: var(--scrollbar-thumb-color) var(--scrollbar-track-color);\n  scrollbar-width: thin;\n}\n.vvd-scrollbar ::-webkit-scrollbar {\n  width: 4px;\n}\n.vvd-scrollbar ::-webkit-scrollbar-track {\n  background: var(--scrollbar-track-color);\n}\n.vvd-scrollbar ::-webkit-scrollbar-thumb {\n  border: 0;\n  border-radius: 4px;\n  background-color: var(--scrollbar-fallback-track-color, var(--scrollbar-thumb-color));\n}\n\n.vvd-neutral-tint-elevation {\n  --_vvd-tinted-color-surface-0dp: var(--vvd-neutral-tint-color-surface-0dp);\n  --_vvd-tinted-color-surface-2dp: var(--vvd-neutral-tint-color-surface-2dp);\n  --_vvd-tinted-color-surface-4dp: var(--vvd-neutral-tint-color-surface-4dp);\n  --_vvd-tinted-color-surface-8dp: var(--vvd-neutral-tint-color-surface-8dp);\n  --_vvd-tinted-color-surface-12dp: var(--vvd-neutral-tint-color-surface-12dp);\n  --_vvd-tinted-color-surface-16dp: var(--vvd-neutral-tint-color-surface-16dp);\n  --_vvd-tinted-color-surface-24dp: var(--vvd-neutral-tint-color-surface-24dp);\n  --_vvd-tinted-shadow-surface-0dp: var(--vvd-neutral-tint-shadow-surface-0dp);\n  --_vvd-tinted-shadow-surface-2dp: var(--vvd-neutral-tint-shadow-surface-2dp);\n  --_vvd-tinted-shadow-surface-4dp: var(--vvd-neutral-tint-shadow-surface-4dp);\n  --_vvd-tinted-shadow-surface-8dp: var(--vvd-neutral-tint-shadow-surface-8dp);\n  --_vvd-tinted-shadow-surface-12dp: var(\n  \t--vvd-neutral-tint-shadow-surface-12dp\n  );\n  --_vvd-tinted-shadow-surface-16dp: var(\n  \t--vvd-neutral-tint-shadow-surface-16dp\n  );\n  --_vvd-tinted-shadow-surface-24dp: var(\n  \t--vvd-neutral-tint-shadow-surface-24dp\n  );\n}\n.vvd-neutral-tint-elevation > * {\n  --_vvd-tinted-color-surface-0dp: var(--vvd-color-surface-0dp);\n  --_vvd-tinted-color-surface-2dp: var(--vvd-color-surface-2dp);\n  --_vvd-tinted-color-surface-4dp: var(--vvd-color-surface-4dp);\n  --_vvd-tinted-color-surface-8dp: var(--vvd-color-surface-8dp);\n  --_vvd-tinted-color-surface-12dp: var(--vvd-color-surface-12dp);\n  --_vvd-tinted-color-surface-16dp: var(--vvd-color-surface-16dp);\n  --_vvd-tinted-color-surface-24dp: var(--vvd-color-surface-24dp);\n  --_vvd-tinted-shadow-surface-0dp: var(--vvd-shadow-surface-0dp);\n  --_vvd-tinted-shadow-surface-2dp: var(--vvd-shadow-surface-2dp);\n  --_vvd-tinted-shadow-surface-4dp: var(--vvd-shadow-surface-4dp);\n  --_vvd-tinted-shadow-surface-8dp: var(--vvd-shadow-surface-8dp);\n  --_vvd-tinted-shadow-surface-12dp: var(--vvd-shadow-surface-12dp);\n  --_vvd-tinted-shadow-surface-16dp: var(--vvd-shadow-surface-16dp);\n  --_vvd-tinted-shadow-surface-24dp: var(--vvd-shadow-surface-24dp);\n}\n";

const coreTypographyCss = "/**\n * Do not edit directly\n * Generated on Thu, 14 Aug 2025 08:21:42 GMT\n */\n.vvd-root:root {\n  --vvd-size-font-scale-base: 1rem;\n  font-size: unset;\n}\n.vvd-root:root > body {\n  font: var(--vvd-typography-base);\n  font-feature-settings: \"kern\"; /* turns on kerning */\n  -webkit-font-smoothing: antialiased; /* apply font anti-aliasing for Webkit on OSX */\n  -moz-osx-font-smoothing: grayscale; /* apply font anti-aliasing for Firefox on OSX */\n  text-rendering: optimizeLegibility; /* emphasise on legibility when rendering, turns on ligatures and kerning */\n}\n.vvd-root:not(:root) {\n  font: var(--vvd-typography-base);\n  font-feature-settings: \"kern\"; /* turns on kerning */\n  -webkit-font-smoothing: antialiased; /* apply font anti-aliasing for Webkit on OSX */\n  -moz-osx-font-smoothing: grayscale; /* apply font anti-aliasing for Firefox on OSX */\n  text-rendering: optimizeLegibility; /* emphasise on legibility when rendering, turns on ligatures and kerning */\n}\n.vvd-root p,\n.vvd-root .font-base {\n  font: var(--vvd-typography-base);\n  margin-block: 16px;\n}\n.vvd-root p.tight,\n.vvd-root .font-base.tight {\n  margin-block: 0;\n}\n.vvd-root .font-base-bold {\n  font: var(--vvd-typography-base-bold);\n}\n.vvd-root .font-base-condensed {\n  font: var(--vvd-typography-base-condensed);\n}\n.vvd-root .font-base-condensed-bold {\n  font: var(--vvd-typography-base-condensed-bold);\n}\n.vvd-root .font-base-extended {\n  font: var(--vvd-typography-base-extended);\n}\n.vvd-root .font-base-extended-bold {\n  font: var(--vvd-typography-base-extended-bold);\n}\n.vvd-root .font-base-code {\n  font: var(--vvd-typography-base-code);\n}\n.vvd-root b, .vvd-root strong {\n  font-weight: 600;\n}\n.vvd-root pre, .vvd-root var, .vvd-root code, .vvd-root kbd, .vvd-root samp {\n  font: var(--vvd-typography-base-code);\n}\n.vvd-root .headline {\n  font: var(--vvd-typography-headline);\n  margin-block: 40px;\n}\n.vvd-root .subtitle {\n  font: var(--vvd-typography-subtitle);\n  margin-block: 40px;\n}\n.vvd-root h1,\n.vvd-root .heading1 {\n  font: var(--vvd-typography-heading-1);\n  margin-block: 32px;\n}\n.vvd-root h2,\n.vvd-root .heading2 {\n  font: var(--vvd-typography-heading-2);\n  margin-block: 32px;\n}\n.vvd-root h3,\n.vvd-root .heading3 {\n  font: var(--vvd-typography-heading-3);\n  margin-block: 24px;\n}\n.vvd-root h4,\n.vvd-root .heading4 {\n  font: var(--vvd-typography-heading-4);\n  margin-block: 24px;\n}\n.vvd-root .headline b, .vvd-root .headline strong,\n.vvd-root .subtitle b,\n.vvd-root .subtitle strong,\n.vvd-root h1 b,\n.vvd-root h1 strong,\n.vvd-root .heading1 b,\n.vvd-root .heading1 strong,\n.vvd-root h2 b,\n.vvd-root h2 strong,\n.vvd-root .heading2 b,\n.vvd-root .heading2 strong,\n.vvd-root h3 b,\n.vvd-root h3 strong,\n.vvd-root .heading3 b,\n.vvd-root .heading3 strong,\n.vvd-root h4 b,\n.vvd-root h4 strong,\n.vvd-root .heading4 b,\n.vvd-root .heading4 strong {\n  font-weight: 500;\n}\n.vvd-root .headline.tight,\n.vvd-root .subtitle.tight,\n.vvd-root h1.tight,\n.vvd-root .heading1.tight,\n.vvd-root h2.tight,\n.vvd-root .heading2.tight,\n.vvd-root h3.tight,\n.vvd-root .heading3.tight,\n.vvd-root h4.tight,\n.vvd-root .heading4.tight {\n  margin-block: 0;\n}\n.vvd-root small,\n.vvd-root figcaption {\n  font: var(--vvd-typography-base-condensed);\n}\n.vvd-root sub,\n.vvd-root sup {\n  position: relative;\n  font: var(--vvd-typography-base-condensed);\n  font-size: 75%;\n  line-height: 0;\n  vertical-align: baseline;\n}\n.vvd-root sub {\n  bottom: -0.25em;\n}\n.vvd-root sup {\n  top: -0.5em;\n}\n";

const theme = {
  name: "core/theme",
  css: coreThemeCss
};
const typography = {
  name: "core/typography",
  css: coreTypographyCss
};
const vivid2Compat = {
  name: "tokens/vivid-2-compat",
  css: tokensVivid2CompatCss
};
const optionalStyles = {
  theme,
  typography,
  vivid2Compat
};

const VividRootClassName = "vvd-root";
const appendLink = (rel, href, crossOrigin = false) => {
  const linkElement = document.createElement("link");
  linkElement.setAttribute("rel", rel);
  linkElement.setAttribute("href", href);
  if (crossOrigin) {
    linkElement.setAttribute("crossorigin", "");
  }
  document.head.append(linkElement);
};
const appendStyle = (css) => {
  const styleElement = document.createElement("style");
  styleElement.setAttribute("type", "text/css");
  styleElement.innerHTML = css;
  document.head.append(styleElement);
};
const vividVue = {
  install(app, options) {
    const addRootClassTo = options.addRootClassTo ?? "root";
    switch (addRootClassTo) {
      case "root":
        document.documentElement.classList.add(VividRootClassName);
        break;
      case "app": {
        const interval = setInterval(() => {
          if (app._container) {
            app._container.classList.add(VividRootClassName);
            clearInterval(interval);
          }
        }, 30);
        break;
      }
      case "none":
        break;
      default:
        throw new Error(`Invalid addRootClassTo option: ${addRootClassTo}`);
    }
    const tokens = options.tokens ?? "light";
    switch (tokens) {
      case "light":
        appendStyle(tokensThemeLightCss);
        break;
      case "dark":
        appendStyle(tokensThemeDarkCss);
        break;
      case "none":
        break;
      default:
        throw new Error(`Invalid theme option: ${tokens}`);
    }
    const styles = options.styles ?? [];
    for (const style of styles) {
      appendStyle(style.css);
    }
    const font = options.font ?? "oss";
    switch (font) {
      case "oss":
        appendLink("preconnect", "https://fonts.googleapis.com");
        appendLink("preconnect", "https://fonts.gstatic.com", true);
        appendLink(
          "stylesheet",
          "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600&family=Roboto+Mono:wght@400;500&display=swap"
        );
        break;
      case "spezia":
        appendStyle(fontsSpeziaVariableCss);
        break;
      case "none":
        break;
      default:
        throw new Error(`Invalid font option: ${font}`);
    }
    if (options.customComponentPrefix) {
      setCustomComponentPrefix(options.customComponentPrefix);
    }
  }
};
const vivid3 = vividVue;

export { Icon, VAccordion, VAccordionItem, VActionGroup, VAlert, VAudioPlayer, VAvatar, VBadge, VBanner, VBreadcrumb, VBreadcrumbItem, VButton, VCalendar, VCalendarEvent, VCard, VCheckbox, VCombobox, VDataGrid, VDataGridCell, VDataGridRow, VDatePicker, VDateRangePicker, VDateTimePicker, VDialPad, VDialog, VDivider, VEmptyState, VFab, VFilePicker, VHeader, VIcon, VLayout, VMenu, VMenuItem, VNav, VNavDisclosure, VNavItem, VNote, VNumberField, VOption, VPagination, VProgress, VProgressRing, VRadio, VRadioGroup, VRangeSlider, VRichTextEditor, VSearchableSelect, VSelect, VSelectableBox, VSideDrawer, VSlider, VSplitButton, VSwitch, VTab, VTabPanel, VTabs, VTag, VTagGroup, VTextArea, VTextField, VTimePicker, VToggletip, VTooltip, VTreeItem, VTreeView, VVideoPlayer, optionalStyles, setCustomComponentPrefix, vivid3, vividVue };
